import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
// eslint-disable-next-line no-unused-vars
import { QRCodeSVG } from 'qrcode.react';
import Cropper from 'react-easy-crop';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import './App.css';
import CompanyLandingPage from './components/CompanyLandingPage';
import TaxpayerConsentForm from './components/TaxpayerConsentForm';
import PDFDocumentFiller from './components/PDFDocumentFiller';
import PDFDocumentBuilder from './components/PDFDocumentBuilder';
import IRSWaiverIntakeForm from './components/IRSWaiverIntakeForm';
import MobileSignaturePage from './components/MobileSignaturePage';
import VideoConference from './components/VideoConference';
import PreparerDashboard from './components/PreparerDashboard';
import CompanyAdminDashboard from './components/CompanyAdminDashboard';
import OwnerDashboard from './components/OwnerDashboard';
import { downloadIRSWaiverPDF, generateIRSWaiverPDF } from './pdfGenerator';
import { getTemplatesList } from './landingPageTemplates';
import { toEasternISOString, toEasternInputDate, toEasternDateTimeString, getEasternYear } from './dateHelper';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  subscribeToAppointments,
  subscribeToPreparers,
  subscribeToAvailability,
  addAppointment,
  deleteAppointment,
  confirmAppointment,
  addPreparer,
  updatePreparer,
  deletePreparer,
  togglePreparerActive,
  setAvailability as saveAvailability,
  checkIsAdmin,
  checkIsOwner,
  subscribeToCompanies,
  addCompany,
  updateCompany,
  deleteCompany,
  toggleCompanyActive,
  toggleCompanyPublished,
  getCompanyBySlug,
  getCompanyById,
  verifyCompanyAdmin,
  getPreparersByCompany,
  addCompanyRequest,
  subscribeToCompanyRequests,
  updateCompanyRequest,
  deleteCompanyRequest,
  createConsentRequest,
  subscribeToConsentRequests,
  subscribeToSignedConsents,
  saveDocumentTemplate,
  subscribeToDocumentTemplates,
  createDocumentRequest,
  subscribeToDocumentRequests,
  subscribeToSignedDocuments,
  subscribeToIRSWaiverSubmissions,
  verifyIRSWaiverSubmission,
  deleteIRSWaiverSubmission,
  completeIRSWaiverSubmission,
  addTaxReturn,
  updateTaxReturn,
  deleteTaxReturn,
  subscribeToTaxReturns,
  subscribeToCompanyTaxReturns,
  getPreparerById
} from './firestoreHelper';
import { sendConfirmationEmail, sendConsentEmail } from './emailService';

function App() {
  // Get all landing page templates - memoized to prevent infinite re-renders
  const landingPageTemplates = useMemo(() => getTemplatesList(), []);
  
  // Utility function to convert 24-hour time to 12-hour format with AM/PM
  const convert24to12Hour = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${period}`;
  };

  // Utility function to format date as MM/DD/YYYY
  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Utility function to format phone number as 717-343-3822
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Limit to 10 digits
    const limited = digits.slice(0, 10);
    // Format as XXX-XXX-XXXX
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  // Utility function to validate email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Utility function to validate phone number (must be 10 digits)
  const validatePhoneNumber = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  };

  // Calendar helper functions for availability
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 23; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === 23 && minute === 30) break;
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const allTimeSlots = generateTimeSlots();

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get today's date string in local timezone (YYYY-MM-DD format)
  const getTodayString = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(calendarMonth);
    const firstDay = getFirstDayOfMonth(calendarMonth);
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
      days.push(date);
    }
    return days;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isCalendarDateSelected = (date) => {
    if (!date || calendarSelectedDates.length === 0) return false;
    return calendarSelectedDates.some(selectedDate => 
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isPastDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const hasAvailabilityOnDate = (date) => {
    if (!date || !loggedInPreparer) return false;
    const dateStr = formatDateToString(date);
    const key = `${loggedInPreparer.name}_${dateStr}`;
    const slots = preparerAvailability[key];
    return slots && slots.length > 0;
  };

  const navigateCalendarMonth = (direction) => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + direction, 1));
  };

  const handleCalendarDateClick = (date) => {
    if (!date || isPastDate(date)) return;
    
    setCalendarSelectedDates(prev => {
      const isAlreadySelected = prev.some(d => 
        d.getDate() === date.getDate() &&
        d.getMonth() === date.getMonth() &&
        d.getFullYear() === date.getFullYear()
      );
      
      if (isAlreadySelected) {
        // Remove from selection
        return prev.filter(d => 
          !(d.getDate() === date.getDate() &&
            d.getMonth() === date.getMonth() &&
            d.getFullYear() === date.getFullYear())
        );
      } else {
        // Add to selection
        return [...prev, date].sort((a, b) => a - b);
      }
    });
  };

  const clearSelectedDates = () => {
    setCalendarSelectedDates([]);
    setCalendarSelectedSlots([]);
  };

  const toggleCalendarTimeSlot = (slot) => {
    setCalendarSelectedSlots(prev => {
      if (prev.includes(slot)) {
        return prev.filter(s => s !== slot);
      } else {
        return [...prev, slot].sort();
      }
    });
  };

  const selectAllCalendarSlots = () => setCalendarSelectedSlots([...allTimeSlots]);
  const clearAllCalendarSlots = () => setCalendarSelectedSlots([]);
  const selectMorningSlots = () => setCalendarSelectedSlots(allTimeSlots.filter(slot => {
    const hour = parseInt(slot.split(':')[0]);
    return hour >= 6 && hour < 12;
  }));
  const selectAfternoonSlots = () => setCalendarSelectedSlots(allTimeSlots.filter(slot => {
    const hour = parseInt(slot.split(':')[0]);
    return hour >= 12 && hour < 17;
  }));
  const selectEveningSlots = () => setCalendarSelectedSlots(allTimeSlots.filter(slot => {
    const hour = parseInt(slot.split(':')[0]);
    return hour >= 17 && hour <= 23;
  }));

  const handleSaveCalendarAvailability = async () => {
    if (calendarSelectedDates.length === 0 || !loggedInPreparer) return;
    if (calendarSelectedSlots.length === 0) {
      alert('⚠️ Please select at least one time slot.');
      return;
    }
    try {
      // Save availability for all selected dates
      for (const date of calendarSelectedDates) {
        const dateStr = formatDateToString(date);
        await saveAvailability(loggedInPreparer.name, dateStr, calendarSelectedSlots, currentCompany?.id);
      }
      alert(`✅ Availability saved for ${calendarSelectedDates.length} date(s)!`);
      // Clear selection after save
      setCalendarSelectedDates([]);
      setCalendarSelectedSlots([]);
    } catch (error) {
      console.error('Error saving availability:', error);
      alert('❌ Failed to save availability. Please try again.');
    }
  };

  const [appointments, setAppointments] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isPublished, setIsPublished] = useState(false);
  const [shareableUrl, setShareableUrl] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [preparers, setPreparers] = useState([]);
  const [preparerCredentials, setPreparerCredentials] = useState([]);
  const [showPreparerManager, setShowPreparerManager] = useState(false);
  // Restore session state from localStorage on page reload
  const [isClientView, setIsClientView] = useState(() => {
    const saved = localStorage.getItem('isClientView');
    if (saved === null || saved === 'undefined') return true;
    try { return JSON.parse(saved); } catch { return true; }
  });
  const [isPreparerView, setIsPreparerView] = useState(() => {
    const saved = localStorage.getItem('isPreparerView');
    if (saved === null || saved === 'undefined') return false;
    try { return JSON.parse(saved); } catch { return false; }
  });
  const [isAdmin, setIsAdmin] = useState(() => {
    const saved = localStorage.getItem('isAdmin');
    if (saved === null || saved === 'undefined') return false;
    try { return JSON.parse(saved); } catch { return false; }
  });
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const saved = localStorage.getItem('isLoggedIn');
    if (saved === null || saved === 'undefined') return false;
    try { return JSON.parse(saved); } catch { return false; }
  });
  const [showLandingPage, setShowLandingPage] = useState(() => {
    // Don't show landing page on root URL - show Sales Portal instead
    return window.location.pathname !== '/';
  });
  const [preSelectedPreparerId, setPreSelectedPreparerId] = useState(null); // For direct preparer booking links
  const [loggedInPreparer, setLoggedInPreparer] = useState(() => {
    const saved = localStorage.getItem('loggedInPreparer');
    if (!saved || saved === 'undefined') return null;
    try { return JSON.parse(saved); } catch { return null; }
  });
  
  // Owner and Company states
  const [isOwner, setIsOwner] = useState(() => {
    const saved = localStorage.getItem('isOwner');
    if (saved === null || saved === 'undefined') return false;
    try { return JSON.parse(saved); } catch { return false; }
  });
  const [showOwnerDashboard, setShowOwnerDashboard] = useState(() => {
    const saved = localStorage.getItem('showOwnerDashboard');
    if (saved === null || saved === 'undefined') return false;
    try { return JSON.parse(saved); } catch { return false; }
  });
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(() => {
    // Load company from localStorage - preserve session for logged-in admins
    const pathname = window.location.pathname;
    const isAdminLoggedIn = localStorage.getItem('isLoggedIn') === 'true' && localStorage.getItem('isAdmin') === 'true';
    
    // Only clear company if at root URL AND not logged in as admin
    if (pathname === '/' && !isAdminLoggedIn) {
      localStorage.removeItem('currentCompany');
      return null;
    }
    const saved = localStorage.getItem('currentCompany');
    if (!saved || saved === 'undefined') return null;
    try { return JSON.parse(saved); } catch { return null; }
  });
  const [showAddCompanyForm, setShowAddCompanyForm] = useState(false);
  const [showAddCompanyFullPage, setShowAddCompanyFullPage] = useState(false);
  const [showEditCompanyForm, setShowEditCompanyForm] = useState(false);
  const [showEditCompanyPassword, setShowEditCompanyPassword] = useState(false);
  const [showPreparerPasswords, setShowPreparerPasswords] = useState({});
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    slug: '',
    email: '',
    phone: '',
    address: '',
    logo: '',
    description: '',
    adminUsername: '',
    adminPassword: '',
    primaryColor: '#2563eb',
    secondaryColor: '#1e40af',
    templateId: 'taxfile-classic'
  });
  const [companyPreparerCounts, setCompanyPreparerCounts] = useState({});
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const [ownerActiveTab, setOwnerActiveTab] = useState('companies'); // 'companies', 'preparers', 'appointments', 'availability', 'requests', 'templates'
  // eslint-disable-next-line no-unused-vars
  const [selectedTemplatePreview, setSelectedTemplatePreview] = useState(null);
  const [selectedCompanyForManagement, setSelectedCompanyForManagement] = useState(null);
  const [ownerViewAllAppointments, setOwnerViewAllAppointments] = useState([]);
  const [ownerViewAllPreparers, setOwnerViewAllPreparers] = useState([]);
  
  // Company Requests (Sales Portal)
  const [companyRequests, setCompanyRequests] = useState([]);
  const [showSalesPortal, setShowSalesPortal] = useState(() => {
    // Only show Sales Portal on root URL
    return window.location.pathname === '/';
  });
  const [salesRequestForm, setSalesRequestForm] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    message: '',
    expectedPreparers: '1-5'
  });
  const [salesRequestSubmitted, setSalesRequestSubmitted] = useState(false);
  
  // Admin portal tab state
  const [adminActiveTab, setAdminActiveTab] = useState('dashboard'); // 'dashboard', 'landing-page'
  
  // Sidebar collapse states for responsive design
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved === null || saved === 'undefined') return false;
    try { return JSON.parse(saved); } catch { return false; }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Landing Page Customization Settings
  const [landingPageSettings, setLandingPageSettings] = useState({
    heroTitle: '',
    heroSubtitle: 'Schedule your appointment with our experienced tax preparers',
    primaryColor: '#667eea',
    secondaryColor: '#764ba2',
    accentColor: '#10b981',
    logoUrl: '',
    companyName: '',
    showFeatures: true,
    featuresTitle: 'Why Choose Us?',
    features: [
      { icon: '👨‍💼', title: 'Expert Preparers', description: 'Certified professionals with years of experience' },
      { icon: '⚡', title: 'Fast Service', description: 'Quick turnaround on all tax filings' },
      { icon: '🔒', title: 'Secure & Confidential', description: 'Your information is always protected' },
      { icon: '💰', title: 'Maximize Returns', description: 'Get every deduction you deserve' }
    ],
    footerCompanyName: 'Friends Tax Services LLC',
    footerPhone: '',
    footerHours: 'Mon-Fri: 9AM-6PM | Sat: 10AM-4PM',
    footerTagline: 'Professional tax preparation'
  });
  
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginType, setLoginType] = useState('preparer'); // 'admin', 'preparer', or 'owner'
  const [firebaseUser, setFirebaseUser] = useState(null); // Track Firebase authenticated user
  // eslint-disable-next-line no-unused-vars
  const [authLoading, setAuthLoading] = useState(true); // Loading state for auth check
  const [showAddPreparerForm, setShowAddPreparerForm] = useState(false);
  const [showEditPreparerForm, setShowEditPreparerForm] = useState(false);
  const [editingPreparer, setEditingPreparer] = useState(null);
  const [viewingPreparer, setViewingPreparer] = useState(null);
  const [showViewPreparerModal, setShowViewPreparerModal] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [showAvailabilityEditor, setShowAvailabilityEditor] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('activeTab');
    return saved ? saved : 'appointments';
  });
  const [preparerAvailability, setPreparerAvailability] = useState({});
  const [availabilityForm, setAvailabilityForm] = useState({
    dates: [],
    selectedTimeSlots: [],
    bulkMode: true
  });
  
  // New Calendar-based availability states
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarSelectedDates, setCalendarSelectedDates] = useState([]); // Array of selected dates for bulk
  const [calendarSelectedSlots, setCalendarSelectedSlots] = useState([]);
  
  // Appointment view and email reminder states
  const [appointmentViewMode, setAppointmentViewMode] = useState('card'); // 'card' or 'table'
  const [appointmentFilterTab, setAppointmentFilterTab] = useState('all'); // 'all', 'confirmed', 'notConfirmed'
  const [emailReminderSelectedIds, setEmailReminderSelectedIds] = useState([]);
  const [showEmailReminderModal, setShowEmailReminderModal] = useState(false);
  const [emailReminderSubject, setEmailReminderSubject] = useState('Tax Filing Reminder');
  const [emailReminderMessage, setEmailReminderMessage] = useState('');
  const [expandedAppointmentId, setExpandedAppointmentId] = useState(null); // For view details modal
  
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  // eslint-disable-next-line no-unused-vars
  const [showManualBookingForm, setShowManualBookingForm] = useState(false);
  const [showAdminManualBooking, setShowAdminManualBooking] = useState(false);
  const [adminBookingForm, setAdminBookingForm] = useState({
    preparer: '',
    date: '',
    time: '',
    name: '',
    email: '',
    phone: ''
  });
  const [newPreparerForm, setNewPreparerForm] = useState({
    name: '',
    username: '',
    password: '',
    email: '',
    phone: '',
    address: '',
    photo: '',
    bio: '',
    isAdmin: false
  });
  const [profileEditData, setProfileEditData] = useState({
    email: '',
    phone: '',
    address: '',
    bio: '',
    photo: ''
  });
  const [showPasswordFor, setShowPasswordFor] = useState({});
  
  // Consent document states
  // eslint-disable-next-line no-unused-vars
  const [consentRequests, setConsentRequests] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [signedConsents, setSignedConsents] = useState([]);
  const [showSendConsentModal, setShowSendConsentModal] = useState(false);
  const [sendingConsent, setSendingConsent] = useState(false);
  const [viewingConsent, setViewingConsent] = useState(null);
  const [consentForm, setConsentForm] = useState({
    taxpayerName: '',
    taxpayerEmail: '',
    taxpayerPhone: '',
    taxYear: getEasternYear() - 1
  });
  
  // IRS Waiver submissions state
  const [irsWaiverSubmissions, setIrsWaiverSubmissions] = useState([]);
  const [viewingWaiverSubmission, setViewingWaiverSubmission] = useState(null);
  const [showPdfPreviewInModal, setShowPdfPreviewInModal] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [selectedTaxYearTab, setSelectedTaxYearTab] = useState(null); // For tax year tabs in completed section
  const [completedFormsPage, setCompletedFormsPage] = useState(1); // Pagination for completed forms
  const COMPLETED_FORMS_PER_PAGE = 5;
  const [downloadingAllPdfs, setDownloadingAllPdfs] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const [editingPreparerSection, setEditingPreparerSection] = useState(null);
  const [preparerSectionData, setPreparerSectionData] = useState({
    preparerName: '',
    preparerSignature: '',
    preparerSignatureDate: '',
    preparerSignatureImage: '',
    ptin: '',
    firmName: '',
    firmAddress: '',
    firmEIN: '',
    notes: ''
  });
  
  // Tax Returns tracking state
  const [taxReturns, setTaxReturns] = useState([]);
  const [taxReturnForm, setTaxReturnForm] = useState({
    clientName: '',
    phone: '',
    amount: '',
    taxYear: new Date().getFullYear() - 1,
    paymentStatus: 'unpaid'
  });
  const [savingTaxReturn, setSavingTaxReturn] = useState(false);
  const [editingTaxReturn, setEditingTaxReturn] = useState(null);
  const [selectedTaxReturns, setSelectedTaxReturns] = useState([]);
  
  // Search and filter states for All Appointments (Owner view)
  const [appointmentSearchName, setAppointmentSearchName] = useState('');
  const [appointmentSearchDate, setAppointmentSearchDate] = useState('');
  const [appointmentCurrentPage, setAppointmentCurrentPage] = useState(1);
  const appointmentsPerPage = 20;
  
  // Search and filter states for Completed Forms
  const [completedSearchName, setCompletedSearchName] = useState('');
  const [completedSearchDateFrom, setCompletedSearchDateFrom] = useState('');
  const [completedSearchDateTo, setCompletedSearchDateTo] = useState('');
  const [selectedCompletedForms, setSelectedCompletedForms] = useState([]); // Array of selected submission IDs
  
  // Saved preparer signature for reuse
  const [savedPreparerSignature, setSavedPreparerSignature] = useState(() => {
    const saved = localStorage.getItem('savedPreparerSignature');
    if (!saved || saved === 'undefined') return null;
    try { return JSON.parse(saved); } catch { return null; }
  });
  const preparerSignatureCanvasRef = useRef(null);
  const [isDrawingPreparerSignature, setIsDrawingPreparerSignature] = useState(false);
  
  // PDF template upload states
  // eslint-disable-next-line no-unused-vars
  const [pdfTemplate, setPdfTemplate] = useState(() => {
    const saved = localStorage.getItem('consentPdfTemplate');
    if (!saved || saved === 'undefined') return null;
    try { return JSON.parse(saved); } catch { return null; }
  });
  // eslint-disable-next-line no-unused-vars
  const [isDraggingPdf, setIsDraggingPdf] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [uploadingPdf, setUploadingPdf] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // Document Template states (DocuSign-like feature)
  // eslint-disable-next-line no-unused-vars
  const [documentTemplates, setDocumentTemplates] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [documentRequests, setDocumentRequests] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [signedDocuments, setSignedDocuments] = useState([]);
  const [showDocumentBuilder, setShowDocumentBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showSendDocumentModal, setShowSendDocumentModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sendingDocument, setSendingDocument] = useState(false);
  const [documentSendForm, setDocumentSendForm] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: ''
  });
  const [preparerFieldValues, setPreparerFieldValues] = useState({});

  // Photo cropper states
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropTarget, setCropTarget] = useState(null); // 'newPreparer', 'editPreparer', 'profile'
  const [showDatePopover, setShowDatePopover] = useState(false); // For hotel/flight style date picker popover
  
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    timeSlots: [], // Array of selected time slots for multiple clients
    fullName: '',
    email: '',
    phoneNumber: '',
    numberOfClients: '1',
    fillingType: '',
    preparer: ''
  });

  // Owner login state
  const [showOwnerLogin, setShowOwnerLogin] = useState(false);
  const [ownerLoginData, setOwnerLoginData] = useState({ email: '', password: '' });
  
  // Admin login state (for company admin at /admin route)
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminLoginData, setAdminLoginData] = useState({ username: '', password: '' });
  
  // Consent form state
  const [showConsentForm, setShowConsentForm] = useState(false);
  
  // PWA detection - check if running as standalone app (iOS home screen)
  // eslint-disable-next-line no-unused-vars
  const [isPWAMode] = useState(() => {
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  });
  
  // PWA login prompt state - show when PWA opens without session
  const [showPWALoginPrompt, setShowPWALoginPrompt] = useState(false);
  const [pwaCompanySlug, setPwaCompanySlug] = useState('');
  const [pwaLoadingCompany, setPwaLoadingCompany] = useState(false);
  const [pwaError, setPwaError] = useState(null);

  // Check URL parameters on component mount to auto-open form for clients
  useEffect(() => {
    // Clean up any corrupted localStorage values
    const keysToCheck = ['loggedInPreparer', 'currentCompany', 'savedPreparerSignature', 'consentPdfTemplate', 'isClientView', 'isPreparerView', 'isAdmin', 'isLoggedIn', 'isOwner', 'showOwnerDashboard', 'sidebarCollapsed'];
    keysToCheck.forEach(key => {
      const val = localStorage.getItem(key);
      if (val === 'undefined' || val === 'null') {
        localStorage.removeItem(key);
      }
    });
    
    const urlParams = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;
    
    // PRIORITY: Check for consent form route FIRST (public route, no login required)
    if (pathname === '/consent') {
      setShowConsentForm(true);
      setIsClientView(true);
      return;
    }
    
    // Check for IRS Waiver form route (public route, no login required)
    if (pathname === '/irs-waiver' || pathname === '/waiver-form') {
      // This will be handled by the IRSWaiverIntakeForm component at render time
      return;
    }
    
    // Check for document signing route (public route, no login required)
    if (pathname === '/document') {
      // This will be handled by the PDFDocumentFiller component
      return;
    }
    
    // Check for mobile signature route (public route, no login required)
    if (pathname === '/mobile-sign') {
      // This will be handled by the MobileSignaturePage component
      return;
    }
    
    // Check saved session FIRST to determine if user is already logged in
    const savedIsLoggedIn = localStorage.getItem('isLoggedIn');
    const savedIsOwner = localStorage.getItem('isOwner');
    const savedIsPreparerView = localStorage.getItem('isPreparerView');
    const savedIsAdmin = localStorage.getItem('isAdmin');
    const savedLoggedInPreparer = localStorage.getItem('loggedInPreparer');
    const savedShowOwnerDashboard = localStorage.getItem('showOwnerDashboard');
    const savedCurrentCompany = localStorage.getItem('currentCompany');
    
    // If user has a saved session, restore it (keeps them logged in on refresh)
    if (savedIsLoggedIn === 'true') {
      // Restore company if saved
      if (savedCurrentCompany && savedCurrentCompany !== 'undefined') {
        try {
          setCurrentCompany(JSON.parse(savedCurrentCompany));
        } catch (e) {
          console.error('Error parsing saved company:', e);
          localStorage.removeItem('currentCompany');
        }
      }
      
      // Check if it's an owner session
      if (savedIsOwner === 'true' && savedShowOwnerDashboard === 'true') {
        setIsOwner(true);
        setIsLoggedIn(true);
        setShowOwnerDashboard(true);
        setIsClientView(false);
        setShowLandingPage(false);
        setShowSalesPortal(false);
        return;
      }
      
      // Check if it's a preparer session
      if (savedIsPreparerView === 'true' && savedLoggedInPreparer && savedLoggedInPreparer !== 'undefined') {
        try {
          const cachedPreparer = JSON.parse(savedLoggedInPreparer);
          // Set cached data immediately for fast UI
          setLoggedInPreparer(cachedPreparer);
          setIsPreparerView(true);
          setIsLoggedIn(true);
          setIsAdmin(cachedPreparer?.isAdmin || false);
          setIsClientView(false);
          setShowLandingPage(false);
          setShowSalesPortal(false);
          
          // Fetch fresh preparer data from Firebase in background
          if (cachedPreparer?.id) {
            getPreparerById(cachedPreparer.id).then(freshPreparer => {
              if (freshPreparer) {
                console.log('✅ Refreshed preparer data from Firebase');
                setLoggedInPreparer(freshPreparer);
              }
            }).catch(err => console.error('Error refreshing preparer:', err));
          }
          return;
        } catch (e) {
          console.error('Error parsing saved preparer:', e);
          localStorage.removeItem('loggedInPreparer');
        }
      }
      
      // Check if it's an admin session (company admin, not preparer)
      if (savedIsAdmin === 'true' && savedIsPreparerView !== 'true') {
        setIsAdmin(true);
        setIsLoggedIn(true);
        setIsClientView(false);
        setShowLandingPage(false);
        setShowSalesPortal(false);
        // Ensure currentCompany is set for company admin
        if (savedCurrentCompany && savedCurrentCompany !== 'undefined') {
          try {
            const company = JSON.parse(savedCurrentCompany);
            console.log('✅ Restoring company admin session with company:', company?.name);
            setCurrentCompany(company);
          } catch (e) {
            console.error('Error parsing saved company for admin:', e);
          }
        }
        return;
      }
    }
    
    // PWA MODE: If opened as PWA (iOS home screen) but no saved session
    // Check if there's a saved company slug first, then redirect there
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone === true ||
                  urlParams.get('pwa') === 'true';
    
    // Check for redirect=company parameter (from default manifest)
    const shouldRedirectToCompany = urlParams.get('redirect') === 'company';
    
    if (isPWA && (pathname === '/' || shouldRedirectToCompany) && savedIsLoggedIn !== 'true') {
      console.log('📱 PWA mode detected without session');
      
      // Check if there's a saved company slug for PWA (try both localStorage and sessionStorage)
      const savedPwaCompanySlug = localStorage.getItem('pwa_company_slug') || sessionStorage.getItem('pwa_company_slug');
      if (savedPwaCompanySlug) {
        console.log('📱 Found saved PWA company:', savedPwaCompanySlug);
        // Redirect to the saved company's landing page
        window.location.replace(`/company/${savedPwaCompanySlug}?pwa=true`);
        return;
      }
      
      // No saved company, show PWA login prompt with company input
      // But DON'T show immediately - let user enter company name to go to company landing page
      setShowPWALoginPrompt(true);
      setShowSalesPortal(false);
      return;
    }
    
    // If NOT logged in, check for company public page route /c/:slug or /company/:slug
    // Also supports direct preparer booking: /company/:slug/book/:preparerId
    if (pathname.startsWith('/c/') || pathname.startsWith('/company/')) {
      const pathParts = pathname.replace('/c/', '').replace('/company/', '').split('/');
      const slug = pathParts[0];
      console.log('🔍 Company URL detected - slug:', slug, 'full path:', pathname);
      // Check if this is a direct preparer booking link: /company/slug/book/preparerId
      const isPreparerBookingLink = pathParts[1] === 'book' && pathParts[2];
      const preparerIdFromUrl = isPreparerBookingLink ? pathParts[2] : null;
      
      if (slug) {
        // Load company by slug for public viewing
        getCompanyBySlug(slug).then((company) => {
          console.log('📦 Company data retrieved:', company);
          if (company) {
            // Check both old and new field names for compatibility
            const isActive = company.isActive !== false && company.active !== false;
            const isPublished = company.isPublished || company.published;
            console.log('✅ Company status - isActive:', isActive, 'isPublished:', isPublished, 'raw data:', { isActive: company.isActive, active: company.active, isPublished: company.isPublished, published: company.published });
            
            if (isActive && isPublished) {
              setCurrentCompany(company);
              setIsClientView(true);
              setShowLandingPage(true);
              setShowSalesPortal(false);
              // Store preparer ID if it's a direct booking link
              if (preparerIdFromUrl) {
                setPreSelectedPreparerId(preparerIdFromUrl);
              }
            } else {
              console.warn('⚠️ Company not active or not published');
              alert('This company page is currently unavailable. Please contact the administrator.');
              window.location.href = '/';
            }
          } else {
            console.warn('⚠️ Company not found for slug:', slug);
            alert('Company not found or not available');
            window.location.href = '/';
          }
        }).catch((err) => {
          console.error('❌ Error loading company:', err);
          window.location.href = '/';
        });
        return;
      }
    }
    
    // Check for owner login route
    if (pathname === '/owner-pc') {
      setShowOwnerLogin(true);
      setIsClientView(false);
      return;
    }
    
    // Check for admin login route (company admin)
    if (pathname === '/admin') {
      setShowAdminLogin(true);
      setIsClientView(false);
      return;
    }
    
    // Check for login route
    if (pathname === '/login' || urlParams.get('preparer') === 'login') {
      setShowLoginForm(true);
      setIsClientView(false);
      return;
    }
    
    // If user is already logged in, keep their session
    if (isLoggedIn) {
      return;
    }
    
    // Show sales portal when at root URL with no company
    if (pathname === '/') {
      // Clear any saved company when at root (only if not logged in)
      if (!savedIsLoggedIn || savedIsLoggedIn !== 'true') {
        setCurrentCompany(null);
        localStorage.removeItem('currentCompany');
        setShowSalesPortal(true);
        setIsClientView(true);
        setShowLandingPage(false);
      }
      return; // Important: return early so we don't override with landing page
    }
    
    // Default: Show landing page for visitors (only if company is loaded)
    if (currentCompany) {
      setIsClientView(true);
      setShowLandingPage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist view state to localStorage
  useEffect(() => {
    localStorage.setItem('isClientView', JSON.stringify(isClientView));
  }, [isClientView]);

  useEffect(() => {
    localStorage.setItem('isPreparerView', JSON.stringify(isPreparerView));
  }, [isPreparerView]);

  useEffect(() => {
    localStorage.setItem('isAdmin', JSON.stringify(isAdmin));
  }, [isAdmin]);

  useEffect(() => {
    localStorage.setItem('isLoggedIn', JSON.stringify(isLoggedIn));
  }, [isLoggedIn]);

  useEffect(() => {
    localStorage.setItem('showLandingPage', JSON.stringify(showLandingPage));
  }, [showLandingPage]);

  useEffect(() => {
    if (loggedInPreparer) {
      localStorage.setItem('loggedInPreparer', JSON.stringify(loggedInPreparer));
    } else {
      localStorage.removeItem('loggedInPreparer');
    }
  }, [loggedInPreparer]);

  useEffect(() => {
    localStorage.setItem('isOwner', JSON.stringify(isOwner));
  }, [isOwner]);

  useEffect(() => {
    localStorage.setItem('showOwnerDashboard', JSON.stringify(showOwnerDashboard));
  }, [showOwnerDashboard]);

  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  // Save sidebar collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileMenuOpen]);

  // Update favicon and page title when currentCompany or landingPageSettings changes (for admin portal)
  useEffect(() => {
    const updateFavicon = () => {
      const favicon = document.getElementById('favicon');
      const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
      
      // Check if we're in admin view with a company
      if (currentCompany && isAdmin && !isClientView) {
        // Get logo from multiple sources
        const companyLogo = landingPageSettings?.logoUrl || 
                          currentCompany?.landingPageSettings?.logoUrl ||
                          currentCompany?.logo || 
                          currentCompany?.logoUrl;
        
        if (companyLogo) {
          if (favicon) favicon.href = companyLogo;
          if (appleTouchIcon) appleTouchIcon.href = companyLogo;
          document.title = `${currentCompany.name} - Admin Portal`;
        } else {
          // Use default TaxFilePro logo
          if (favicon) favicon.href = '/logo.png';
          if (appleTouchIcon) appleTouchIcon.href = '/logo.png';
          document.title = `${currentCompany.name} - Admin Portal`;
        }
      } else if (isOwner && showOwnerDashboard) {
        // Owner dashboard - use TaxFilePro logo
        if (favicon) favicon.href = '/logo.png';
        if (appleTouchIcon) appleTouchIcon.href = '/logo.png';
        document.title = 'TaxFilePro - Owner Dashboard';
      } else if (!isClientView && !currentCompany) {
        // Reset to default
        if (favicon) favicon.href = '/logo.png';
        if (appleTouchIcon) appleTouchIcon.href = '/logo.png';
        document.title = 'TaxFilePro - Tax Appointment Booking';
      }
    };
    
    updateFavicon();
  }, [currentCompany, isAdmin, isClientView, landingPageSettings, isOwner, showOwnerDashboard]);

  // Close date popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePopover) {
        const popover = document.querySelector('.date-picker-popover');
        const trigger = document.querySelector('.date-picker-trigger');
        if (popover && !popover.contains(event.target) && trigger && !trigger.contains(event.target)) {
          setShowDatePopover(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePopover]);

  // Firebase Auth state listener - check if user is already authenticated
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setAuthLoading(true);
      if (user) {
        // User is signed in, check if they're an admin
        const isAdminUser = await checkIsAdmin(user.uid);
        setFirebaseUser(user);
        if (isAdminUser) {
          setIsAdmin(true);
          setIsLoggedIn(true);
        }
      } else {
        setFirebaseUser(null);
        // Only clear admin status, not preparer status
        // (preparers use different auth)
      }
      setAuthLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  // Persist currentCompany to localStorage
  useEffect(() => {
    if (currentCompany) {
      localStorage.setItem('currentCompany', JSON.stringify(currentCompany));
    } else {
      localStorage.removeItem('currentCompany');
    }
  }, [currentCompany]);

  // Sync landing page settings with currentCompany
  useEffect(() => {
    if (currentCompany) {
      // Use landingPageContent (from template selection) or landingPageSettings (customized)
      const content = currentCompany.landingPageContent || currentCompany.landingPageSettings || {};
      const template = landingPageTemplates.find(t => t.id === (content.templateId || currentCompany.templateId)) || landingPageTemplates[0];
      
      setLandingPageSettings({
        templateId: content.templateId || currentCompany.templateId || 'taxfile-classic',
        heroTitle: content.heroTitle || currentCompany.landingPageSettings?.heroTitle || currentCompany.name || '',
        heroSubtitle: content.heroSubtitle || currentCompany.landingPageSettings?.heroSubtitle || template.heroSubtitle || 'Schedule your appointment with our experienced tax preparers',
        ctaText: content.ctaText || template.ctaText || 'Book Now',
        primaryColor: content.primaryColor || currentCompany.landingPageSettings?.primaryColor || currentCompany.primaryColor || template.primaryColor || '#667eea',
        secondaryColor: content.secondaryColor || currentCompany.landingPageSettings?.secondaryColor || currentCompany.secondaryColor || template.secondaryColor || '#764ba2',
        accentColor: content.accentColor || currentCompany.landingPageSettings?.accentColor || template.accentColor || '#10b981',
        logoUrl: currentCompany.landingPageSettings?.logoUrl || currentCompany.logo || '',
        companyName: currentCompany.name || '',
        showFeatures: currentCompany.landingPageSettings?.showFeatures !== false,
        featuresTitle: currentCompany.landingPageSettings?.featuresTitle || 'Why Choose Us?',
        features: content.features || currentCompany.landingPageSettings?.features || template.features || [
          { icon: '👨‍💼', title: 'Expert Preparers', description: 'Certified professionals with years of experience' },
          { icon: '⚡', title: 'Fast Service', description: 'Quick turnaround on all tax filings' },
          { icon: '🔒', title: 'Secure & Confidential', description: 'Your information is always protected' },
          { icon: '💰', title: 'Maximize Returns', description: 'Get every deduction you deserve' }
        ],
        services: content.services || currentCompany.landingPageSettings?.services || currentCompany.servicesList || template.services || [],
        footerCompanyName: currentCompany.landingPageSettings?.footerCompanyName || currentCompany.name || 'Friends Tax Services LLC',
        footerPhone: currentCompany.landingPageSettings?.footerPhone || currentCompany.phone || '',
        footerHours: currentCompany.landingPageSettings?.footerHours || 'Mon-Fri: 9AM-6PM | Sat: 10AM-4PM',
        footerTagline: currentCompany.landingPageSettings?.footerTagline || 'Professional tax preparation',
        footerTitle: content.footerTitle || 'Ready to Get Started?',
        footerSubtitle: content.footerSubtitle || 'Book an appointment with our expert team today!',
        footerCta: content.footerCta || 'Book Now →'
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id, landingPageTemplates]);

  // Subscribe to companies for owner
  useEffect(() => {
    if (isOwner) {
      const unsubscribe = subscribeToCompanies((data) => {
        setCompanies(data);
        // Fetch preparer counts for each company
        data.forEach(async (company) => {
          const preparers = await getPreparersByCompany(company.id);
          setCompanyPreparerCounts(prev => ({
            ...prev,
            [company.id]: preparers.length
          }));
        });
      });
      return () => unsubscribe();
    }
  }, [isOwner]);

  // Subscribe to ALL appointments and preparers for owner overview
  useEffect(() => {
    if (isOwner) {
      // Subscribe to all appointments
      const unsubscribeAllAppointments = subscribeToAppointments((data) => {
        setOwnerViewAllAppointments(data);
      }, null);

      // Subscribe to all preparers
      const unsubscribeAllPreparers = subscribeToPreparers((data) => {
        setOwnerViewAllPreparers(data);
      }, null);

      return () => {
        unsubscribeAllAppointments();
        unsubscribeAllPreparers();
      };
    }
  }, [isOwner]);

  // Subscribe to company requests for owner
  useEffect(() => {
    if (isOwner) {
      const unsubscribe = subscribeToCompanyRequests((data) => {
        setCompanyRequests(data);
      });
      return () => unsubscribe();
    }
  }, [isOwner]);

  // When owner selects a company for management, update currentCompany
  useEffect(() => {
    if (isOwner && selectedCompanyForManagement) {
      setCurrentCompany(selectedCompanyForManagement);
    }
  }, [isOwner, selectedCompanyForManagement]);

  // Subscribe to Firestore data - Real-time updates (scoped to company if logged in as company admin)
  useEffect(() => {
    const companyId = currentCompany?.id || null;
    
    // Subscribe to appointments (scoped to company if applicable)
    const unsubscribeAppointments = subscribeToAppointments((data) => {
      // Filter by company if logged in as company admin
      const filteredData = companyId ? data.filter(a => a.companyId === companyId) : data;
      setAppointments(filteredData);
    }, companyId);

    // Subscribe to preparers (scoped to company if applicable)
    const unsubscribePreparers = subscribeToPreparers((data) => {
      // Filter by company if logged in as company admin
      const filteredData = companyId ? data.filter(p => p.companyId === companyId) : data;
      setPreparerCredentials(filteredData);
    }, companyId);

    // Subscribe to availability (scoped to company if applicable, also include preparer-specific)
    const preparerName = loggedInPreparer?.name || null;
    const unsubscribeAvailability = subscribeToAvailability((data) => {
      setPreparerAvailability(data);
    }, companyId, preparerName);

    // Subscribe to consent requests and signed consents (for preparer)
    let unsubscribeConsentRequests = () => {};
    let unsubscribeSignedConsents = () => {};
    let unsubscribeDocumentTemplates = () => {};
    let unsubscribeDocumentRequests = () => {};
    let unsubscribeSignedDocuments = () => {};
    let unsubscribeIRSWaiverSubmissions = () => {};
    
    if (loggedInPreparer?.id) {
      unsubscribeConsentRequests = subscribeToConsentRequests(loggedInPreparer.id, (data) => {
        setConsentRequests(data);
      });
      unsubscribeSignedConsents = subscribeToSignedConsents(loggedInPreparer.id, (data) => {
        setSignedConsents(data);
      });
      
      // Subscribe to document templates, requests, and signed documents
      unsubscribeDocumentTemplates = subscribeToDocumentTemplates(loggedInPreparer.id, (data) => {
        setDocumentTemplates(data);
      });
      unsubscribeDocumentRequests = subscribeToDocumentRequests(loggedInPreparer.id, (data) => {
        setDocumentRequests(data);
      });
      unsubscribeSignedDocuments = subscribeToSignedDocuments(loggedInPreparer.id, (data) => {
        setSignedDocuments(data);
      });
    }
    
    // Subscribe to IRS Waiver submissions (for both preparers and admins)
    // Admin (isLoggedIn && !isPreparerView) sees all, preparers see their own + published
    if (loggedInPreparer?.id || (isLoggedIn && isAdmin)) {
      const isAdminView = isAdmin && !isPreparerView;
      unsubscribeIRSWaiverSubmissions = subscribeToIRSWaiverSubmissions(loggedInPreparer?.id || null, isAdminView, (data) => {
        setIrsWaiverSubmissions(data);
      }, companyId);
    }

    // Subscribe to tax returns
    let unsubscribeTaxReturns = () => {};
    if (companyId) {
      // Always subscribe to company-wide tax returns if we have a company
      unsubscribeTaxReturns = subscribeToCompanyTaxReturns(companyId, (data) => {
        console.log('Tax returns loaded:', data.length);
        setTaxReturns(data);
      });
    } else if (loggedInPreparer?.id) {
      // Fallback to preparer-specific if no company
      unsubscribeTaxReturns = subscribeToTaxReturns(loggedInPreparer.id, (data) => {
        console.log('Preparer tax returns loaded:', data.length);
        setTaxReturns(data);
      });
    }

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeAppointments();
      unsubscribePreparers();
      unsubscribeAvailability();
      unsubscribeConsentRequests();
      unsubscribeSignedConsents();
      unsubscribeDocumentTemplates();
      unsubscribeDocumentRequests();
      unsubscribeSignedDocuments();
      unsubscribeIRSWaiverSubmissions();
      unsubscribeTaxReturns();
    };
  }, [currentCompany?.id, loggedInPreparer?.name, loggedInPreparer?.id, isLoggedIn, isPreparerView, isAdmin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Auto-format phone number
    if (name === 'phoneNumber') {
      setFormData({
        ...formData,
        [name]: formatPhoneNumber(value)
      });
    } else if (name === 'numberOfClients') {
      // Reset time selections when number of clients changes
      setFormData({
        ...formData,
        [name]: value,
        time: '',
        timeSlots: []
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const numClients = parseInt(formData.numberOfClients) || 1;
    
    // Validate required fields
    if (!formData.date || !formData.fullName || !formData.email || !formData.phoneNumber || !formData.numberOfClients || !formData.fillingType) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate time slots based on number of clients
    if (numClients === 1 && !formData.time) {
      alert('Please select a time slot');
      return;
    }
    
    if (numClients > 1 && formData.timeSlots.length !== numClients) {
      alert(`Please select ${numClients} different time slots for ${numClients} clients`);
      return;
    }

    // Validate email format
    if (!validateEmail(formData.email)) {
      alert('Please enter a valid email address (e.g., name@example.com)');
      return;
    }

    // Validate phone number format (10 digits)
    if (!validatePhoneNumber(formData.phoneNumber)) {
      alert('Please enter a valid 10-digit phone number (e.g., 717-343-3822)');
      return;
    }

    // Check if preparer is selected (unless logged in as preparer)
    const selectedPreparer = isPreparerView ? loggedInPreparer?.name : formData.preparer;
    if (!selectedPreparer) {
      alert('Please select a preparer');
      return;
    }

    // Check if time slots are still available
    const availableSlots = getAvailableTimeSlots(formData.date, selectedPreparer);
    const timesToCheck = numClients > 1 ? formData.timeSlots : [formData.time];
    
    for (const time of timesToCheck) {
      if (!isPreparerView && !availableSlots.includes(time)) {
        alert(`Time slot ${convert24to12Hour(time)} is no longer available. Please select another time.`);
        return;
      }
    }

    try {
      // Create appointment(s) based on number of clients
      const timeSlotsToBook = numClients > 1 ? formData.timeSlots : [formData.time];
      
      for (let i = 0; i < timeSlotsToBook.length; i++) {
        const appointmentData = {
          ...formData,
          time: timeSlotsToBook[i],
          preparer: selectedPreparer,
          confirmed: false,
          clientNumber: numClients > 1 ? `${i + 1} of ${numClients}` : null
        };
        // Remove timeSlots from individual appointment data
        delete appointmentData.timeSlots;
        
        // Save to Firestore
        await addAppointment(appointmentData);
      }
      
      // Reset form
      setFormData({
        date: '',
        time: '',
        timeSlots: [],
        fullName: '',
        email: '',
        phoneNumber: '',
        numberOfClients: '1',
        fillingType: '',
        preparer: ''
      });
      
      // Show success message then redirect
      alert('✅ Appointment Successfully Booked!\n\nThank you for scheduling with us.\n\n[key] A confirmation email will be sent to your email address once the preparer confirms your appointment.\n\nWe look forward to serving you!');
      
      // Redirect to landing page for clients
      if (isClientView) {
        setShowLandingPage(true);
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      alert('❌ Error booking appointment. Please try again.');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(appointments.map(apt => apt.id));
    } else {
      setSelectedIds([]);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await deleteAppointment(id);
        setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
      } catch (error) {
        console.error('Error deleting appointment:', error);
        alert('❌ Error deleting appointment. Please try again.');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('Please select appointments to delete');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} appointment(s)?`)) {
      try {
        await Promise.all(selectedIds.map(id => deleteAppointment(id)));
        setSelectedIds([]);
      } catch (error) {
        console.error('Error deleting appointments:', error);
        alert('❌ Error deleting appointments. Please try again.');
      }
    }
  };

  const handleConfirm = async () => {
    if (selectedIds.length === 0) {
      alert('Please select appointments to confirm');
      return;
    }
    
    try {
      await Promise.all(selectedIds.map(id => confirmAppointment(id)));
      alert(`${selectedIds.length} appointment(s) confirmed successfully!`);
      setSelectedIds([]);
    } catch (error) {
      console.error('Error confirming appointments:', error);
      alert('❌ Error confirming appointments. Please try again.');
    }
  };

  const handlePublishToggle = () => {
    if (!isPublished) {
      // Generate shareable URL (in production, this would be your actual domain)
      const baseUrl = window.location.origin;
      const url = `${baseUrl}?form=open`;
      setShareableUrl(url);
      setIsPublished(true);
    } else {
      setIsPublished(false);
      setShareableUrl('');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareableUrl);
    alert('Link copied to clipboard!');
  };

  const handleAddPreparer = async () => {
    if (!newPreparerForm.name.trim() || !newPreparerForm.username.trim() || !newPreparerForm.password.trim()) {
      alert('Please fill in all required fields (Name, Username, Password)');
      return;
    }
    if (preparers.includes(newPreparerForm.name.trim())) {
      alert('This preparer name already exists');
      return;
    }
    if (preparerCredentials.some(p => p.username === newPreparerForm.username.trim())) {
      alert('This username already exists');
      return;
    }
    
    try {
      const preparerData = {
        name: newPreparerForm.name.trim(),
        username: newPreparerForm.username.trim(),
        password: newPreparerForm.password.trim(),
        email: newPreparerForm.email.trim(),
        phone: newPreparerForm.phone.trim(),
        address: newPreparerForm.address.trim(),
        photo: newPreparerForm.photo,
        bio: newPreparerForm.bio.trim(),
        isAdmin: newPreparerForm.isAdmin || false,
        active: true,
        companyId: currentCompany?.id || null,
        companyName: currentCompany?.name || null
      };
      
      await addPreparer(preparerData);
      
      setNewPreparerForm({ name: '', username: '', password: '', email: '', phone: '', address: '', photo: '', bio: '', isAdmin: false });
      setShowAddPreparerForm(false);
      alert('Preparer added successfully! They can now log in with their credentials.');
    } catch (error) {
      console.error('Error adding preparer:', error);
      alert('❌ Error adding preparer. Please try again.');
    }
  };

  // Owner Company Management Functions
  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name.trim() || !companyForm.slug.trim() || !companyForm.adminUsername.trim() || !companyForm.adminPassword.trim()) {
      alert('Please fill in Company Name, URL Slug, Admin Username, and Admin Password');
      return;
    }
    try {
      // Get selected template to initialize landing page content
      const selectedTemplate = landingPageTemplates.find(t => t.id === companyForm.templateId) || landingPageTemplates[0];
      
      // Create landing page content based on selected template
      const landingPageContent = {
        templateId: companyForm.templateId || 'taxfile-classic',
        heroTitle: companyForm.name ? `Welcome to ${companyForm.name}` : selectedTemplate.heroTitle,
        heroSubtitle: companyForm.description || selectedTemplate.heroSubtitle,
        ctaText: selectedTemplate.ctaText,
        features: selectedTemplate.features,
        services: selectedTemplate.services,
        primaryColor: selectedTemplate.primaryColor,
        secondaryColor: selectedTemplate.secondaryColor,
        accentColor: selectedTemplate.accentColor || '#10b981',
        gradientStart: selectedTemplate.gradientStart,
        gradientEnd: selectedTemplate.gradientEnd,
        footerTitle: 'Ready to Get Started?',
        footerSubtitle: 'Book an appointment with our expert team today!',
        footerCta: 'Book Now →'
      };
      
      await addCompany({
        ...companyForm,
        landingPageContent
      });
      setCompanyForm({
        name: '', slug: '', email: '', phone: '', address: '', logo: '', description: '',
        adminUsername: '', adminPassword: '', primaryColor: '#2563eb', secondaryColor: '#1e40af', templateId: 'taxfile-classic'
      });
      setShowAddCompanyForm(false);
      alert('✅ Company created successfully with landing page!');
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || '',
      slug: company.slug || '',
      email: company.email || '',
      phone: company.phone || '',
      address: company.address || '',
      logo: company.logo || '',
      description: company.description || '',
      adminUsername: company.adminUsername || '',
      adminPassword: '',
      primaryColor: company.primaryColor || '#2563eb',
      secondaryColor: company.secondaryColor || '#1e40af',
      templateId: company.templateId || 'taxfile-classic'
    });
    setShowEditCompanyForm(true);
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name.trim() || !companyForm.slug.trim() || !companyForm.adminUsername.trim()) {
      alert('Please fill in Company Name, URL Slug, and Admin Username');
      return;
    }
    try {
      // Only include password in update if user entered a new one
      const updateData = { ...companyForm };
      if (!updateData.adminPassword) {
        delete updateData.adminPassword;
      }
      await updateCompany(editingCompany.id, updateData);
      setShowEditCompanyForm(false);
      setEditingCompany(null);
      setCompanyForm({
        name: '', slug: '', email: '', phone: '', address: '', logo: '', description: '',
        adminUsername: '', adminPassword: '', primaryColor: '#2563eb', secondaryColor: '#1e40af'
      });
      alert('✅ Company updated successfully!');
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  // Save Landing Page Settings
  const handleSaveLandingPageSettings = async () => {
    if (!currentCompany) {
      alert('No company selected');
      return;
    }
    try {
      await updateCompany(currentCompany.id, {
        landingPageSettings: landingPageSettings,
        // Save logo at root level for easy access
        logo: landingPageSettings.logoUrl || '',
        logoUrl: landingPageSettings.logoUrl || '',
        // Also save services/features at root level for CompanyLandingPage compatibility
        // Normalize service fields to use 'title' consistently
        servicesList: (landingPageSettings.services || []).map(s => ({
          ...s,
          title: s.title || s.name || '',
          name: s.name || s.title || '',
          description: s.description || '',
          icon: s.icon || '📋'
        })),
        featuresList: landingPageSettings.features || [],
        primaryColor: landingPageSettings.primaryColor || '',
        secondaryColor: landingPageSettings.secondaryColor || '',
        accentColor: landingPageSettings.accentColor || '',
        heroTitle: landingPageSettings.heroTitle || '',
        heroSubtitle: landingPageSettings.heroSubtitle || '',
        servicesTitle: landingPageSettings.servicesTitle || 'Our Services'
      });
      // Update currentCompany with new settings
      setCurrentCompany(prev => ({
        ...prev,
        landingPageSettings: landingPageSettings,
        logo: landingPageSettings.logoUrl || '',
        logoUrl: landingPageSettings.logoUrl || '',
        servicesList: (landingPageSettings.services || []).map(s => ({
          ...s,
          title: s.title || s.name,
          name: s.name || s.title
        })),
        featuresList: landingPageSettings.features || []
      }));
      
      // Update favicon immediately after saving
      const favicon = document.getElementById('favicon');
      if (favicon && landingPageSettings.logoUrl) {
        favicon.href = landingPageSettings.logoUrl;
      }
      
      alert('✅ Landing page settings saved successfully!');
    } catch (error) {
      alert(`❌ Error saving settings: ${error.message}`);
    }
  };

  // Update a specific feature
  const handleUpdateFeature = (index, field, value) => {
    const newFeatures = [...landingPageSettings.features];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    setLandingPageSettings({ ...landingPageSettings, features: newFeatures });
  };

  // Add a new feature
  const handleAddFeature = () => {
    setLandingPageSettings({
      ...landingPageSettings,
      features: [...landingPageSettings.features, { icon: '⭐', title: 'New Feature', description: 'Feature description' }]
    });
  };

  // Remove a feature
  const handleRemoveFeature = (index) => {
    const newFeatures = landingPageSettings.features.filter((_, i) => i !== index);
    setLandingPageSettings({ ...landingPageSettings, features: newFeatures });
  };

  const handleDeleteCompany = async (company) => {
    if (window.confirm(`Are you sure you want to delete "${company.name}"? This will also remove all associated preparers and data.`)) {
      try {
        await deleteCompany(company.id);
        alert('✅ Company deleted successfully!');
      } catch (error) {
        alert(`❌ Error: ${error.message}`);
      }
    }
  };

  const handleToggleCompanyActive = async (companyId, currentIsActive) => {
    try {
      await toggleCompanyActive(companyId, !currentIsActive);
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleToggleCompanyPublished = async (companyId, currentIsPublished, companyName, companySlug) => {
    try {
      await toggleCompanyPublished(companyId, !currentIsPublished);
      if (!currentIsPublished) {
        alert(`✅ Company "${companyName}" is now published!\n\nShare URL: ${window.location.origin}/company/${companySlug}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const copyCompanyUrl = (slug) => {
    const url = `${window.location.origin}/company/${slug}`;
    navigator.clipboard.writeText(url);
    alert('✅ URL copied to clipboard!');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (loginType === 'owner') {
      // Owner login using Firebase Authentication
      try {
        const userCredential = await signInWithEmailAndPassword(auth, loginData.username, loginData.password);
        const user = userCredential.user;
        
        // Check if user is an owner
        const isOwnerUser = await checkIsOwner(user.uid);
        
        if (isOwnerUser) {
          setFirebaseUser(user);
          setIsOwner(true);
          setShowOwnerDashboard(true);
          setIsAdmin(false);
          setIsLoggedIn(true);
          setIsPreparerView(false);
          setShowLandingPage(false);
          setIsClientView(false);
          setShowSalesPortal(false);
          setCurrentCompany(null);
          setLoginData({ username: '', password: '' });
          setShowLoginForm(false);
          setShowOwnerLogin(false);
        } else {
          await signOut(auth);
          alert('Access denied. You are not authorized as an owner.');
        }
      } catch (error) {
        console.error('Owner login error:', error);
        if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
          alert('Invalid email or password');
        } else {
          alert('Login failed: ' + error.message);
        }
      }
    } else if (loginType === 'admin') {
      // Company Admin login using company credentials
      try {
        const company = await verifyCompanyAdmin(loginData.username, loginData.password);
        
        if (company) {
          if (!company.active) {
            alert('This company account is inactive. Please contact the owner.');
            return;
          }
          setCurrentCompany(company);
          setIsAdmin(true);
          setIsLoggedIn(true);
          setIsPreparerView(false);
          setShowLandingPage(false);
          setIsClientView(false);
          setLoginData({ username: '', password: '' });
          setShowLoginForm(false);
        } else {
          alert('Invalid company admin credentials');
        }
      } catch (error) {
        console.error('Company admin login error:', error);
        alert('Login failed: ' + error.message);
      }
    } else {
      // Preparer login (still using preparer credentials stored in Firestore)
      const preparer = preparerCredentials.find(
        p => p.username === loginData.username && p.password === loginData.password
      );
      
      if (preparer) {
        if (!preparer.active) {
          alert('Your account is inactive. Please contact your company admin.');
          return;
        }
        setLoggedInPreparer(preparer);
        setIsPreparerView(true);
        setIsLoggedIn(true);
        setIsAdmin(preparer.isAdmin || false);
        setShowLoginForm(false);
        setShowLandingPage(false);
        setIsClientView(false);
        setLoginData({ username: '', password: '' });
        
        // Set the company for this preparer
        if (preparer.companyId) {
          const company = companies.find(c => c.id === preparer.companyId) || currentCompany;
          if (company) {
            setCurrentCompany(company);
          }
        }
      } else {
        alert('Invalid username or password');
      }
    }
  };

  // Callback for admin login from CompanyLandingPage
  const handleLandingPageAdminLogin = (company) => {
    setCurrentCompany(company);
    setIsAdmin(true);
    setIsLoggedIn(true);
    setIsPreparerView(false);
    setShowLandingPage(false);
    setIsClientView(false);
  };

  // Callback for preparer login from CompanyLandingPage
  const handleLandingPagePreparerLogin = (preparer) => {
    setLoggedInPreparer(preparer);
    setIsPreparerView(true);
    setIsLoggedIn(true);
    setIsAdmin(preparer.isAdmin || false);
    setShowLandingPage(false);
    setIsClientView(false);
    
    // Set the company for this preparer
    if (preparer.companyId) {
      const company = companies.find(c => c.id === preparer.companyId) || currentCompany;
      if (company) {
        setCurrentCompany(company);
      }
    }
  };

  const handleLogout = async () => {
    // Store company info before clearing for redirect
    const wasOwner = isOwner;
    const companyForRedirect = currentCompany;
    
    // Sign out from Firebase if authenticated
    if (firebaseUser) {
      try {
        await signOut(auth);
      } catch (error) {
        console.error('Firebase signout error:', error);
      }
    }
    
    setFirebaseUser(null);
    setLoggedInPreparer(null);
    setIsPreparerView(false);
    setIsAdmin(false);
    setIsOwner(false);
    setShowOwnerDashboard(false);
    setIsLoggedIn(false);
    setLoginData({ username: '', password: '' });
    
    // Clear persisted view state from localStorage
    localStorage.removeItem('isClientView');
    localStorage.removeItem('isPreparerView');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('showLandingPage');
    localStorage.removeItem('loggedInPreparer');
    localStorage.removeItem('activeTab');
    localStorage.removeItem('isOwner');
    localStorage.removeItem('showOwnerDashboard');
    localStorage.removeItem('ownerUid');
    
    // Redirect based on who logged out
    if (wasOwner) {
      // Owner logout → Show Sales Portal at root URL
      setCurrentCompany(null);
      localStorage.removeItem('currentCompany');
      setShowSalesPortal(true);
      setShowLandingPage(false);
      setIsClientView(true);
      window.history.pushState({}, '', '/');
    } else if (companyForRedirect && companyForRedirect.slug) {
      // Preparer/Admin logout → Show their company's landing page
      setCurrentCompany(companyForRedirect);
      localStorage.setItem('currentCompany', JSON.stringify(companyForRedirect));
      setShowSalesPortal(false);
      setShowLandingPage(true);
      setIsClientView(true);
      window.history.pushState({}, '', `/c/${companyForRedirect.slug}`);
    } else {
      // Fallback: Show Sales Portal
      setCurrentCompany(null);
      localStorage.removeItem('currentCompany');
      setShowSalesPortal(true);
      setShowLandingPage(false);
      setIsClientView(true);
      window.history.pushState({}, '', '/');
    }
  };

  const handleOpenProfileEditor = () => {
    setProfileEditData({
      email: loggedInPreparer?.email || '',
      phone: loggedInPreparer?.phone || '',
      address: loggedInPreparer?.address || '',
      bio: loggedInPreparer?.bio || '',
      photo: loggedInPreparer?.photo || ''
    });
    setShowProfileEditor(true);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    try {
      // Update in Firebase
      const updateData = {
        email: profileEditData.email.trim(),
        phone: profileEditData.phone.trim(),
        address: profileEditData.address.trim(),
        bio: profileEditData.bio.trim(),
        photo: profileEditData.photo
      };
      
      await updatePreparer(loggedInPreparer.id, updateData);
      
      // Update local preparer credentials
      const updatedCredentials = preparerCredentials.map(p => 
        p.username === loggedInPreparer.username 
          ? { ...p, ...updateData }
          : p
      );
      
      setPreparerCredentials(updatedCredentials);
      
      // Update logged in preparer state
      const updatedPreparer = { ...loggedInPreparer, ...updateData };
      setLoggedInPreparer(updatedPreparer);
      
      setShowProfileEditor(false);
      alert('✅ Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('❌ Error updating profile: ' + error.message);
    }
  };

  const handleAddAvailability = async (e) => {
    e.preventDefault();
    
    if (availabilityForm.dates.length === 0) {
      alert('Please select at least one date');
      return;
    }
    
    if (availabilityForm.selectedTimeSlots.length === 0) {
      alert('Please select at least one time slot');
      return;
    }

    try {
      const preparerName = loggedInPreparer?.name;
      const companyId = loggedInPreparer?.companyId || currentCompany?.id || null;
      const updatedAvailability = { ...preparerAvailability };
      
      // Add selected time slots to all selected dates
      const promises = availabilityForm.dates.map(async (date) => {
        const key = `${preparerName}_${date}`;
        const existingSlots = updatedAvailability[key] || [];
        const newSlots = [...new Set([...existingSlots, ...availabilityForm.selectedTimeSlots])];
        updatedAvailability[key] = newSlots.sort();
        
        // Save to Firestore with companyId
        await saveAvailability(preparerName, date, newSlots, companyId);
      });
      
      await Promise.all(promises);
      
      // Update local state immediately
      setPreparerAvailability(updatedAvailability);
      
      setAvailabilityForm({ dates: [], selectedTimeSlots: [], bulkMode: true });
      alert(`✅ Availability set for ${availabilityForm.dates.length} date(s)`);
    } catch (error) {
      console.error('Error setting availability:', error);
      alert('❌ Error setting availability. Please try again.');
    }
  };

  const handleDeleteAvailability = (preparerName, date) => {
    if (window.confirm(`Are you sure you want to delete all availability for ${date}?`)) {
      const key = `${preparerName}_${date}`;
      const updated = { ...preparerAvailability };
      delete updated[key];
      setPreparerAvailability(updated);
    }
  };

  // Bulk delete without individual confirmations (for Delete All Past)
  const handleBulkDeleteAvailability = (keysToDelete) => {
    const updated = { ...preparerAvailability };
    keysToDelete.forEach(key => {
      delete updated[key];
    });
    setPreparerAvailability(updated);
  };

  // Email Reminder Functions
  const handleEmailReminderSelect = (appointmentId) => {
    setEmailReminderSelectedIds(prev => 
      prev.includes(appointmentId) 
        ? prev.filter(id => id !== appointmentId)
        : [...prev, appointmentId]
    );
  };

  const handleSelectAllForEmail = (appointmentsList) => {
    if (emailReminderSelectedIds.length === appointmentsList.length) {
      setEmailReminderSelectedIds([]);
    } else {
      setEmailReminderSelectedIds(appointmentsList.map(apt => apt.id));
    }
  };

  const handleSendEmailReminder = async () => {
    const selectedAppointments = appointments.filter(apt => emailReminderSelectedIds.includes(apt.id));
    const emailsToSend = selectedAppointments.filter(apt => apt.email);
    
    if (emailsToSend.length === 0) {
      alert('❌ No valid email addresses found in selected appointments.');
      return;
    }

    // Get company info for branding
    const companyInfo = currentCompany ? {
      name: currentCompany.name || 'Tax Filing Services',
      phone: currentCompany.phone || '',
      email: currentCompany.email || '',
      address: currentCompany.address || ''
    } : { name: 'Tax Filing Services' };

    // eslint-disable-next-line no-unused-vars
    const defaultMessage = emailReminderMessage || `Dear Client,

This is a friendly reminder about your upcoming tax filing. 

Please make sure to bring all necessary documents for your appointment:
• W-2 forms from all employers
• 1099 forms (if applicable)
• Social Security cards for all family members
• Previous year's tax return
• Any other relevant tax documents

If you have any questions, please don't hesitate to contact us.

Best regards,
${companyInfo.name}
${companyInfo.phone ? `Phone: ${companyInfo.phone}` : ''}
${companyInfo.email ? `Email: ${companyInfo.email}` : ''}`;

    try {
      // Send emails (simulated - in production, integrate with your email service)
      // eslint-disable-next-line no-unused-vars
      for (const apt of emailsToSend) {
        // Here you would call your email service
        // await sendEmail({ to: apt.email, subject: emailReminderSubject, body: defaultMessage });
      }
      
      alert(`✅ Reminder Emails Sent!\n\n[key] ${emailsToSend.length} client(s) notified successfully.`);
      setEmailReminderSelectedIds([]);
      setShowEmailReminderModal(false);
      setEmailReminderMessage('');
    } catch (error) {
      console.error('Error sending emails:', error);
      alert('❌ Error sending emails. Please try again.');
    }
  };

  const handleDeleteTimeSlot = (preparerName, date, timeSlot) => {
    if (window.confirm(`Are you sure you want to delete time slot ${timeSlot}?`)) {
      const key = `${preparerName}_${date}`;
      const updatedSlots = preparerAvailability[key].filter(slot => slot !== timeSlot);
      
      if (updatedSlots.length === 0) {
        // If no slots left, delete the entire date
        const updated = { ...preparerAvailability };
        delete updated[key];
        setPreparerAvailability(updated);
      } else {
        setPreparerAvailability({
          ...preparerAvailability,
          [key]: updatedSlots
        });
      }
    }
  };

  const getAvailableTimeSlots = (selectedDate, selectedPreparer) => {
    if (!selectedDate || !selectedPreparer) return [];
    
    const key = `${selectedPreparer}_${selectedDate}`;
    const availableSlots = preparerAvailability[key] || [];
    
    // Only filter out CONFIRMED appointments - unconfirmed bookings don't block slots
    const bookedSlots = appointments
      .filter(apt => 
        apt.date === selectedDate && 
        apt.preparer === selectedPreparer &&
        apt.confirmed === true // Only confirmed appointments block the slot
      )
      .map(apt => apt.time);
    
    return availableSlots.filter(slot => !bookedSlots.includes(slot));
  };

  // ==================== Tax Returns Handlers ====================
  
  const handleTaxReturnFormChange = (e) => {
    const { name, value } = e.target;
    setTaxReturnForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitTaxReturn = async (e) => {
    e.preventDefault();
    if (!taxReturnForm.clientName || !taxReturnForm.amount) {
      alert('Please enter client name and amount');
      return;
    }

    setSavingTaxReturn(true);
    try {
      if (editingTaxReturn) {
        await updateTaxReturn(editingTaxReturn.id, {
          clientName: taxReturnForm.clientName,
          phone: taxReturnForm.phone,
          amount: parseFloat(taxReturnForm.amount),
          taxYear: parseInt(taxReturnForm.taxYear),
          paymentStatus: taxReturnForm.paymentStatus
        });
        alert('✅ Tax return updated successfully!');
        setEditingTaxReturn(null);
      } else {
        await addTaxReturn({
          clientName: taxReturnForm.clientName,
          phone: taxReturnForm.phone,
          amount: parseFloat(taxReturnForm.amount),
          taxYear: parseInt(taxReturnForm.taxYear),
          paymentStatus: taxReturnForm.paymentStatus,
          preparerId: loggedInPreparer?.id || 'admin',
          preparerName: loggedInPreparer?.name || 'Admin',
          companyId: currentCompany?.id || null
        });
        alert('✅ Tax return added successfully!');
      }
      
      setTaxReturnForm({
        clientName: '',
        phone: '',
        amount: '',
        taxYear: new Date().getFullYear() - 1,
        paymentStatus: 'unpaid'
      });
    } catch (error) {
      console.error('Error saving tax return:', error);
      alert('❌ Failed to save tax return. Please try again.');
    } finally {
      setSavingTaxReturn(false);
    }
  };

  const handleEditTaxReturn = (taxReturn) => {
    setEditingTaxReturn(taxReturn);
    setTaxReturnForm({
      clientName: taxReturn.clientName,
      phone: taxReturn.phone || '',
      amount: taxReturn.amount.toString(),
      taxYear: taxReturn.taxYear,
      paymentStatus: taxReturn.paymentStatus
    });
  };

  const handleDeleteTaxReturn = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tax return entry?')) return;
    try {
      await deleteTaxReturn(id);
      alert('✅ Tax return deleted');
    } catch (error) {
      console.error('Error deleting tax return:', error);
      alert('❌ Failed to delete tax return');
    }
  };

  const handleCancelEditTaxReturn = () => {
    setEditingTaxReturn(null);
    setTaxReturnForm({
      clientName: '',
      phone: '',
      amount: '',
      taxYear: new Date().getFullYear() - 1,
      paymentStatus: 'unpaid'
    });
  };

  // Handle tax return row selection
  const handleSelectTaxReturn = (id) => {
    setSelectedTaxReturns(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Handle select all tax returns
  const handleSelectAllTaxReturns = () => {
    if (selectedTaxReturns.length === taxReturns.length) {
      setSelectedTaxReturns([]);
    } else {
      setSelectedTaxReturns(taxReturns.map(tr => tr.id));
    }
  };

  // Download tax returns as PDF
  const handleDownloadTaxReturnsPDF = () => {
    const { jsPDF } = require('jspdf');
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Filter selected or all if none selected
    const dataToExport = selectedTaxReturns.length > 0 
      ? taxReturns.filter(tr => selectedTaxReturns.includes(tr.id))
      : taxReturns;
    
    if (dataToExport.length === 0) {
      alert('No tax returns to export');
      return;
    }

    // Calculate totals for exported data
    const exportGrandTotal = dataToExport.reduce((sum, tr) => sum + (tr.amount || 0), 0);
    const exportPaidTotal = dataToExport.filter(tr => tr.paymentStatus === 'paid').reduce((sum, tr) => sum + (tr.amount || 0), 0);
    const exportUnpaidTotal = dataToExport.filter(tr => tr.paymentStatus === 'unpaid').reduce((sum, tr) => sum + (tr.amount || 0), 0);

    // Header
    doc.setFillColor(5, 150, 105);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Tax Returns Report', pageWidth / 2, 18, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 28, { align: 'center' });

    // Summary Section
    let yPos = 50;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 14, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Records: ${dataToExport.length}`, 14, yPos);
    yPos += 7;
    doc.text(`Grand Total: $${exportGrandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 14, yPos);
    yPos += 7;
    doc.setTextColor(16, 185, 129);
    doc.text(`Paid: $${exportPaidTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 14, yPos);
    yPos += 7;
    doc.setTextColor(239, 68, 68);
    doc.text(`Unpaid: $${exportUnpaidTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 14, yPos);
    yPos += 15;

    // Table Header
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(55, 65, 81);
    doc.rect(14, yPos, pageWidth - 28, 10, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Client Name', 18, yPos + 7);
    doc.text('Phone', 65, yPos + 7);
    doc.text('Tax Year', 105, yPos + 7);
    doc.text('Amount', 135, yPos + 7);
    doc.text('Status', 170, yPos + 7);
    yPos += 14;

    // Table Rows
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    dataToExport.forEach((tr, index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
        // Repeat header on new page
        doc.setTextColor(255, 255, 255);
        doc.setFillColor(55, 65, 81);
        doc.rect(14, yPos, pageWidth - 28, 10, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Client Name', 18, yPos + 7);
        doc.text('Phone', 65, yPos + 7);
        doc.text('Tax Year', 105, yPos + 7);
        doc.text('Amount', 135, yPos + 7);
        doc.text('Status', 170, yPos + 7);
        yPos += 14;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
      }

      // Alternate row colors
      if (index % 2 === 0) {
        doc.setFillColor(249, 250, 251);
        doc.rect(14, yPos - 4, pageWidth - 28, 10, 'F');
      }

      doc.setTextColor(0, 0, 0);
      doc.text(tr.clientName?.substring(0, 20) || '', 18, yPos + 2);
      doc.text(tr.phone || '-', 65, yPos + 2);
      doc.text(tr.taxYear?.toString() || '', 105, yPos + 2);
      doc.text(`$${tr.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}`, 135, yPos + 2);
      
      // Status with color
      if (tr.paymentStatus === 'paid') {
        doc.setTextColor(22, 163, 74);
        doc.text('Paid', 170, yPos + 2);
      } else {
        doc.setTextColor(220, 38, 38);
        doc.text('Unpaid', 170, yPos + 2);
      }
      
      yPos += 10;
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
    }

    doc.save(`tax-returns-report-${new Date().toISOString().split('T')[0]}.pdf`);
    alert('✅ PDF downloaded successfully!');
  };

  // Calculate tax returns totals
  const taxReturnsGrandTotal = taxReturns.reduce((sum, tr) => sum + (tr.amount || 0), 0);
  const taxReturnsPaidTotal = taxReturns.filter(tr => tr.paymentStatus === 'paid').reduce((sum, tr) => sum + (tr.amount || 0), 0);
  const taxReturnsUnpaidTotal = taxReturns.filter(tr => tr.paymentStatus === 'unpaid').reduce((sum, tr) => sum + (tr.amount || 0), 0);

  // Download all completed IRS Waiver PDFs for a specific year (or all years)
  const handleDownloadAllPdfs = async (year = null) => {
    const completedSubmissions = irsWaiverSubmissions.filter(s => 
      (s.status === 'verified' || s.status === 'completed') && 
      (year === null || s.taxYear === year)
    );

    if (completedSubmissions.length === 0) {
      alert('No completed forms to download.');
      return;
    }

    const confirmMessage = year 
      ? `Download all ${completedSubmissions.length} completed forms for Tax Year ${year}?`
      : `Download all ${completedSubmissions.length} completed forms?`;
    
    if (!window.confirm(confirmMessage)) return;

    setDownloadingAllPdfs(true);
    setDownloadProgress({ current: 0, total: completedSubmissions.length });

    try {
      const zip = new JSZip();
      const folderName = year ? `IRS_Waiver_Forms_TY${year}` : 'IRS_Waiver_Forms_All';
      const folder = zip.folder(folderName);

      for (let i = 0; i < completedSubmissions.length; i++) {
        const submission = completedSubmissions[i];
        setDownloadProgress({ current: i + 1, total: completedSubmissions.length });

        try {
          // Prepare consent data for PDF generation
          const consentData = {
            taxpayerInfo: {
              firstName: submission.taxpayerInfo?.firstName || submission.taxpayerFirstName || '',
              lastName: submission.taxpayerInfo?.lastName || submission.taxpayerLastName || '',
              middleInitial: submission.taxpayerInfo?.middleInitial || submission.taxpayerMiddleInitial || '',
              ssn: submission.taxpayerInfo?.ssn || submission.taxpayerSSN || '',
              dob: submission.taxpayerInfo?.dob || submission.taxpayerDOB || '',
              occupation: submission.taxpayerInfo?.occupation || submission.taxpayerOccupation || '',
              address: submission.taxpayerInfo?.address || submission.taxpayerAddress || '',
              city: submission.taxpayerInfo?.city || submission.taxpayerCity || '',
              state: submission.taxpayerInfo?.state || submission.taxpayerState || '',
              zip: submission.taxpayerInfo?.zip || submission.taxpayerZip || '',
              phone: submission.taxpayerInfo?.phone || submission.taxpayerPhone || '',
              email: submission.taxpayerInfo?.email || submission.taxpayerEmail || ''
            },
            spouseInfo: submission.spouseInfo || {
              firstName: submission.spouseFirstName || '',
              lastName: submission.spouseLastName || '',
              ssn: submission.spouseSSN || '',
              dob: submission.spouseDOB || '',
              occupation: submission.spouseOccupation || ''
            },
            dependents: submission.dependents || [],
            careRecipient: submission.careRecipient || {
              name: submission.careRecipientName || '',
              address: submission.careRecipientAddress || ''
            },
            taxYear: submission.taxYear,
            signature: submission.signature || {},
            spouseSignature: submission.spouseSignature || {},
            preparerSection: submission.preparerSection || {}
          };

          // Generate PDF
          const doc = await generateIRSWaiverPDF(consentData, currentCompany, loggedInPreparer);
          
          // Get PDF as array buffer
          const pdfArrayBuffer = doc.output('arraybuffer');

          // Create filename
          const taxpayerName = `${submission.taxpayerInfo?.lastName || submission.taxpayerLastName || 'Unknown'}_${submission.taxpayerInfo?.firstName || submission.taxpayerFirstName || ''}`.replace(/\s+/g, '_');
          const fileName = `IRS_Waiver_${taxpayerName}_TY${submission.taxYear}.pdf`;

          // Add to zip
          folder.file(fileName, pdfArrayBuffer);
        } catch (pdfError) {
          console.error(`Error generating PDF for submission ${submission.id}:`, pdfError);
        }
      }

      // Generate and download zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFileName = year 
        ? `IRS_Waiver_Forms_TaxYear_${year}_${new Date().toISOString().split('T')[0]}.zip`
        : `IRS_Waiver_Forms_All_${new Date().toISOString().split('T')[0]}.zip`;
      
      saveAs(zipBlob, zipFileName);
      alert(`✅ Successfully downloaded ${completedSubmissions.length} forms!`);
    } catch (error) {
      console.error('Error downloading PDFs:', error);
      alert('❌ Error downloading forms. Please try again.');
    } finally {
      setDownloadingAllPdfs(false);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  const handlePreparerChange = (e) => {
    const newPreparer = e.target.value;
    setFormData({
      ...formData,
      preparer: newPreparer,
      time: '' // Reset time when preparer changes
    });
  };

  const getAvailableDates = (preparer) => {
    if (!preparer) return [];
    const dates = Object.keys(preparerAvailability)
      .filter(key => key.startsWith(preparer + '_'))
      .map(key => key.split('_')[1])
      .sort();
    return dates;
  };

  const isDateFullyBooked = (date, preparer) => {
    const availableSlots = getAvailableTimeSlots(date, preparer);
    return availableSlots.length === 0 && preparerAvailability[`${preparer}_${date}`]?.length > 0;
  };

  const getNextNDays = (n = 30) => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < n; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(formatDateToString(date));
    }
    return dates;
  };

  const handleConfirmAppointment = async (appointment) => {
    if (appointment.confirmed) {
      alert('This appointment is already confirmed');
      return;
    }

    // Find the preparer details
    const preparerDetails = preparerCredentials.find(p => p.name === appointment.preparer);
    
    // Get company info for the email
    const companyInfo = currentCompany ? {
      name: currentCompany.name || '',
      phone: currentCompany.phone || '',
      email: currentCompany.email || '',
      address: currentCompany.address || ''
    } : null;
    
    // Send confirmation email if email exists
    if (appointment.email) {
      try {
        // Send actual email via EmailJS with company info
        await sendConfirmationEmail(appointment, preparerDetails, companyInfo);
        
        // Save confirmation to Firestore (ensure ID is a string)
        const appointmentId = String(appointment.id);
        await confirmAppointment(appointmentId);
        
        // Update local state
        const updatedAppointments = appointments.map(apt => 
          apt.id === appointment.id ? { ...apt, confirmed: true, confirmedAt: toEasternISOString() } : apt
        );
        setAppointments(updatedAppointments);
        
        alert(`✅ Appointment Confirmed Successfully!\n\n[key] Confirmation email sent to: ${appointment.email}`);
      } catch (error) {
        console.error('❌ Error confirming appointment:', error);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error text:', error.text);
        alert(`❌ Error sending confirmation email.\n\nError: ${error.text || error.message || 'Unknown error'}\n\nPlease check console for details.`);
      }
    } else {
      // No email provided (legacy appointments)
      try {
        await confirmAppointment(appointment.id);
        
        // Update local state
        const updatedAppointments = appointments.map(apt => 
          apt.id === appointment.id ? { ...apt, confirmed: true, confirmedAt: toEasternISOString() } : apt
        );
        setAppointments(updatedAppointments);
        
        alert(`✅ Appointment Confirmed Successfully!\n\n⚠️ No email on file - please contact client at: ${appointment.phoneNumber}`);
      } catch (error) {
        console.error('Error updating appointment:', error);
        alert('❌ Error confirming appointment. Please try again.');
      }
    }
  };

  const handleDeletePreparer = async (preparerName) => {
    if (window.confirm(`Are you sure you want to remove ${preparerName}?`)) {
      try {
        const preparer = preparerCredentials.find(p => p.name === preparerName);
        if (preparer && preparer.id) {
          await deletePreparer(preparer.id);
        }
      } catch (error) {
        console.error('Error deleting preparer:', error);
        alert('❌ Error deleting preparer. Please try again.');
      }
    }
  };

  const handleEditPreparer = (preparer) => {
    setEditingPreparer(preparer);
    setNewPreparerForm({
      name: preparer.name,
      username: preparer.username,
      password: preparer.password,
      email: preparer.email || '',
      phone: preparer.phone || '',
      address: preparer.address || '',
      photo: preparer.photo || '',
      bio: preparer.bio || '',
      isAdmin: preparer.isAdmin || false
    });
    setShowEditPreparerForm(true);
  };

  const handleUpdatePreparer = async () => {
    if (!newPreparerForm.name.trim() || !newPreparerForm.username.trim() || !newPreparerForm.password.trim()) {
      alert('Please fill in all required fields (Name, Username, Password)');
      return;
    }

    try {
      const preparerData = {
        name: newPreparerForm.name.trim(),
        username: newPreparerForm.username.trim(),
        password: newPreparerForm.password.trim(),
        email: newPreparerForm.email.trim(),
        phone: newPreparerForm.phone.trim(),
        address: newPreparerForm.address.trim(),
        photo: newPreparerForm.photo,
        bio: newPreparerForm.bio.trim(),
        isAdmin: newPreparerForm.isAdmin || false
      };

      await updatePreparer(editingPreparer.id, preparerData);

      setNewPreparerForm({ name: '', username: '', password: '', email: '', phone: '', address: '', photo: '', bio: '', isAdmin: false });
      setShowEditPreparerForm(false);
      setEditingPreparer(null);
      alert('Preparer updated successfully!');
    } catch (error) {
      console.error('Error updating preparer:', error);
      alert('❌ Error updating preparer. Please try again.');
    }
  };

  const handleToggleActive = async (preparerName) => {
    try {
      const preparer = preparerCredentials.find(p => p.name === preparerName);
      if (preparer && preparer.id) {
        await togglePreparerActive(preparer.id, !preparer.active);
      }
    } catch (error) {
      console.error('Error toggling preparer:', error);
      alert('❌ Error updating preparer status. Please try again.');
    }
  };

  const handleToggleAdmin = async (preparer) => {
    const action = preparer.isAdmin ? 'remove admin access from' : 'grant admin access to';
    if (window.confirm(`Are you sure you want to ${action} ${preparer.name}?`)) {
      try {
        await updatePreparer(preparer.id, { ...preparer, isAdmin: !preparer.isAdmin });
        alert(`✅ Admin access ${preparer.isAdmin ? 'removed from' : 'granted to'} ${preparer.name}!`);
      } catch (error) {
        console.error('Error toggling admin status:', error);
        alert('❌ Error updating admin status. Please try again.');
      }
    }
  };

  const handleViewPreparer = (preparer) => {
    setViewingPreparer(preparer);
    setShowViewPreparerModal(true);
  };

  // Crop complete callback
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Create cropped image from canvas
  const createCroppedImage = async () => {
    if (!cropImage || !croppedAreaPixels) return null;
    
    const image = new Image();
    image.src = cropImage;
    
    return new Promise((resolve) => {
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas to 400x400 for circular profile photo
        canvas.width = 400;
        canvas.height = 400;
        
        // Draw the cropped area
        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          400,
          400
        );
        
        // Convert to base64
        const croppedImage = canvas.toDataURL('image/jpeg', 0.9);
        resolve(croppedImage);
      };
    });
  };

  // Handle crop confirm
  const handleCropConfirm = async () => {
    const croppedImage = await createCroppedImage();
    if (croppedImage) {
      if (cropTarget === 'newPreparer' || cropTarget === 'editPreparer') {
        setNewPreparerForm({...newPreparerForm, photo: croppedImage});
      } else if (cropTarget === 'profile') {
        setProfileEditData({...profileEditData, photo: croppedImage});
      }
    }
    // Reset cropper
    setShowCropper(false);
    setCropImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCropTarget(null);
  };

  // Handle crop cancel
  const handleCropCancel = () => {
    setShowCropper(false);
    setCropImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCropTarget(null);
  };

  const handlePhotoUpload = (e, target = 'newPreparer') => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit for original
        alert('Photo size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        // Open cropper with the image
        setCropImage(reader.result);
        setCropTarget(target);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // PUBLIC ROUTES - Check these FIRST before any login/dashboard checks
  // These routes should always show their forms regardless of login state
  
  // Show PDFDocumentFiller if document route
  if (window.location.pathname === '/document') {
    return <PDFDocumentFiller />;
  }

  // Show IRS Waiver Intake Form
  if (window.location.pathname === '/irs-waiver' || window.location.pathname === '/waiver-form') {
    return <IRSWaiverIntakeForm />;
  }

  // Show Mobile Signature Page (for QR code signing)
  if (window.location.pathname === '/mobile-sign') {
    return <MobileSignaturePage />;
  }

  // Show Video Conference Page (opens in new tab or via shared link)
  // Support both /video-conference and /conference paths
  if (window.location.pathname === '/video-conference' || window.location.pathname === '/conference') {
    return <VideoConference />;
  }

  // Show TaxpayerConsentForm if consent route
  if (showConsentForm) {
    return <TaxpayerConsentForm />;
  }
  
  // Show PWA Login Prompt when app is opened from iOS home screen without session
  if (showPWALoginPrompt) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '40px 30px',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📱</div>
          <h1 style={{ color: '#1e293b', fontSize: '1.5rem', marginBottom: '10px' }}>Welcome to TaxFilePro</h1>
          <p style={{ color: '#64748b', marginBottom: '30px', lineHeight: '1.6' }}>
            Enter your company name to access the booking page and login as staff.
          </p>
          
          {pwaLoadingCompany ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⏳</div>
              <p style={{ color: '#64748b' }}>Loading company...</p>
            </div>
          ) : pwaError ? (
            <div style={{ padding: '20px', background: '#fef2f2', borderRadius: '12px', marginBottom: '20px' }}>
              <p style={{ color: '#dc2626', margin: 0 }}>{pwaError}</p>
              <button
                onClick={() => { setPwaError(null); setPwaCompanySlug(''); }}
                style={{ marginTop: '10px', padding: '8px 16px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                Try Again
              </button>
            </div>
          ) : (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', textAlign: 'left', marginBottom: '8px', color: '#374151', fontWeight: '600', fontSize: '0.9rem' }}>
                Company Name or URL Slug
              </label>
              <input
                type="text"
                value={pwaCompanySlug}
                onChange={(e) => setPwaCompanySlug(e.target.value)}
                placeholder="e.g., frdtax"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s'
                }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    document.getElementById('pwa-go-btn')?.click();
                  }
                }}
              />
              <p style={{ textAlign: 'left', marginTop: '8px', fontSize: '0.8rem', color: '#94a3b8' }}>
                This is the name in your company's booking URL (e.g., "frdtax" from /company/frdtax)
              </p>
            </div>
            
            <button
              id="pwa-go-btn"
              type="button"
              onClick={async () => {
                if (pwaCompanySlug.trim()) {
                  const slug = pwaCompanySlug.trim().toLowerCase().replace(/\s+/g, '-');
                  console.log('📱 PWA loading company:', slug);
                  setPwaLoadingCompany(true);
                  setPwaError(null);
                  
                  try {
                    // Load company directly from Firestore (no URL navigation needed)
                    const company = await getCompanyBySlug(slug);
                    console.log('📱 Company loaded:', company);
                    
                    if (company) {
                      const isActive = company.isActive !== false && company.active !== false;
                      const isPublished = company.isPublished || company.published;
                      
                      if (isActive && isPublished) {
                        // Save for future PWA opens
                        localStorage.setItem('pwa_company_slug', slug);
                        sessionStorage.setItem('pwa_company_slug', slug);
                        
                        // Set company and show landing page (no navigation!)
                        setCurrentCompany(company);
                        setIsClientView(true);
                        setShowLandingPage(true);
                        setShowSalesPortal(false);
                        setShowPWALoginPrompt(false);
                        console.log('✅ PWA: Company set, showing landing page');
                      } else {
                        setPwaError('This company is currently unavailable.');
                        setPwaLoadingCompany(false);
                      }
                    } else {
                      setPwaError('Company not found. Please check the name and try again.');
                      setPwaLoadingCompany(false);
                    }
                  } catch (err) {
                    console.error('❌ PWA error loading company:', err);
                    setPwaError('Error loading company. Please try again.');
                    setPwaLoadingCompany(false);
                  }
                } else {
                  alert('Please enter your company name');
                }
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
              }}
            >
              🚀 Go to Company Page & Login
            </button>
          </div>
          )}
          
          <div style={{ marginTop: '24px', padding: '16px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
            <p style={{ color: '#166534', fontSize: '0.85rem', margin: 0, lineHeight: '1.5' }}>
              <strong>💡 Tip:</strong> After logging in as preparer, your session will be saved. Next time you open the app, you'll go straight to your dashboard.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show the Modern Preparer Dashboard with sidebar when logged in as preparer
  if (isPreparerView && loggedInPreparer) {
    return (
      <PreparerDashboard 
        preparer={loggedInPreparer}
        company={currentCompany}
        onLogout={handleLogout}
        onUpdatePreparer={(updatedPreparer) => {
          setLoggedInPreparer(updatedPreparer);
        }}
        onSwitchToAdmin={loggedInPreparer?.isAdmin ? async () => {
          if (loggedInPreparer.companyId) {
            const company = await getCompanyById(loggedInPreparer.companyId);
            if (company) {
              setCurrentCompany(company);
              localStorage.setItem('currentCompany', JSON.stringify(company));
            }
          }
          setIsClientView(false);
          setIsAdmin(true);
          setIsPreparerView(false);
          setMobileMenuOpen(false);
        } : undefined}
      />
    );
  }

  return (
    <div 
      className={`App ${isPreparerView ? 'preparer-view-active' : ''} ${isPreparerView && sidebarCollapsed ? 'sidebar-collapsed' : ''} ${showOwnerDashboard ? 'owner-dashboard-active' : ''} ${isAdmin && currentCompany ? 'admin-dashboard-active' : ''} ${showSalesPortal ? 'sales-portal-active' : ''} ${isClientView && showLandingPage ? 'landing-page-active' : ''}`}
      style={(isPreparerView || showOwnerDashboard || (isAdmin && currentCompany) || showSalesPortal || (isClientView && showLandingPage)) ? {
        padding: 0,
        margin: 0,
        maxWidth: '100%',
        width: '100%',
        minHeight: '100vh',
        backgroundColor: (showSalesPortal || (isClientView && showLandingPage)) ? '#0f172a' : '#f8fafc'
      } : {}}
    >
      {/* Photo Cropper Modal */}
      {showCropper && cropImage && (
        <div className="modal-overlay cropper-modal-overlay">
          <div className="modal-content cropper-modal">
            <div className="modal-header">
              <h2>📷 Adjust Your Photo</h2>
              <button className="close-btn" onClick={handleCropCancel}>x</button>
            </div>
            <div className="cropper-container">
              <Cropper
                image={cropImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
            <div className="cropper-controls">
              <div className="zoom-control">
                <label>🔍 Zoom:</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="zoom-slider"
                />
                <span className="zoom-value">{zoom.toFixed(1)}x</span>
              </div>
              <p className="crop-instruction">Drag to position • Scroll or use slider to zoom</p>
            </div>
            <div className="cropper-actions">
              <button className="btn btn-secondary" onClick={handleCropCancel}>
                ✕ Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCropConfirm}>
                ✅ Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Owner Login Page - Show at /owner-pc route */}
      {showOwnerLogin && !isOwner && (
        <div className="login-page">
          <div className="login-container">
            <div className="login-header">
              <h1 className="login-title">👑 Platform Owner</h1>
              <p className="login-subtitle">Secure Owner Access</p>
            </div>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                // Sign in with Firebase Auth
                const userCredential = await signInWithEmailAndPassword(auth, ownerLoginData.email, ownerLoginData.password);
                const user = userCredential.user;
                
                // Check if user is an owner
                const isOwnerUser = await checkIsOwner(user.uid);
                if (isOwnerUser) {
                  setFirebaseUser(user);
                  setIsOwner(true);
                  setShowOwnerDashboard(true);
                  setShowOwnerLogin(false);
                  setIsLoggedIn(true);
                  setIsClientView(false);
                  setShowLandingPage(false);
                  setShowSalesPortal(false);
                  localStorage.setItem('isOwner', 'true');
                  localStorage.setItem('showOwnerDashboard', 'true');
                  localStorage.setItem('isLoggedIn', 'true');
                  localStorage.setItem('ownerUid', user.uid);
                  window.history.pushState({}, '', '/');
                } else {
                  // Not an owner, sign out
                  await signOut(auth);
                  alert('This account is not authorized as a platform owner');
                }
              } catch (error) {
                console.error('Owner login error:', error);
                if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
                  alert('Invalid email or password');
                } else {
                  alert('Login failed: ' + error.message);
                }
              }
            }} className="login-form">
              <div className="login-form-header">
                <h2>Owner Login</h2>
                <p>Platform Administrator Access</p>
              </div>
              
              <div className="form-group">
                <label htmlFor="owner-email">
                  <span className="label-icon">📧</span>
                  Email
                </label>
                <input
                  type="email"
                  id="owner-email"
                  value={ownerLoginData.email}
                  onChange={(e) => setOwnerLoginData({...ownerLoginData, email: e.target.value})}
                  placeholder="Enter owner email"
                  required
                  autoComplete="email"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="owner-password">
                  <span className="label-icon">🔒</span>
                  Password
                </label>
                <input
                  type="password"
                  id="owner-password"
                  value={ownerLoginData.password}
                  onChange={(e) => setOwnerLoginData({...ownerLoginData, password: e.target.value})}
                  placeholder="Enter owner password"
                  required
                  autoComplete="current-password"
                />
              </div>
              
              <button type="submit" className="btn-login">
                <span>🔍“</span>
                Sign In as Owner
              </button>
            </form>
            
            <div className="login-footer">
              <p><a href="/" className="client-link">← Back to main site</a></p>
            </div>
          </div>
        </div>
      )}

      {/* Company Admin Login Page - Show at /admin route */}
      {showAdminLogin && !isAdmin && (
        <div className="login-page">
          <div className="login-container">
            <div className="login-header" style={{background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'}}>
              <h1 className="login-title">🏢 Company Admin</h1>
              <p className="login-subtitle">Administrator Portal Access</p>
            </div>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const company = await verifyCompanyAdmin(adminLoginData.username, adminLoginData.password);
                if (company) {
                  if (!company.active) {
                    alert('This company account is inactive. Please contact the owner.');
                    return;
                  }
                  // Successful admin login
                  setCurrentCompany(company);
                  setIsAdmin(true);
                  setIsLoggedIn(true);
                  setIsClientView(false);
                  setShowLandingPage(false);
                  setShowAdminLogin(false);
                  setShowSalesPortal(false);
                  localStorage.setItem('currentCompany', JSON.stringify(company));
                  localStorage.setItem('isAdmin', 'true');
                  localStorage.setItem('isLoggedIn', 'true');
                  window.history.pushState({}, '', `/c/${company.slug}`);
                } else {
                  alert('Invalid company admin credentials');
                }
              } catch (error) {
                console.error('Admin login error:', error);
                alert('Login failed: ' + error.message);
              }
            }} className="login-form">
              <div className="login-form-header">
                <h2>Admin Login</h2>
                <p>Company Administrator Access</p>
              </div>
              
              <div className="form-group">
                <label htmlFor="admin-username">
                  <span className="label-icon">[U]</span>
                  Username
                </label>
                <input
                  type="text"
                  id="admin-username"
                  value={adminLoginData.username}
                  onChange={(e) => setAdminLoginData({...adminLoginData, username: e.target.value})}
                  placeholder="Enter admin username"
                  required
                  autoComplete="username"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="admin-password">
                  <span className="label-icon">🔒</span>
                  Password
                </label>
                <input
                  type="password"
                  id="admin-password"
                  value={adminLoginData.password}
                  onChange={(e) => setAdminLoginData({...adminLoginData, password: e.target.value})}
                  placeholder="Enter admin password"
                  required
                  autoComplete="current-password"
                />
              </div>
              
              <button type="submit" className="btn-login" style={{background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'}}>
                <span>🔍“</span>
                Sign In as Admin
              </button>
            </form>
            
            <div className="login-footer">
              <p><a href="/" className="client-link">← Back to main site</a></p>
            </div>
          </div>
        </div>
      )}

      {/* Sales Portal - Show at root URL - NOVA BOOTSTRAP STYLE */}
      {showSalesPortal && (
        <div className="nova-home">
          {/* Animated Background */}
          <div className="nova-hero-bg">
            <div className="nova-gradient-circle circle-1"></div>
            <div className="nova-gradient-circle circle-2"></div>
            <div className="nova-gradient-circle circle-3"></div>
            <div className="nova-particles">
              {[...Array(15)].map((_, i) => (
                <div key={i} className="nova-particle" style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 4}s`
                }}></div>
              ))}
            </div>
          </div>

          {/* Nova Navbar */}
          <nav className="nova-navbar scrolled" style={{background: 'linear-gradient(135deg, #0a2540 0%, #134e5e 50%, #1a6b3d 100%)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)'}}>
            <div className="nova-container">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#top" className="nova-brand" style={{color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px'}} onClick={(e) => { e.preventDefault(); window.scrollTo({top: 0, behavior: 'smooth'}); }}>
                <img src="/logo.png" alt="TaxFilePro" style={{height: '150px', width: 'auto'}} />
              </a>
              <div className="nova-nav-menu">
                <a href="#features" className="nova-nav-link" style={{color: '#ffffff'}}>Features</a>
                <a href="#how-it-works" className="nova-nav-link" style={{color: '#ffffff'}}>How It Works</a>
                <a href="#request-form" className="nova-nav-link" style={{color: '#ffffff'}}>Get Started</a>
                <a href="#contact" className="nova-nav-link" style={{color: '#ffffff'}}>Contact</a>
              </div>
            </div>
          </nav>

          {/* Nova Hero Section */}
          <section className="nova-hero">
            <div className="nova-container">
              <div className="nova-hero-content">
                <div className="nova-hero-text">
                  <h1 className="nova-hero-title">
                    Simplify Scheduling. <span className="nova-gradient-text">Grow Your Tax Business.</span>
                  </h1>
                  <p className="nova-hero-subtitle">
                    The appointment booking platform designed exclusively for tax professionals. 
                    Let clients book online 24/7 while you focus on what matters most.
                  </p>
                </div>
                <div className="nova-hero-visual">
                  <div className="nova-hero-card nova-card-main">
                    <div className="nova-card-header">
                      <div className="nova-card-dots">
                        <span></span><span></span><span></span>
                      </div>
                    </div>
                    <div className="nova-card-body">
                      <div className="nova-card-icon">📅</div>
                      <h3>New Booking!</h3>
                      <p>John Smith - Tax Return</p>
                      <div className="nova-card-status">
                        <span className="nova-status-dot"></span>
                        Confirmed
                      </div>
                    </div>
                  </div>
                  <div className="nova-hero-card nova-card-float-1">
                    <div className="nova-mini-card">
                      <span className="nova-mini-icon">📈</span>
                      <div>
                        <strong>+32%</strong>
                        <small>More Bookings</small>
                      </div>
                    </div>
                  </div>
                  <div className="nova-hero-card nova-card-float-2">
                    <div className="nova-mini-card">
                      <span className="nova-mini-icon">⭐</span>
                      <div>
                        <strong>*****</strong>
                        <small>5.0 Rating</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="nova-hero-wave">
              <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
                <path d="M0,64L48,69.3C96,75,192,85,288,90.7C384,96,480,96,576,85.3C672,75,768,53,864,48C960,43,1056,53,1152,58.7C1248,64,1344,64,1392,64L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="#ffffff"/>
              </svg>
            </div>
          </section>

          {/* Trusted By Section */}
          <section className="nova-trusted">
            <div className="nova-container">
              <p className="nova-trusted-text">Why Tax Professionals Choose Us</p>
              <div className="nova-trusted-badges">
                <div className="nova-trust-item">
                  <span className="nova-trust-icon">⚡</span>
                  <span>Ready in Minutes</span>
                </div>
                <div className="nova-trust-item">
                  <span className="nova-trust-icon">🔒</span>
                  <span>Secure & Reliable</span>
                </div>
              </div>
            </div>
          </section>

          {/* Problem Section */}
          <section className="nova-features" style={{background: 'var(--nova-light)'}}>
            <div className="nova-container">
              <div className="nova-section-header">
                <span className="nova-section-badge">Sound Familiar?</span>
                <h2 className="nova-section-title">The Struggles of <span className="nova-gradient-text">Manual Scheduling</span></h2>
                <p className="nova-section-desc">These common problems cost you time, money, and clients every day.</p>
              </div>
              <div className="nova-features-grid">
                <div className="nova-feature-card">
                  <div className="nova-feature-icon nova-icon-orange">
                    <span>📞</span>
                  </div>
                  <h3>Endless Phone Calls</h3>
                  <p>Hours wasted playing phone tag instead of serving clients and growing your business.</p>
                </div>
                <div className="nova-feature-card">
                  <div className="nova-feature-icon nova-icon-pink">
                    <span>📅</span>
                  </div>
                  <h3>Scheduling Conflicts</h3>
                  <p>Double bookings causing frustration for you and your clients.</p>
                </div>
                <div className="nova-feature-card">
                  <div className="nova-feature-icon nova-icon-purple">
                    <span>🌅</span>
                  </div>
                  <h3>Missed Appointments</h3>
                  <p>No-shows eating into your valuable time and revenue.</p>
                </div>
                <div className="nova-feature-card">
                  <div className="nova-feature-icon nova-icon-blue">
                    <span>💰</span>
                  </div>
                  <h3>Lost Opportunities</h3>
                  <p>Clients booking elsewhere because you weren't available when they called.</p>
                </div>
              </div>
              <p style={{textAlign: 'center', marginTop: '40px', fontSize: '1.2rem', color: 'var(--nova-dark)'}}>
                <strong style={{background: 'var(--nova-gradient-2)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>We solved it.</strong> TaxFile Pro automates your scheduling so you can focus on taxes.
              </p>
            </div>
          </section>

          {/* How It Works Section */}
          <section id="how-it-works" className="nova-how-it-works">
            <div className="nova-container">
              <div className="nova-section-header">
                <span className="nova-section-badge">How It Works</span>
                <h2 className="nova-section-title">Get Started in <span className="nova-gradient-text">3 Easy Steps</span></h2>
                <p className="nova-section-desc">From signup to your first booking in under 10 minutes.</p>
              </div>
              <div className="nova-steps">
                <div className="nova-step">
                  <div className="nova-step-number">01</div>
                  <div className="nova-step-icon">🎨</div>
                  <h3>Create Your Account</h3>
                  <p>Sign up and customize your booking page with your logo and brand colors.</p>
                </div>
                <div className="nova-step-connector">
                  <svg width="100" height="20" viewBox="0 0 100 20">
                    <path d="M0 10 L90 10" stroke="url(#grad1)" strokeWidth="2" strokeDasharray="5,5"/>
                    <polygon points="90,5 100,10 90,15" fill="#7c3aed"/>
                    <defs>
                      <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3b82f6"/>
                        <stop offset="100%" stopColor="#7c3aed"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div className="nova-step">
                  <div className="nova-step-number">02</div>
                  <div className="nova-step-icon">🔍—</div>
                  <h3>Share Your Link</h3>
                  <p>Add your booking link to your website, emails, and social media.</p>
                </div>
                <div className="nova-step-connector">
                  <svg width="100" height="20" viewBox="0 0 100 20">
                    <path d="M0 10 L90 10" stroke="url(#grad2)" strokeWidth="2" strokeDasharray="5,5"/>
                    <polygon points="90,5 100,10 90,15" fill="#ec4899"/>
                    <defs>
                      <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#7c3aed"/>
                        <stop offset="100%" stopColor="#ec4899"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div className="nova-step">
                  <div className="nova-step-number">03</div>
                  <div className="nova-step-icon">✔️</div>
                  <h3>Accept Bookings</h3>
                  <p>Clients book online 24/7. You receive notifications instantly.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section id="features" className="nova-features">
            <div className="nova-container">
              <div className="nova-section-header">
                <span className="nova-section-badge">Features</span>
                <h2 className="nova-section-title">Everything You Need to <span className="nova-gradient-text">Succeed</span></h2>
                <p className="nova-section-desc">Powerful tools built specifically for tax professionals.</p>
              </div>
              <div className="nova-features-grid">
                <div className="nova-feature-card">
                  <div className="nova-feature-icon nova-icon-blue">
                    <span>🌅</span>
                  </div>
                  <h3>Professional Booking Page</h3>
                  <p>Your own branded online booking page where clients can schedule appointments anytime.</p>
                  <span className="nova-feature-badge">Popular</span>
                </div>
                <div className="nova-feature-card">
                  <div className="nova-feature-icon nova-icon-purple">
                    <span>👥</span>
                  </div>
                  <h3>Team Management</h3>
                  <p>Add your team members and manage everyone's schedule from one dashboard.</p>
                </div>
                <div className="nova-feature-card">
                  <div className="nova-feature-icon nova-icon-green">
                    <span>📊</span>
                  </div>
                  <h3>Analytics Dashboard</h3>
                  <p>Track appointments, view reports, and monitor your business growth.</p>
                </div>
                <div className="nova-feature-card">
                  <div className="nova-feature-icon nova-icon-pink">
                    <span>🎨</span>
                  </div>
                  <h3>Custom Branding</h3>
                  <p>Add your logo and colors to match your company's identity.</p>
                </div>
                <div className="nova-feature-card">
                  <div className="nova-feature-icon nova-icon-orange">
                    <span>🔍”</span>
                  </div>
                  <h3>Smart Reminders</h3>
                  <p>Automated email reminders to reduce no-shows and keep clients informed.</p>
                </div>
                <div className="nova-feature-card">
                  <div className="nova-feature-icon nova-icon-cyan">
                    <span>🔒</span>
                  </div>
                  <h3>Secure & Reliable</h3>
                  <p>Enterprise-grade security to protect your data and your clients.</p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="nova-features" style={{background: 'var(--nova-light)'}}>
            <div className="nova-container">
              <div className="nova-section-header">
                <span className="nova-section-badge">FAQ</span>
                <h2 className="nova-section-title">Frequently Asked <span className="nova-gradient-text">Questions</span></h2>
              </div>
              <div className="nova-features-grid" style={{gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))'}}>
                <div className="nova-feature-card">
                  <h3 style={{marginBottom: '12px'}}>How quickly can I get started?</h3>
                  <p>Most users are up and running in under 10 minutes. Simply sign up, add your info, and share your booking link.</p>
                </div>
                <div className="nova-feature-card">
                  <h3 style={{marginBottom: '12px'}}>Is there a free plan?</h3>
                  <p>Yes! Our free plan includes unlimited appointments. No credit card required to get started.</p>
                </div>
                <div className="nova-feature-card">
                  <h3 style={{marginBottom: '12px'}}>Do you offer support?</h3>
                  <p>We provide email support for all users and priority support for premium plans.</p>
                </div>
                <div className="nova-feature-card">
                  <h3 style={{marginBottom: '12px'}}>Can I cancel anytime?</h3>
                  <p>Absolutely. No contracts, no commitments. Cancel whenever you need to.</p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA / Request Form Section */}
          <section id="request-form" className="nova-cta" style={{padding: '100px 0'}}>
            <div className="nova-cta-bg">
              <div className="nova-cta-circle nova-cta-circle-1"></div>
              <div className="nova-cta-circle nova-cta-circle-2"></div>
            </div>
            <div className="nova-container">
              <div style={{display: 'flex', justifyContent: 'center'}}>
                <div style={{background: 'white', borderRadius: '24px', padding: '40px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)', maxWidth: '500px', width: '100%'}}>
                  {salesRequestSubmitted ? (
                    <div style={{textAlign: 'center', padding: '20px'}}>
                      <div style={{width: '80px', height: '80px', background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '2.5rem', color: 'white'}}>✅</div>
                      <h3 style={{fontSize: '1.5rem', fontWeight: '700', color: 'var(--nova-dark)', marginBottom: '12px'}}>Thank You!</h3>
                      <p style={{color: 'var(--nova-gray)', marginBottom: '24px'}}>We've received your request and will contact you within 24 hours to set up your account.</p>
                      <button 
                        className="nova-btn nova-btn-primary"
                        onClick={() => {
                          setSalesRequestSubmitted(false);
                          setSalesRequestForm({
                            companyName: '',
                            contactName: '',
                            email: '',
                            phone: '',
                            address: '',
                            message: '',
                            expectedPreparers: '1-5'
                          });
                        }}
                      >
                        Submit Another Request
                      </button>
                    </div>
                  ) : (
                    <form 
                      onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          await addCompanyRequest(salesRequestForm);
                          setSalesRequestSubmitted(true);
                        } catch (error) {
                          console.error('Form submission error:', error);
                          if (error.code === 'permission-denied') {
                            alert('Permission denied. Please contact support.');
                          } else {
                            alert(`Error: ${error.message || 'Please try again.'}`);
                          }
                        }
                      }}
                    >
                      <h3 style={{fontSize: '1.5rem', fontWeight: '700', color: 'var(--nova-dark)', marginBottom: '24px', textAlign: 'center'}}>Get Started Today</h3>
                      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '16px'}}>
                        <div style={{gridColumn: 'span 1'}}>
                          <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: 'var(--nova-dark)', marginBottom: '8px'}}>Company Name *</label>
                          <input
                            type="text"
                            value={salesRequestForm.companyName}
                            onChange={(e) => setSalesRequestForm({...salesRequestForm, companyName: e.target.value})}
                            placeholder="Your Tax Company"
                            required
                            style={{width: '100%', padding: '14px 18px', border: '2px solid var(--nova-light)', borderRadius: '12px', fontSize: '1rem', transition: 'all 0.3s', outline: 'none'}}
                          />
                        </div>
                        <div style={{gridColumn: 'span 1'}}>
                          <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: 'var(--nova-dark)', marginBottom: '8px'}}>Your Name *</label>
                          <input
                            type="text"
                            value={salesRequestForm.contactName}
                            onChange={(e) => setSalesRequestForm({...salesRequestForm, contactName: e.target.value})}
                            placeholder="John Smith"
                            required
                            style={{width: '100%', padding: '14px 18px', border: '2px solid var(--nova-light)', borderRadius: '12px', fontSize: '1rem', transition: 'all 0.3s', outline: 'none'}}
                          />
                        </div>
                        <div style={{gridColumn: 'span 1'}}>
                          <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: 'var(--nova-dark)', marginBottom: '8px'}}>Email Address *</label>
                          <input
                            type="email"
                            value={salesRequestForm.email}
                            onChange={(e) => setSalesRequestForm({...salesRequestForm, email: e.target.value})}
                            placeholder="you@company.com"
                            required
                            style={{width: '100%', padding: '14px 18px', border: '2px solid var(--nova-light)', borderRadius: '12px', fontSize: '1rem', transition: 'all 0.3s', outline: 'none'}}
                          />
                        </div>
                        <div style={{gridColumn: 'span 1'}}>
                          <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: 'var(--nova-dark)', marginBottom: '8px'}}>Phone Number *</label>
                          <input
                            type="tel"
                            value={salesRequestForm.phone}
                            onChange={(e) => setSalesRequestForm({...salesRequestForm, phone: formatPhoneNumber(e.target.value)})}
                            placeholder="(555) 123-4567"
                            required
                            style={{width: '100%', padding: '14px 18px', border: '2px solid var(--nova-light)', borderRadius: '12px', fontSize: '1rem', transition: 'all 0.3s', outline: 'none'}}
                          />
                        </div>
                        <div style={{gridColumn: 'span 2'}}>
                          <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: 'var(--nova-dark)', marginBottom: '8px'}}>Team Size</label>
                          <select
                            value={salesRequestForm.expectedPreparers}
                            onChange={(e) => setSalesRequestForm({...salesRequestForm, expectedPreparers: e.target.value})}
                            style={{width: '100%', padding: '14px 18px', border: '2px solid var(--nova-light)', borderRadius: '12px', fontSize: '1rem', transition: 'all 0.3s', outline: 'none', background: 'white'}}
                          >
                            <option value="1-5">1-5 Tax Preparers</option>
                            <option value="6-10">6-10 Tax Preparers</option>
                            <option value="11-20">11-20 Tax Preparers</option>
                            <option value="20+">20+ Tax Preparers</option>
                          </select>
                        </div>
                      </div>
                      <button type="submit" className="nova-btn nova-btn-primary nova-btn-block" style={{marginTop: '24px', padding: '16px'}}>
                        <span>Submit Request</span>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </button>
                      <p style={{fontSize: '0.8rem', color: 'var(--nova-gray)', textAlign: 'center', marginTop: '16px'}}>
                        By signing up, you agree to our Terms of Service and Privacy Policy
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="nova-features">
            <div className="nova-container">
              <div className="nova-section-header">
                <span className="nova-section-badge">Contact</span>
                <h2 className="nova-section-title">Get in <span className="nova-gradient-text">Touch</span></h2>
                <p className="nova-section-desc">Have questions? We'd love to hear from you.</p>
              </div>
              <div style={{display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap'}}>
                <a href="mailto:contact@taxfilepro.com" className="nova-feature-card" style={{textDecoration: 'none', textAlign: 'center', flex: '1', maxWidth: '280px'}}>
                  <div className="nova-feature-icon nova-icon-blue" style={{margin: '0 auto 16px'}}>
                    <span>📧</span>
                  </div>
                  <h3>Email Us</h3>
                  <p style={{color: 'var(--nova-primary)', fontWeight: '600'}}>contact@taxfilepro.com</p>
                </a>
                <a href="tel:+17173433822" className="nova-feature-card" style={{textDecoration: 'none', textAlign: 'center', flex: '1', maxWidth: '280px'}}>
                  <div className="nova-feature-icon nova-icon-green" style={{margin: '0 auto 16px'}}>
                    <span>📞</span>
                  </div>
                  <h3>Call Us</h3>
                  <p style={{color: 'var(--nova-primary)', fontWeight: '600'}}>(717) 343-3822</p>
                </a>
                <div className="nova-feature-card" style={{textAlign: 'center', flex: '1', maxWidth: '280px'}}>
                  <div className="nova-feature-icon nova-icon-purple" style={{margin: '0 auto 16px'}}>
                    <span>⏰</span>
                  </div>
                  <h3>Business Hours</h3>
                  <p style={{color: 'var(--nova-primary)', fontWeight: '600'}}>Mon-Fri 9AM-6PM EST</p>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="nova-footer">
            <div className="nova-container">
              <div className="nova-footer-grid">
                <div className="nova-footer-brand">
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a href="#top" className="nova-brand" style={{marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px'}} onClick={(e) => { e.preventDefault(); window.scrollTo({top: 0, behavior: 'smooth'}); }}>
                    <img src="/logo.png" alt="TaxFilePro" style={{height: '50px', width: 'auto'}} />
                    <span style={{fontSize: '1.3rem', fontWeight: '700'}}>Tax<span className="nova-highlight">File</span>Pro</span>
                  </a>
                  <p>Professional appointment booking for tax companies. Simplify scheduling and grow your business.</p>
                  <div className="nova-social-links">
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a href="#!" className="nova-social" onClick={(e) => e.preventDefault()}>X</a>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a href="#!" className="nova-social" onClick={(e) => e.preventDefault()}>in</a>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a href="#!" className="nova-social" onClick={(e) => e.preventDefault()}>f</a>
                  </div>
                </div>
                <div className="nova-footer-links">
                  <h4>Product</h4>
                  <a href="#features">Features</a>
                  <a href="#how-it-works">How It Works</a>
                  <a href="#request-form">Get Started</a>
                </div>
                <div className="nova-footer-links">
                  <h4>Company</h4>
                  <a href="#contact">Contact</a>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a href="#!" onClick={(e) => e.preventDefault()}>About Us</a>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a href="#!" onClick={(e) => e.preventDefault()}>Blog</a>
                </div>
                <div className="nova-footer-links">
                  <h4>Legal</h4>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a href="#!" onClick={(e) => e.preventDefault()}>Privacy Policy</a>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a href="#!" onClick={(e) => e.preventDefault()}>Terms of Service</a>
                </div>
              </div>
              <div className="nova-footer-bottom">
                <p>(c) {new Date().getFullYear()} TaxFile Pro. All rights reserved. Made with love for tax professionals.</p>
              </div>
            </div>
          </footer>
        </div>
      )}

      {/* Company Landing Page - Show when client visits company URL */}
      {isClientView && currentCompany && showLandingPage && !showSalesPortal && (
        <CompanyLandingPage 
          companySlug={currentCompany.slug}
          onAdminLogin={handleLandingPageAdminLogin}
          onPreparerLogin={handleLandingPagePreparerLogin}
        />
      )}

      {/* Main App Content - Only show when NOT on Sales Portal */}
      {!showSalesPortal && !isClientView && (
        <>
        {/* Login Page - Show if not logged in and not client view */}
        {!isLoggedIn && !isClientView && !showOwnerDashboard && !showOwnerLogin ? (
          <div className="login-page">
          <div className="login-container">
            <div className="login-header">
              <h1 className="login-title">Tax Appointment System</h1>
              <p className="login-subtitle">Secure Login Portal</p>
            </div>
            
            <div className="login-type-selector">
              <button 
                className={`login-type-btn ${loginType === 'admin' ? 'active' : ''}`}
                onClick={() => {
                  setLoginType('admin');
                  setLoginData({ username: '', password: '' });
                }}
              >
                <span className="login-type-icon">🏢</span>
                <span>Company Admin</span>
              </button>
              <button 
                className={`login-type-btn ${loginType === 'preparer' ? 'active' : ''}`}
                onClick={() => {
                  setLoginType('preparer');
                  setLoginData({ username: '', password: '' });
                }}
              >
                <span className="login-type-icon">[U]</span>
                <span>Preparer Login</span>
              </button>
            </div>

            <form onSubmit={handleLogin} className="login-form">
              <div className="login-form-header">
                <h2>{loginType === 'admin' ? 'Company Admin Login' : 'Preparer Login'}</h2>
                <p>{loginType === 'admin' ? 'Company Administrator Access' : 'Tax Preparer Portal'}</p>
              </div>
              
              <div className="form-group">
                <label htmlFor="username">
                  <span className="label-icon">[U]</span>
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  value={loginData.username}
                  onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                  placeholder="Enter your username"
                  required
                  autoComplete="username"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="password">
                  <span className="label-icon">🔒</span>
                  Password
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswordFor['mainLogin'] ? "text" : "password"}
                    id="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPasswordFor({...showPasswordFor, mainLogin: !showPasswordFor['mainLogin']})}
                    aria-label="Toggle password visibility"
                  >
                    {showPasswordFor['mainLogin'] ? '⭕' : '⭕'}
                  </button>
                </div>
              </div>
              
              <button type="submit" className="btn-login">
                <span>🔍“</span>
                Sign In
              </button>
            </form>
            
            <div className="login-footer">
              <p>Client booking? <a href="/" className="client-link" onClick={(e) => { e.preventDefault(); setIsClientView(true); setShowLandingPage(true); }}>Click here</a></p>
              <p style={{marginTop: '10px'}}><a href="/owner-pc" className="client-link" style={{fontSize: '0.85em', opacity: 0.7}}>Platform Owner</a></p>
            </div>
          </div>
        </div>
      ) : showAddCompanyFullPage && isOwner ? (
        /* Full Page Company Creation - Template First, Preview Below */
        <div className="company-creation-fullpage">
          <div className="creation-header">
            <button 
              className="back-btn"
              onClick={() => {
                setShowAddCompanyFullPage(false);
                setCompanyForm({
                  name: '', slug: '', email: '', phone: '', address: '', logo: '',
                  description: '', adminUsername: '', adminPassword: '',
                  primaryColor: '#2563eb', secondaryColor: '#1e40af', templateId: 'taxfile-classic'
                });
              }}
            >
              ← Back to Dashboard
            </button>
            <h1>🏢 Create New Company</h1>
          </div>
          
          <div className="creation-single-column">
            <form onSubmit={(e) => { handleAddCompany(e); setShowAddCompanyFullPage(false); }} className="creation-form-vertical">
              
              {/* STEP 1: Select Template First */}
              <div className="creation-step">
                <div className="step-header">
                  <span className="step-number">1</span>
                  <div>
                    <h3>🎨 Choose Your Landing Page Template</h3>
                    <p className="step-desc">Select a design for your company's public booking page</p>
                  </div>
                </div>
                
                <div className="template-selection-grid">
                  {landingPageTemplates.map(template => (
                    <div
                      key={template.id}
                      className={`template-select-card ${companyForm.templateId === template.id ? 'selected' : ''}`}
                      onClick={() => setCompanyForm(prev => ({ 
                        ...prev, 
                        templateId: template.id,
                        primaryColor: template.primaryColor,
                        secondaryColor: template.secondaryColor
                      }))}
                    >
                      <div 
                        className="template-thumb" 
                        style={{ background: `linear-gradient(135deg, ${template.primaryColor}, ${template.secondaryColor})` }}
                      >
                        <span className="template-thumb-icon">{template.icon}</span>
                        {companyForm.templateId === template.id && (
                          <div className="template-selected-badge">✅ Selected</div>
                        )}
                      </div>
                      <div className="template-select-info">
                        <h4>{template.name}</h4>
                        <p>{template.description}</p>
                        <button 
                          type="button"
                          className="btn-view-template"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Open full preview in new tab
                            const previewWindow = window.open('', '_blank');
                            const templateData = template;
                            previewWindow.document.write(`
                              <!DOCTYPE html>
                              <html>
                              <head>
                                <title>${templateData.name} - Template Preview</title>
                                <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
                                <style>
                                  * { margin: 0; padding: 0; box-sizing: border-box; }
                                  body { font-family: 'Inter', sans-serif; }
                                  .navbar { display: flex; align-items: center; justify-content: space-between; padding: 20px 60px; background: ${templateData.id === 'taxfile-dark' ? '#111827' : '#fff'}; border-bottom: 1px solid ${templateData.id === 'taxfile-dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}; }
                                  .nav-logo { display: flex; align-items: center; gap: 12px; font-weight: 700; font-size: 20px; color: ${templateData.id === 'taxfile-dark' ? '#fff' : '#1f2937'}; }
                                  .nav-logo-icon { font-size: 28px; }
                                  .nav-links { display: flex; gap: 32px; }
                                  .nav-links a { text-decoration: none; color: ${templateData.id === 'taxfile-dark' ? '#9ca3af' : '#6b7280'}; font-weight: 500; transition: color 0.2s; }
                                  .nav-links a:hover { color: ${templateData.primaryColor}; }
                                  .hero { padding: 100px 60px; text-align: center; color: #fff; background: linear-gradient(135deg, ${templateData.primaryColor}, ${templateData.gradientEnd || templateData.secondaryColor}); position: relative; overflow: hidden; }
                                  .hero::before { content: ''; position: absolute; top: -50%; left: -50%; width: 200%; height: 200%; background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 50%); animation: heroGlow 15s infinite linear; }
                                  @keyframes heroGlow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                                  .hero-content { max-width: 700px; margin: 0 auto; position: relative; z-index: 1; }
                                  .hero-badge { display: inline-block; background: rgba(255,255,255,0.2); backdrop-filter: blur(10px); padding: 10px 24px; border-radius: 30px; font-size: 14px; font-weight: 600; margin-bottom: 24px; border: 1px solid rgba(255,255,255,0.2); }
                                  .hero h1 { font-size: 52px; font-weight: 800; margin-bottom: 20px; line-height: 1.1; }
                                  .hero p { font-size: 20px; opacity: 0.9; margin-bottom: 36px; line-height: 1.6; }
                                  .hero-btn { display: inline-block; padding: 18px 40px; background: #fff; color: ${templateData.primaryColor}; font-size: 16px; font-weight: 700; border-radius: 12px; text-decoration: none; transition: transform 0.2s, box-shadow 0.2s; box-shadow: 0 4px 20px rgba(0,0,0,0.2); }
                                  .hero-btn:hover { transform: translateY(-3px); box-shadow: 0 8px 30px rgba(0,0,0,0.3); }
                                  .features { padding: 80px 60px; background: ${templateData.id === 'taxfile-dark' ? '#0a0a0f' : '#f9fafb'}; }
                                  .section-title { text-align: center; font-size: 36px; font-weight: 700; margin-bottom: 50px; color: ${templateData.id === 'taxfile-dark' ? '#fff' : '#1f2937'}; }
                                  .features-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; max-width: 1200px; margin: 0 auto; }
                                  .feature-card { background: ${templateData.id === 'taxfile-dark' ? 'rgba(31,41,55,0.8)' : '#fff'}; padding: 32px; border-radius: 16px; border: 1px solid ${templateData.id === 'taxfile-dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}; transition: transform 0.3s, box-shadow 0.3s; }
                                  .feature-card:hover { transform: translateY(-5px); box-shadow: 0 20px 40px rgba(0,0,0,0.1); }
                                  .feature-icon { width: 56px; height: 56px; background: ${templateData.primaryColor}15; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 28px; margin-bottom: 20px; }
                                  .feature-card h4 { font-size: 18px; font-weight: 700; color: ${templateData.id === 'taxfile-dark' ? '#fff' : '#1f2937'}; margin-bottom: 10px; }
                                  .feature-card p { font-size: 14px; color: ${templateData.id === 'taxfile-dark' ? '#9ca3af' : '#6b7280'}; line-height: 1.6; }
                                  .services { padding: 80px 60px; background: ${templateData.id === 'taxfile-dark' ? '#111827' : '#fff'}; }
                                  .services-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; max-width: 1200px; margin: 0 auto; }
                                  .service-card { background: ${templateData.id === 'taxfile-dark' ? 'rgba(31,41,55,0.6)' : '#f9fafb'}; padding: 32px 24px; border-radius: 16px; text-align: center; border: 1px solid ${templateData.id === 'taxfile-dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}; transition: transform 0.3s; }
                                  .service-card:hover { transform: translateY(-5px); }
                                  .service-icon { font-size: 40px; display: block; margin-bottom: 16px; }
                                  .service-card h4 { font-size: 16px; font-weight: 600; color: ${templateData.id === 'taxfile-dark' ? '#fff' : '#1f2937'}; margin-bottom: 12px; }
                                  .service-price { font-size: 24px; font-weight: 800; color: ${templateData.primaryColor}; }
                                  .cta-section { padding: 80px 60px; text-align: center; background: linear-gradient(135deg, ${templateData.primaryColor}, ${templateData.gradientEnd || templateData.secondaryColor}); color: #fff; }
                                  .cta-section h2 { font-size: 40px; font-weight: 700; margin-bottom: 16px; }
                                  .cta-section p { font-size: 18px; opacity: 0.9; margin-bottom: 32px; }
                                  .cta-btn { display: inline-block; padding: 18px 40px; background: rgba(255,255,255,0.2); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.3); color: #fff; font-size: 16px; font-weight: 700; border-radius: 12px; text-decoration: none; transition: background 0.2s; }
                                  .cta-btn:hover { background: rgba(255,255,255,0.3); }
                                  .footer { padding: 40px 60px; background: ${templateData.id === 'taxfile-dark' ? '#0a0a0f' : '#f9fafb'}; text-align: center; }
                                  .footer-info { display: flex; justify-content: center; gap: 40px; margin-bottom: 20px; color: ${templateData.id === 'taxfile-dark' ? '#9ca3af' : '#6b7280'}; font-size: 14px; }
                                  .footer-copyright { color: ${templateData.id === 'taxfile-dark' ? '#6b7280' : '#9ca3af'}; font-size: 13px; }
                                  @media (max-width: 1024px) { .features-grid, .services-grid { grid-template-columns: repeat(2, 1fr); } }
                                  @media (max-width: 768px) { .navbar { padding: 16px 24px; } .nav-links { display: none; } .hero { padding: 60px 24px; } .hero h1 { font-size: 32px; } .features, .services { padding: 50px 24px; } .features-grid, .services-grid { grid-template-columns: 1fr; } }
                                </style>
                              </head>
                              <body>
                                <nav class="navbar">
                                  <div class="nav-logo">
                                    <span class="nav-logo-icon">${templateData.icon}</span>
                                    <span>Your Company Name</span>
                                  </div>
                                  <div class="nav-links">
                                    <a href="#">About</a>
                                    <a href="#">Services</a>
                                    <a href="#">Team</a>
                                    <a href="#">Contact</a>
                                  </div>
                                </nav>
                                
                                <section class="hero">
                                  <div class="hero-content">
                                    <span class="hero-badge">✅ ${templateData.name}</span>
                                    <h1>${templateData.heroTitle}</h1>
                                    <p>${templateData.heroSubtitle}</p>
                                    <a href="#" class="hero-btn">${templateData.ctaText}</a>
                                  </div>
                                </section>
                                
                                <section class="features">
                                  <h2 class="section-title">Why Choose Us</h2>
                                  <div class="features-grid">
                                    ${templateData.features.map(f => `
                                      <div class="feature-card">
                                        <div class="feature-icon">${f.icon}</div>
                                        <h4>${f.title}</h4>
                                        <p>${f.description}</p>
                                      </div>
                                    `).join('')}
                                  </div>
                                </section>
                                
                                <section class="services">
                                  <h2 class="section-title">Our Services</h2>
                                  <div class="services-grid">
                                    ${templateData.services.map(s => `
                                      <div class="service-card">
                                        <span class="service-icon">${s.icon || '📋'}</span>
                                        <h4>${s.name}</h4>
                                        <span class="service-price">${s.price}</span>
                                      </div>
                                    `).join('')}
                                  </div>
                                </section>
                                
                                <section class="cta-section">
                                  <h2>Ready to Get Started?</h2>
                                  <p>Book an appointment with our expert team today!</p>
                                  <a href="#" class="cta-btn">Book Now →</a>
                                </section>
                                
                                <footer class="footer">
                                  <div class="footer-info">
                                    <span>📧 contact@company.com</span>
                                    <span>📞 (123) 456-7890</span>
                                    <span>📍 123 Business Ave</span>
                                  </div>
                                  <p class="footer-copyright">(c) 2025 Your Company Name. All rights reserved.</p>
                                </footer>
                              </body>
                              </html>
                            `);
                            previewWindow.document.close();
                          }}
                        >
                          ⭕ View Full Preview
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Template Preview Section */}
              <div className="template-preview-section">
                <div className="preview-section-header">
                  <h3>⭕ Preview: {landingPageTemplates.find(t => t.id === companyForm.templateId)?.name}</h3>
                  <span className="preview-note">This is how your landing page will look</span>
                </div>
                <div className="template-preview-frame">
                  {(() => {
                    const selectedTemplate = landingPageTemplates.find(t => t.id === companyForm.templateId) || landingPageTemplates[0];
                    const previewCompanyName = companyForm.name || 'Your Company Name';
                    const previewDescription = companyForm.description || selectedTemplate.heroSubtitle;
                    
                    return (
                      <div className="preview-content-inline" style={{ background: selectedTemplate.id === 'taxfile-dark' ? '#0a0a0f' : '#fff' }}>
                        {/* Mini Navbar */}
                        <div className="preview-mini-navbar" style={{ background: selectedTemplate.id === 'taxfile-dark' ? '#111827' : '#fff', borderBottom: `1px solid ${selectedTemplate.id === 'taxfile-dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}` }}>
                          <div className="preview-mini-logo">
                            <span>{selectedTemplate.icon}</span>
                            <span style={{ color: selectedTemplate.id === 'taxfile-dark' ? '#fff' : '#1f2937' }}>{previewCompanyName}</span>
                          </div>
                          <div className="preview-mini-links" style={{ color: selectedTemplate.id === 'taxfile-dark' ? '#9ca3af' : '#6b7280' }}>
                            <span>About</span>
                            <span>Services</span>
                            <span>Contact</span>
                          </div>
                        </div>
                        
                        {/* Hero */}
                        <div className="preview-mini-hero" style={{ background: `linear-gradient(135deg, ${selectedTemplate.primaryColor}, ${selectedTemplate.gradientEnd || selectedTemplate.secondaryColor})` }}>
                          <span className="preview-mini-badge">✅ {selectedTemplate.name}</span>
                          <h2>{companyForm.name ? `Welcome to ${companyForm.name}` : selectedTemplate.heroTitle}</h2>
                          <p>{previewDescription}</p>
                          <button className="preview-mini-cta" style={{ background: '#fff', color: selectedTemplate.primaryColor }}>
                            {selectedTemplate.ctaText}
                          </button>
                        </div>
                        
                        {/* Features Row */}
                        <div className="preview-mini-features" style={{ background: selectedTemplate.id === 'taxfile-dark' ? '#0a0a0f' : '#f9fafb' }}>
                          {selectedTemplate.features.slice(0, 4).map((f, i) => (
                            <div key={i} className="preview-mini-feature" style={{ background: selectedTemplate.id === 'taxfile-dark' ? 'rgba(31,41,55,0.8)' : '#fff' }}>
                              <span className="feature-icon-mini" style={{ background: `${selectedTemplate.primaryColor}20` }}>{f.icon}</span>
                              <span style={{ color: selectedTemplate.id === 'taxfile-dark' ? '#fff' : '#1f2937' }}>{f.title}</span>
                            </div>
                          ))}
                        </div>
                        
                        {/* CTA Footer */}
                        <div className="preview-mini-cta-section" style={{ background: `linear-gradient(135deg, ${selectedTemplate.primaryColor}, ${selectedTemplate.gradientEnd || selectedTemplate.secondaryColor})` }}>
                          <span>Ready to Get Started? Book Now →</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              
              {/* STEP 2: Company Information */}
              <div className="creation-step">
                <div className="step-header">
                  <span className="step-number">2</span>
                  <div>
                    <h3>📋 Company Information</h3>
                    <p className="step-desc">Enter your company details</p>
                  </div>
                </div>
                
                <div className="step-form-content">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Company Name *</label>
                      <input
                        type="text"
                        value={companyForm.name}
                        onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})}
                        placeholder="Enter company name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>URL Slug *</label>
                      <input
                        type="text"
                        value={companyForm.slug}
                        onChange={(e) => setCompanyForm({...companyForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                        placeholder="company-url-slug"
                        required
                      />
                      <small className="field-hint">
                        🔍— {window.location.origin}/company/{companyForm.slug || 'your-slug'}
                      </small>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Contact Email</label>
                      <input
                        type="email"
                        value={companyForm.email}
                        onChange={(e) => setCompanyForm({...companyForm, email: e.target.value})}
                        placeholder="contact@company.com"
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={companyForm.phone}
                        onChange={(e) => setCompanyForm({...companyForm, phone: e.target.value})}
                        placeholder="(123) 456-7890"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      value={companyForm.address}
                      onChange={(e) => setCompanyForm({...companyForm, address: e.target.value})}
                      placeholder="Company address"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={companyForm.description}
                      onChange={(e) => setCompanyForm({...companyForm, description: e.target.value})}
                      placeholder="Brief description of your company (shown on landing page)"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
              
              {/* STEP 3: Admin Credentials */}
              <div className="creation-step">
                <div className="step-header">
                  <span className="step-number">3</span>
                  <div>
                    <h3>🔍 Admin Credentials</h3>
                    <p className="step-desc">Set up login for company administrator</p>
                  </div>
                </div>
                
                <div className="step-form-content">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Admin Username *</label>
                      <input
                        type="text"
                        value={companyForm.adminUsername}
                        onChange={(e) => setCompanyForm({...companyForm, adminUsername: e.target.value})}
                        placeholder="Admin login username"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Admin Password *</label>
                      <input
                        type="password"
                        value={companyForm.adminPassword}
                        onChange={(e) => setCompanyForm({...companyForm, adminPassword: e.target.value})}
                        placeholder="Admin login password"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="creation-actions">
                <button type="button" className="btn-cancel-creation" onClick={() => setShowAddCompanyFullPage(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-create-company">
                  * Create Company with Selected Template
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : showOwnerDashboard && isOwner ? (
        /* Use Modern OwnerDashboard Component */
        <OwnerDashboard 
          onLogout={handleLogout}
          firebaseUser={firebaseUser}
          onLoginAsAdmin={(company) => {
            // Owner logging in as company admin
            if (!company.active) {
              alert('This company is inactive. Activate it first.');
              return;
            }
            setCurrentCompany(company);
            setIsAdmin(true);
            setIsLoggedIn(true);
            setIsPreparerView(false);
            setShowLandingPage(false);
            setIsClientView(false);
            setShowOwnerDashboard(false);
            localStorage.setItem('currentCompany', JSON.stringify(company));
            localStorage.setItem('isAdmin', 'true');
            localStorage.setItem('isLoggedIn', 'true');
            localStorage.setItem('showOwnerDashboard', 'false');
          }}
          onLoginAsPreparer={(preparer, company) => {
            // Owner logging in as preparer
            if (!preparer.active) {
              alert('This preparer account is inactive.');
              return;
            }
            setLoggedInPreparer(preparer);
            setCurrentCompany(company);
            setIsPreparerView(true);
            setIsLoggedIn(true);
            setIsAdmin(preparer.isAdmin || false);
            setShowLandingPage(false);
            setIsClientView(false);
            setShowOwnerDashboard(false);
            localStorage.setItem('loggedInPreparer', JSON.stringify(preparer));
            localStorage.setItem('currentCompany', JSON.stringify(company));
            localStorage.setItem('isPreparerView', 'true');
            localStorage.setItem('isLoggedIn', 'true');
            localStorage.setItem('showOwnerDashboard', 'false');
          }}
        />
      ) : null}
        
        {/* Use Modern CompanyAdminDashboard Component */}
        {!isClientView && isAdmin && !isPreparerView && currentCompany && (
          <CompanyAdminDashboard 
            company={currentCompany}
            onLogout={handleLogout}
            onUpdateCompany={(updates) => updateCompany(currentCompany.id, updates)}
            onSwitchToPreparer={(preparer) => {
              // Switch to preparer view
              setLoggedInPreparer(preparer);
              localStorage.setItem('loggedInPreparer', JSON.stringify(preparer));
              setIsPreparerView(true);
              setIsAdmin(preparer.isAdmin || false);
              // Company is already set
            }}
          />
        )}

        {/* Preparer Manager Section - Only show to admin on dashboard tab */}
        {!isClientView && !isPreparerView && showPreparerManager && adminActiveTab === 'dashboard' && !currentCompany && (
          <div className="preparer-manager">
            <h3>[U] Manage Preparers</h3>
            <div className="preparer-input-section">
              <button onClick={() => setShowAddPreparerForm(true)} className="btn btn-success btn-large">
                + Add New Preparer
              </button>
            </div>
            
            {preparerCredentials.length > 0 ? (
              <div className="preparers-list">
                <p className="list-title">Current Preparers ({preparerCredentials.length}):</p>
                <div className="preparers-cards-grid">
                  {preparerCredentials.map((preparer) => (
                    <div key={preparer.name} className={`preparer-admin-card ${!preparer.active ? 'inactive' : ''}`}>
                      <div className="admin-card-photo">
                        {preparer.photo ? (
                          <img src={preparer.photo} alt={preparer.name} className="admin-preparer-photo" />
                        ) : (
                          <div className="admin-photo-placeholder">
                            <span>[U]</span>
                          </div>
                        )}
                        <div className={`status-badge ${preparer.active ? 'active' : 'inactive'}`}>
                          {preparer.active ? '🟢 Active' : '🔍´ Inactive'}
                        </div>
                      </div>
                      <div className="admin-card-content">
                        <h4 className="admin-card-name">
                          <span className="admin-card-name-text">{preparer.name}</span>
                          {preparer.isAdmin && <span className="admin-badge">👑 ADMIN</span>}
                        </h4>
                        <div className="admin-card-detail">
                          <span>[U] Username: {preparer.username}</span>
                        </div>
                        <div className="admin-card-detail password-field">
                          <span>🔍‘ Password: </span>
                          <span className="password-text">
                            {showPasswordFor[preparer.name] ? preparer.password : '••••••••'}
                          </span>
                          <button 
                            type="button"
                            className="eye-icon-btn"
                            onClick={() => setShowPasswordFor({...showPasswordFor, [preparer.name]: !showPasswordFor[preparer.name]})}
                            title={showPasswordFor[preparer.name] ? 'Hide password' : 'Show password'}
                          >
                            {showPasswordFor[preparer.name] ? '⭕' : '⭕'}
                          </button>
                        </div>
                        {preparer.email && <p className="admin-card-detail">📧 Email: {preparer.email}</p>}
                        {preparer.phone && <p className="admin-card-detail">📞 Phone: {preparer.phone}</p>}
                        {preparer.address && <p className="admin-card-detail">📍 Address: {preparer.address}</p>}
                        {preparer.bio && (
                          <div className="admin-card-bio">
                            <strong>Bio:</strong>
                            <p>{preparer.bio}</p>
                          </div>
                        )}
                      </div>
                      <div className="admin-card-actions">
                        <button 
                          onClick={() => handleViewPreparer(preparer)} 
                          className="btn-action btn-view"
                          title="View Details"
                        >
                          ⭕ View
                        </button>
                        <button 
                          onClick={() => handleEditPreparer(preparer)} 
                          className="btn-action btn-edit"
                          title="Edit Preparer"
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          onClick={() => handleToggleAdmin(preparer)} 
                          className={`btn-action ${preparer.isAdmin ? 'btn-warning' : 'btn-info'}`}
                          title={preparer.isAdmin ? 'Remove Admin Access' : 'Grant Admin Access'}
                        >
                          {preparer.isAdmin ? '🔍“ Remove Admin' : '🔍 Make Admin'}
                        </button>
                        <button 
                          onClick={() => handleToggleActive(preparer.name)} 
                          className={`btn-action ${preparer.active ? 'btn-deactivate' : 'btn-activate'}`}
                          title={preparer.active ? 'Deactivate' : 'Activate'}
                        >
                          {preparer.active ? '|| Deactivate' : '[>] Activate'}
                        </button>
                        <button 
                          onClick={() => handleDeletePreparer(preparer.name)} 
                          className="btn-action btn-delete"
                          title="Delete Preparer"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="no-preparers">No preparers added yet. Add preparers to assign them to appointments.</p>
            )}
          </div>
        )}

        {/* Preparer Login Form */}
        {showLoginForm && !isPreparerView && (
          <div className="modal-overlay" onClick={() => setShowLoginForm(false)}>
            <div className="modal-content login-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>🔍 Preparer Login</h2>
                <button onClick={() => setShowLoginForm(false)} className="close-btn">&times;</button>
              </div>
              <form onSubmit={handleLogin} className="login-form">
                <div className="form-group">
                  <label htmlFor="username">Username:</label>
                  <input
                    type="text"
                    id="username"
                    value={loginData.username}
                    onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                    placeholder="Enter username"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password:</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPasswordFor['modalLogin'] ? "text" : "password"}
                      id="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      placeholder="Enter password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPasswordFor({...showPasswordFor, modalLogin: !showPasswordFor['modalLogin']})}
                      aria-label="Toggle password visibility"
                    >
                      {showPasswordFor['modalLogin'] ? '⭕' : '⭕'}
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-large">Login</button>
              </form>
            </div>
          </div>
        )}

        {/* Add Preparer Form */}
        {showAddPreparerForm && (
          <div className="modal-overlay" onClick={() => setShowAddPreparerForm(false)}>
            <div className="modal-content modal-scrollable" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>+ Add New Preparer</h2>
                <button onClick={() => setShowAddPreparerForm(false)} className="close-btn">&times;</button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => { e.preventDefault(); handleAddPreparer(); }} className="preparer-form">
                  <div className="form-group">
                    <label htmlFor="preparerName">Full Name: *</label>
                    <input
                      type="text"
                      id="preparerName"
                      value={newPreparerForm.name}
                      onChange={(e) => setNewPreparerForm({...newPreparerForm, name: e.target.value})}
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="preparerUsername">Username: *</label>
                    <input
                      type="text"
                      id="preparerUsername"
                      value={newPreparerForm.username}
                      onChange={(e) => setNewPreparerForm({...newPreparerForm, username: e.target.value})}
                      placeholder="Enter username"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="preparerPassword">Password: *</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPasswordFor['newPreparer'] ? "text" : "password"}
                        id="preparerPassword"
                        value={newPreparerForm.password}
                        onChange={(e) => setNewPreparerForm({...newPreparerForm, password: e.target.value})}
                        placeholder="Enter password"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPasswordFor({...showPasswordFor, newPreparer: !showPasswordFor['newPreparer']})}
                        aria-label="Toggle password visibility"
                      >
                        {showPasswordFor['newPreparer'] ? '⭕' : '⭕'}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="preparerEmail">Email:</label>
                    <input
                      type="email"
                      id="preparerEmail"
                      value={newPreparerForm.email}
                      onChange={(e) => setNewPreparerForm({...newPreparerForm, email: e.target.value})}
                      placeholder="Enter email (optional)"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="preparerPhone">Phone Number:</label>
                    <input
                      type="tel"
                      id="preparerPhone"
                      value={newPreparerForm.phone}
                      onChange={(e) => setNewPreparerForm({...newPreparerForm, phone: e.target.value})}
                      placeholder="Enter phone number (optional)"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="preparerAddress">Office Address:</label>
                    <textarea
                      id="preparerAddress"
                      value={newPreparerForm.address}
                      onChange={(e) => setNewPreparerForm({...newPreparerForm, address: e.target.value})}
                      placeholder="Enter office address (optional)"
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="preparerBio">Professional Bio:</label>
                    <textarea
                      id="preparerBio"
                      value={newPreparerForm.bio}
                      onChange={(e) => setNewPreparerForm({...newPreparerForm, bio: e.target.value})}
                      placeholder="Describe experience and expertise (optional)"
                      rows="4"
                    />
                  </div>
                  <div className="form-group photo-form-group">
                    <label>Profile Photo:</label>
                    {showCropper && cropImage && cropTarget === 'newPreparer' ? (
                      <div className="inline-cropper-container">
                        <div className="inline-cropper-header">
                          <span>📷 Adjust Your Photo</span>
                        </div>
                        <div className="inline-cropper-area">
                          <Cropper
                            image={cropImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropShape="round"
                            showGrid={false}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                          />
                        </div>
                        <div className="inline-cropper-controls">
                          <div className="zoom-control-inline">
                            <label>🔍 Zoom:</label>
                            <input
                              type="range"
                              min={1}
                              max={3}
                              step={0.1}
                              value={zoom}
                              onChange={(e) => setZoom(Number(e.target.value))}
                              className="zoom-slider"
                            />
                            <span className="zoom-value">{zoom.toFixed(1)}x</span>
                          </div>
                          <p className="crop-hint">Drag to position • Scroll to zoom</p>
                        </div>
                        <div className="inline-cropper-actions">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={handleCropCancel}>
                            ✕ Cancel
                          </button>
                          <button type="button" className="btn btn-primary btn-sm" onClick={handleCropConfirm}>
                            ✅ Apply
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="photo-upload-card">
                        <div className="photo-upload-left">
                          <label htmlFor="preparerPhoto" className="photo-preview-circle">
                            {newPreparerForm.photo ? (
                              <img src={newPreparerForm.photo} alt="Preview" className="preview-image" />
                            ) : (
                              <div className="photo-placeholder">
                                <span className="placeholder-icon">[U]</span>
                              </div>
                            )}
                            <div className="photo-overlay">
                              <span>📷</span>
                            </div>
                          </label>
                        </div>
                        <div className="photo-upload-right">
                          <p className="photo-upload-title">Upload Profile Picture</p>
                          <p className="photo-upload-subtitle">JPG, PNG or GIF (Max 2MB)</p>
                          <div className="photo-upload-actions">
                            <label htmlFor="preparerPhoto" className="photo-upload-btn">
                              <span>📷</span> Choose Photo
                            </label>
                            {newPreparerForm.photo && (
                              <button 
                                type="button" 
                                onClick={() => setNewPreparerForm({...newPreparerForm, photo: ''})}
                                className="btn-remove-photo"
                              >
                                ✕ Remove
                              </button>
                            )}
                          </div>
                        </div>
                        <input
                          type="file"
                          id="preparerPhoto"
                          accept="image/*"
                          onChange={(e) => handlePhotoUpload(e, 'newPreparer')}
                          className="photo-input"
                        />
                      </div>
                    )}
                  </div>
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newPreparerForm.isAdmin}
                        onChange={(e) => setNewPreparerForm({...newPreparerForm, isAdmin: e.target.checked})}
                      />
                      <span className="checkbox-text">🔍‘ Grant Admin Access</span>
                    </label>
                    <small className="checkbox-hint">Admin can manage all preparers and appointments</small>
                  </div>
                  <div className="modal-footer">
                    <button type="submit" className="btn btn-primary btn-large">Create Preparer Account</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Preparer Modal - Full Page Style */}
        {showViewPreparerModal && viewingPreparer && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            zIndex: 9999,
            overflowY: 'auto'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #0ea5e9 100%)',
              color: 'white',
              padding: 'clamp(15px, 3vw, 25px) clamp(15px, 4vw, 40px)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px',
              boxShadow: '0 4px 20px rgba(30, 58, 138, 0.3)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}>⭕</div>
                <div>
                  <h1 style={{ margin: 0, fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)', fontWeight: '700' }}>
                    Preparer Details
                  </h1>
                  <p style={{ margin: '3px 0 0', opacity: 0.85, fontSize: 'clamp(0.8rem, 1.5vw, 0.95rem)' }}>
                    Viewing information for {viewingPreparer.name}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => { setShowViewPreparerModal(false); setViewingPreparer(null); }}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                ← Back
              </button>
            </div>

            {/* Content */}
            <div style={{ maxWidth: '900px', margin: '0 auto', padding: 'clamp(20px, 4vw, 40px)' }}>
              {/* Profile Card */}
              <div style={{
                background: 'white',
                borderRadius: '20px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                marginBottom: '25px'
              }}>
                {/* Profile Header */}
                <div style={{
                  background: `linear-gradient(135deg, #8b5cf6, #6d28d9)`,
                  padding: 'clamp(25px, 4vw, 40px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: 'clamp(100px, 20vw, 140px)',
                    height: 'clamp(100px, 20vw, 140px)',
                    borderRadius: '50%',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    border: '5px solid rgba(255,255,255,0.5)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                  }}>
                    {viewingPreparer.photo ? (
                      <img src={viewingPreparer.photo} alt={viewingPreparer.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: 'clamp(3rem, 8vw, 4rem)' }}>[U]</span>
                    )}
                  </div>
                  <h2 style={{ color: 'white', margin: '15px 0 5px', fontSize: 'clamp(1.3rem, 3vw, 1.8rem)', fontWeight: '700' }}>
                    {viewingPreparer.name}
                  </h2>
                  <p style={{ color: 'rgba(255,255,255,0.85)', margin: 0, fontSize: '1rem' }}>
                    @{viewingPreparer.username}
                  </p>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <span style={{
                      background: viewingPreparer.active !== false ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                      color: 'white',
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>
                      {viewingPreparer.active !== false ? '✅ Active' : '❌ Inactive'}
                    </span>
                    {viewingPreparer.isAdmin && (
                      <span style={{
                        background: 'rgba(251, 191, 36, 0.3)',
                        color: 'white',
                        padding: '6px 16px',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}>
                        👑 Admin
                      </span>
                    )}
                  </div>
                </div>

                {/* Profile Details */}
                <div style={{ padding: 'clamp(20px, 4vw, 35px)' }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))', 
                    gap: '20px' 
                  }}>
                    {/* Password */}
                    <div style={{
                      background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                      padding: '18px',
                      borderRadius: '14px',
                      border: '1px solid #fcd34d'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '1.3rem' }}>🔍</span>
                        <span style={{ fontWeight: '600', color: '#92400e', fontSize: '0.9rem' }}>Password</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <code style={{
                          background: 'white',
                          padding: '8px 14px',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          fontFamily: 'monospace',
                          flex: 1,
                          border: '1px solid #e5e7eb'
                        }}>
                          {showPasswordFor['viewPreparer'] ? (viewingPreparer.password || 'N/A') : '••••••••'}
                        </code>
                        <button
                          onClick={() => setShowPasswordFor(prev => ({ ...prev, viewPreparer: !prev.viewPreparer }))}
                          style={{
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '10px 14px',
                            cursor: 'pointer',
                            fontSize: '1.2rem'
                          }}
                          title={showPasswordFor['viewPreparer'] ? 'Hide' : 'Show'}
                        >
                          {showPasswordFor['viewPreparer'] ? '⭕' : '⭕'}
                        </button>
                      </div>
                    </div>

                    {/* Email */}
                    <div style={{
                      background: '#f0f9ff',
                      padding: '18px',
                      borderRadius: '14px',
                      border: '1px solid #bae6fd'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '1.3rem' }}>📧</span>
                        <span style={{ fontWeight: '600', color: '#0369a1', fontSize: '0.9rem' }}>Email</span>
                      </div>
                      <p style={{ margin: 0, fontSize: '1rem', color: '#1f2937', wordBreak: 'break-all' }}>
                        {viewingPreparer.email || 'Not provided'}
                      </p>
                    </div>

                    {/* Phone */}
                    <div style={{
                      background: '#f0fdf4',
                      padding: '18px',
                      borderRadius: '14px',
                      border: '1px solid #bbf7d0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '1.3rem' }}>📞</span>
                        <span style={{ fontWeight: '600', color: '#166534', fontSize: '0.9rem' }}>Phone</span>
                      </div>
                      <p style={{ margin: 0, fontSize: '1rem', color: '#1f2937' }}>
                        {viewingPreparer.phone || 'Not provided'}
                      </p>
                    </div>

                    {/* Address */}
                    <div style={{
                      background: '#fdf4ff',
                      padding: '18px',
                      borderRadius: '14px',
                      border: '1px solid #f0abfc',
                      gridColumn: 'span 1'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '1.3rem' }}>📍</span>
                        <span style={{ fontWeight: '600', color: '#86198f', fontSize: '0.9rem' }}>Address</span>
                      </div>
                      <p style={{ margin: 0, fontSize: '1rem', color: '#1f2937' }}>
                        {viewingPreparer.address || 'Not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Bio Section */}
                  {viewingPreparer.bio && (
                    <div style={{
                      background: '#f8fafc',
                      padding: '20px',
                      borderRadius: '14px',
                      marginTop: '20px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                        <span style={{ fontSize: '1.3rem' }}>📝</span>
                        <span style={{ fontWeight: '600', color: '#475569', fontSize: '0.95rem' }}>Professional Bio</span>
                      </div>
                      <p style={{ margin: 0, fontSize: '1rem', color: '#334155', lineHeight: 1.7 }}>
                        {viewingPreparer.bio}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '15px',
                flexWrap: 'wrap'
              }}>
                <button 
                  onClick={() => { setShowViewPreparerModal(false); setViewingPreparer(null); }}
                  style={{
                    padding: '14px 30px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}
                >
                  ✕ Close
                </button>
                <button 
                  onClick={() => { 
                    setShowViewPreparerModal(false); 
                    setEditingPreparer(viewingPreparer);
                    setNewPreparerForm({
                      name: viewingPreparer.name || '',
                      username: viewingPreparer.username || '',
                      password: viewingPreparer.password || '',
                      email: viewingPreparer.email || '',
                      phone: viewingPreparer.phone || '',
                      address: viewingPreparer.address || '',
                      photo: viewingPreparer.photo || '',
                      bio: viewingPreparer.bio || '',
                      isAdmin: viewingPreparer.isAdmin || false
                    });
                    setViewingPreparer(null);
                    setShowEditPreparerForm(true);
                  }}
                  style={{
                    padding: '14px 30px',
                    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '1rem',
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                  }}
                >
                  ✏️ Edit Preparer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Preparer Form - Full Page Style */}
        {showEditPreparerForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            zIndex: 9999,
            overflowY: 'auto'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #4c1d95 0%, #5b21b6 50%, #6d28d9 100%)',
              color: 'white',
              padding: 'clamp(18px, 3vw, 28px) clamp(15px, 4vw, 40px)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px',
              boxShadow: '0 4px 20px rgba(76, 29, 149, 0.5)',
              position: 'sticky',
              top: 0,
              zIndex: 100
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{
                  width: '55px',
                  height: '55px',
                  background: 'white',
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}>✏️</div>
                <div>
                  <h1 style={{ margin: 0, fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', fontWeight: '800', color: '#ffffff', letterSpacing: '0.5px' }}>
                    Edit Preparer
                  </h1>
                  <p style={{ margin: '6px 0 0', fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)', color: '#e9d5ff', fontWeight: '600' }}>
                    {editingPreparer ? `Editing: ${editingPreparer.name}` : 'Update preparer information'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => { setShowEditPreparerForm(false); setEditingPreparer(null); }}
                style={{
                  background: 'white',
                  color: '#5b21b6',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              >
                ← Back
              </button>
            </div>

            {/* Form Content */}
            <div style={{ maxWidth: '900px', margin: '0 auto', padding: 'clamp(20px, 4vw, 40px)' }}>
              <form onSubmit={(e) => { e.preventDefault(); handleUpdatePreparer(); }}>
                {/* Two Column Layout */}
                <div 
                  className="edit-preparer-form-grid"
                  style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap',
                    gap: '25px',
                    marginBottom: '25px'
                  }}
                >
                  {/* Left Column - Basic Info */}
                  <div style={{
                    flex: '1 1 350px',
                    minWidth: '300px',
                    background: 'white',
                    borderRadius: '16px',
                    padding: 'clamp(20px, 3vw, 30px)',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
                      <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>[U]</div>
                      <h3 style={{ margin: 0, color: '#1f2937', fontSize: 'clamp(1rem, 2vw, 1.15rem)' }}>Basic Information</h3>
                    </div>
                    
                    <div style={{ marginBottom: '18px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                        <span style={{ color: '#ef4444' }}>*</span> Full Name
                      </label>
                      <input
                        type="text"
                        value={newPreparerForm.name}
                        onChange={(e) => setNewPreparerForm({...newPreparerForm, name: e.target.value})}
                        placeholder="Enter full name"
                        required
                        style={{ 
                          width: '100%', 
                          padding: '12px 16px', 
                          borderRadius: '10px', 
                          border: '2px solid #e5e7eb', 
                          fontSize: '1rem', 
                          boxSizing: 'border-box',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>

                    <div style={{ marginBottom: '18px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                        <span style={{ color: '#ef4444' }}>*</span> Username
                      </label>
                      <input
                        type="text"
                        value={newPreparerForm.username}
                        onChange={(e) => setNewPreparerForm({...newPreparerForm, username: e.target.value})}
                        placeholder="Enter username"
                        required
                        style={{ 
                          width: '100%', 
                          padding: '12px 16px', 
                          borderRadius: '10px', 
                          border: '2px solid #e5e7eb', 
                          fontSize: '1rem', 
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>

                    <div style={{ marginBottom: '18px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                        <span style={{ color: '#ef4444' }}>*</span> Password
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showPasswordFor['editPreparer'] ? "text" : "password"}
                          value={newPreparerForm.password}
                          onChange={(e) => setNewPreparerForm({...newPreparerForm, password: e.target.value})}
                          placeholder="Enter password"
                          required
                          style={{ 
                            width: '100%', 
                            padding: '12px 16px', 
                            paddingRight: '55px',
                            borderRadius: '10px', 
                            border: '2px solid #e5e7eb', 
                            fontSize: '1rem', 
                            boxSizing: 'border-box'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswordFor({...showPasswordFor, editPreparer: !showPasswordFor['editPreparer']})}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '1.1rem'
                          }}
                        >
                          {showPasswordFor['editPreparer'] ? '⭕' : '⭕'}
                        </button>
                      </div>
                    </div>

                    <div style={{ marginBottom: '18px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>📧 Email</label>
                      <input
                        type="email"
                        value={newPreparerForm.email}
                        onChange={(e) => setNewPreparerForm({...newPreparerForm, email: e.target.value})}
                        placeholder="Enter email (optional)"
                        style={{ 
                          width: '100%', 
                          padding: '12px 16px', 
                          borderRadius: '10px', 
                          border: '2px solid #e5e7eb', 
                          fontSize: '1rem', 
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>📞 Phone</label>
                      <input
                        type="tel"
                        value={newPreparerForm.phone}
                        onChange={(e) => setNewPreparerForm({...newPreparerForm, phone: e.target.value})}
                        placeholder="Enter phone number (optional)"
                        style={{ 
                          width: '100%', 
                          padding: '12px 16px', 
                          borderRadius: '10px', 
                          border: '2px solid #e5e7eb', 
                          fontSize: '1rem', 
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>
                  </div>

                  {/* Right Column - Additional Info & Photo */}
                  <div style={{
                    flex: '1 1 350px',
                    minWidth: '300px',
                    background: 'white',
                    borderRadius: '16px',
                    padding: 'clamp(20px, 3vw, 30px)',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
                      <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>📝</div>
                      <h3 style={{ margin: 0, color: '#1f2937', fontSize: 'clamp(1rem, 2vw, 1.15rem)' }}>Additional Details</h3>
                    </div>

                    <div style={{ marginBottom: '18px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>📍 Address</label>
                      <textarea
                        value={newPreparerForm.address}
                        onChange={(e) => setNewPreparerForm({...newPreparerForm, address: e.target.value})}
                        placeholder="Enter office address (optional)"
                        rows="2"
                        style={{ 
                          width: '100%', 
                          padding: '12px 16px', 
                          borderRadius: '10px', 
                          border: '2px solid #e5e7eb', 
                          fontSize: '1rem', 
                          boxSizing: 'border-box',
                          resize: 'vertical'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>

                    <div style={{ marginBottom: '18px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>📝 Professional Bio</label>
                      <textarea
                        value={newPreparerForm.bio}
                        onChange={(e) => setNewPreparerForm({...newPreparerForm, bio: e.target.value})}
                        placeholder="Describe experience and expertise (optional)"
                        rows="3"
                        style={{ 
                          width: '100%', 
                          padding: '12px 16px', 
                          borderRadius: '10px', 
                          border: '2px solid #e5e7eb', 
                          fontSize: '1rem', 
                          boxSizing: 'border-box',
                          resize: 'vertical'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>

                    {/* Photo Upload */}
                    <div style={{ marginBottom: '18px' }}>
                      <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>📷 Profile Photo</label>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px',
                        padding: '15px',
                        background: '#f8fafc',
                        borderRadius: '12px',
                        border: '2px dashed #d1d5db'
                      }}>
                        <div style={{
                          width: '70px',
                          height: '70px',
                          borderRadius: '50%',
                          background: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          border: '3px solid #e5e7eb',
                          flexShrink: 0
                        }}>
                          {newPreparerForm.photo ? (
                            <img src={newPreparerForm.photo} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <span style={{ fontSize: '2rem' }}>[U]</span>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 8px', fontWeight: '500', color: '#374151', fontSize: '0.9rem' }}>Upload a profile picture</p>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <label style={{
                              background: '#8b5cf6',
                              color: 'white',
                              padding: '8px 16px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: '600'
                            }}>
                              📷 Choose
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handlePhotoUpload(e, 'editPreparer')}
                                style={{ display: 'none' }}
                              />
                            </label>
                            {newPreparerForm.photo && (
                              <button 
                                type="button" 
                                onClick={() => setNewPreparerForm({...newPreparerForm, photo: ''})}
                                style={{
                                  background: '#fee2e2',
                                  color: '#dc2626',
                                  padding: '8px 16px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: '600'
                                }}
                              >
                                ✕ Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Admin Access Toggle */}
                    <div style={{
                      background: newPreparerForm.isAdmin ? 'linear-gradient(135deg, #fef3c7, #fde68a)' : '#f8fafc',
                      padding: '15px 18px',
                      borderRadius: '12px',
                      border: newPreparerForm.isAdmin ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                      cursor: 'pointer'
                    }}
                    onClick={() => setNewPreparerForm({...newPreparerForm, isAdmin: !newPreparerForm.isAdmin})}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '1.5rem' }}>👑</span>
                          <div>
                            <p style={{ margin: 0, fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>Admin Access</p>
                            <p style={{ margin: '3px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>Can manage all preparers and appointments</p>
                          </div>
                        </div>
                        <div style={{
                          width: '50px',
                          height: '28px',
                          background: newPreparerForm.isAdmin ? '#10b981' : '#d1d5db',
                          borderRadius: '20px',
                          position: 'relative',
                          transition: 'background 0.2s'
                        }}>
                          <div style={{
                            width: '22px',
                            height: '22px',
                            background: 'white',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '3px',
                            left: newPreparerForm.isAdmin ? '25px' : '3px',
                            transition: 'left 0.2s',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
              
              {/* Action Buttons - Outside flex, at bottom */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                padding: '16px 20px',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                marginTop: '25px'
              }}>
                <button 
                  type="button"
                  onClick={() => { setShowEditPreparerForm(false); setEditingPreparer(null); }}
                  style={{
                    padding: '12px 28px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.95rem'
                  }}
                >
                  ✕ Cancel
                </button>
                <button 
                  type="button"
                  onClick={(e) => { e.preventDefault(); handleUpdatePreparer(); }}
                  style={{
                    padding: '12px 28px',
                    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
                  }}
                >
                  ✅ Update Preparer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manual Booking Form for Preparer - Inline View (not modal) */}
        {activeTab === 'new-booking' && isPreparerView && (
          <div className="preparer-new-booking-section" style={{
            background: 'white',
            borderRadius: '16px',
            padding: '0',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden'
          }}>
            <div className="mbm-header" style={{ borderRadius: '16px 16px 0 0' }}>
              <div className="mbm-header-content">
                <div className="mbm-header-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <div>
                  <h2>Book Appointment</h2>
                  <p>Schedule an appointment for a client</p>
                </div>
              </div>
            </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                
                // Validate email
                if (!validateEmail(formData.email)) {
                  alert('Please enter a valid email address (e.g., name@example.com)');
                  return;
                }
                
                // Validate phone
                if (!validatePhoneNumber(formData.phone)) {
                  alert('Please enter a valid 10-digit phone number (e.g., 717-343-3822)');
                  return;
                }
                
                try {
                  // Save to Firestore
                  const appointmentData = {
                    fullName: formData.name,
                    email: formData.email,
                    phoneNumber: formData.phone,
                    date: formData.date,
                    time: formData.time,
                    preparer: loggedInPreparer?.name,
                    companyId: currentCompany?.id || loggedInPreparer?.companyId || null,
                    confirmed: true,
                    bookedByPreparer: true
                  };
                  // eslint-disable-next-line no-unused-vars
                  const savedAppointment = await addAppointment(appointmentData);
                
                // Format date and time for display
                const appointmentDate = new Date(formData.date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                });
                const [hours, minutes] = formData.time.split(':');
                const hour = parseInt(hours);
                const appointmentTime = `${hour > 12 ? hour - 12 : hour}:${minutes} ${hour >= 12 ? 'PM' : 'AM'}`;
                
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  date: '',
                  time: '',
                  preparer: ''
                });
                setActiveTab('appointments');
                
                alert(`✅ Appointment Successfully Booked!\n\n[U] Client: ${formData.name}\n[key] Date: ${appointmentDate}\n⏰ Time: ${appointmentTime}\n[key] Email: ${formData.email}\n[key] Phone: ${formData.phone}\n\nThe appointment has been added to your schedule.`);
                } catch (error) {
                  console.error('Error saving appointment:', error);
                  alert('❌ Error saving appointment. Please try again.');
                }
              }} className="mbm-form">
                
                {/* Step 1: Client Information */}
                <div className="mbm-section">
                  <div className="mbm-section-header">
                    <div className={`mbm-step-number ${formData.name && formData.email && formData.phone ? 'completed' : ''}`}>
                      {formData.name && formData.email && formData.phone ? '✅' : '1'}
                    </div>
                    <h3>Client Information</h3>
                  </div>
                  <div className="mbm-section-content">
                    <div className="mbm-form-grid">
                      <div className="mbm-field full-width">
                        <label>Full Name <span className="required">*</span></label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Enter client's full name"
                          required
                        />
                      </div>
                      <div className="mbm-field">
                        <label>Email Address <span className="required">*</span></label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="client@email.com"
                          required
                        />
                      </div>
                      <div className="mbm-field">
                        <label>Phone Number <span className="required">*</span></label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: formatPhoneNumber(e.target.value)})}
                          placeholder="717-343-3822"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 2: Select Date & Time */}
                <div className="mbm-section">
                  <div className="mbm-section-header">
                    <div className={`mbm-step-number ${formData.date && formData.time ? 'completed' : ''}`}>
                      {formData.date && formData.time ? '✅' : '2'}
                    </div>
                    <h3>Select Date & Time</h3>
                  </div>
                  <div className="mbm-section-content">
                    <div className="mbm-form-grid">
                      <div className="mbm-field">
                        <label>Appointment Date <span className="required">*</span></label>
                        <input
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({...formData, date: e.target.value})}
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>
                      <div className="mbm-field">
                        <label>Appointment Time <span className="required">*</span></label>
                        <input
                          type="time"
                          value={formData.time}
                          onChange={(e) => setFormData({...formData, time: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                {formData.date && formData.time && formData.name && (
                  <div className="mbm-summary">
                    <div className="mbm-summary-info">
                      <div className="mbm-summary-icon">📋</div>
                      <div className="mbm-summary-details">
                        <strong>Appointment Summary</strong>
                        <p>
                          {formData.name} • {formData.date ? new Date(formData.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''} • {(() => {
                            if (!formData.time) return '';
                            const [h, m] = formData.time.split(':');
                            const hour = parseInt(h);
                            return `${hour > 12 ? hour - 12 : hour === 0 ? 12 : hour}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mbm-buttons">
                  <button type="button" onClick={() => setActiveTab('appointments')} className="mbm-btn-cancel">
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="mbm-btn-submit"
                    disabled={!formData.date || !formData.time || !formData.name || !formData.email || !formData.phone}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Confirm Booking
                  </button>
                </div>
              </form>
          </div>
        )}

        {/* Admin Manual Booking Form - Modern Stepped Design */}
        {showAdminManualBooking && isAdmin && !isPreparerView && (
          <div className="modal-overlay" onClick={() => setShowAdminManualBooking(false)}>
            <div className="modern-booking-modal" onClick={(e) => e.stopPropagation()}>
              <div className="mbm-header">
                <div className="mbm-header-content">
                  <div className="mbm-header-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </div>
                  <div>
                    <h2>Book Appointment</h2>
                    <p>Schedule an appointment for a client</p>
                  </div>
                </div>
                <button onClick={() => setShowAdminManualBooking(false)} className="mbm-close">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                
                if (!validateEmail(adminBookingForm.email)) {
                  alert('Please enter a valid email address');
                  return;
                }
                
                if (!validatePhoneNumber(adminBookingForm.phone)) {
                  alert('Please enter a valid 10-digit phone number');
                  return;
                }
                
                try {
                  const appointmentData = {
                    fullName: adminBookingForm.name,
                    email: adminBookingForm.email,
                    phoneNumber: adminBookingForm.phone,
                    date: adminBookingForm.date,
                    time: adminBookingForm.time,
                    preparer: adminBookingForm.preparer,
                    companyId: currentCompany?.id || null,
                    confirmed: true,
                    bookedByAdmin: true
                  };
                  await addAppointment(appointmentData);
                  
                  setAdminBookingForm({ preparer: '', date: '', time: '', name: '', email: '', phone: '' });
                  setShowAdminManualBooking(false);
                  alert('✅ Appointment booked successfully!');
                } catch (error) {
                  console.error('Error saving appointment:', error);
                  alert('❌ Error saving appointment. Please try again.');
                }
              }} className="mbm-form">
                
                {/* Step 1: Select Preparer */}
                <div className="mbm-section">
                  <div className="mbm-section-header">
                    <div className={`mbm-step-number ${adminBookingForm.preparer ? 'completed' : ''}`}>
                      {adminBookingForm.preparer ? '✅' : '1'}
                    </div>
                    <h3>Select Preparer</h3>
                  </div>
                  <div className="mbm-section-content">
                    <div className="mbm-preparer-grid">
                      {preparerCredentials
                        .filter(p => p.active !== false)
                        .map(preparer => (
                          <div 
                            key={preparer.id || preparer.name}
                            className={`mbm-preparer-card ${adminBookingForm.preparer === preparer.name ? 'selected' : ''}`}
                            onClick={() => setAdminBookingForm({
                              ...adminBookingForm, 
                              preparer: preparer.name,
                              date: '',
                              time: ''
                            })}
                          >
                            <div className="mbm-preparer-avatar">
                              {preparer.photo ? (
                                <img src={preparer.photo} alt={preparer.name} />
                              ) : (
                                <span>[U]</span>
                              )}
                            </div>
                            <span className="mbm-preparer-name">{preparer.name}</span>
                            {adminBookingForm.preparer === preparer.name && (
                              <div className="mbm-check-badge">✅</div>
                            )}
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </div>

                {/* Step 2: Select Date & Time */}
                {adminBookingForm.preparer && (
                  <div className="mbm-section">
                    <div className="mbm-section-header">
                      <div className={`mbm-step-number ${adminBookingForm.date && adminBookingForm.time ? 'completed' : ''}`}>
                        {adminBookingForm.date && adminBookingForm.time ? '✅' : '2'}
                      </div>
                      <h3>Select Date & Time</h3>
                    </div>
                    <div className="mbm-section-content">
                      {getAvailableDates(adminBookingForm.preparer).length > 0 ? (
                        <>
                          <div className="mbm-date-scroll">
                            {getAvailableDates(adminBookingForm.preparer).map(date => {
                              const dateObj = new Date(date + 'T00:00:00');
                              const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                              const dayNum = dateObj.getDate();
                              const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
                              return (
                                <div 
                                  key={date}
                                  className={`mbm-date-card ${adminBookingForm.date === date ? 'selected' : ''}`}
                                  onClick={() => setAdminBookingForm({
                                    ...adminBookingForm, 
                                    date: date,
                                    time: ''
                                  })}
                                >
                                  <span className="mbm-date-day">{dayName}</span>
                                  <span className="mbm-date-num">{dayNum}</span>
                                  <span className="mbm-date-month">{month}</span>
                                </div>
                              );
                            })}
                          </div>
                          
                          {adminBookingForm.date && (
                            <div className="mbm-time-section">
                              <label className="mbm-time-label">Available Times</label>
                              {getAvailableTimeSlots(adminBookingForm.date, adminBookingForm.preparer).length > 0 ? (
                                <div className="mbm-time-grid">
                                  {getAvailableTimeSlots(adminBookingForm.date, adminBookingForm.preparer).map(slot => {
                                    const [h, m] = slot.split(':');
                                    const hour = parseInt(h);
                                    const displayTime = `${hour > 12 ? hour - 12 : hour === 0 ? 12 : hour}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
                                    return (
                                      <button
                                        type="button"
                                        key={slot}
                                        className={`mbm-time-btn ${adminBookingForm.time === slot ? 'selected' : ''}`}
                                        onClick={() => setAdminBookingForm({...adminBookingForm, time: slot})}
                                      >
                                        {displayTime}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="mbm-no-slots">
                                  <span>⚠️</span>
                                  <p>No available time slots for this date</p>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="mbm-no-slots">
                          <span>📅</span>
                          <p>No available dates for this preparer</p>
                          <small>Please set up availability first</small>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Client Information */}
                {adminBookingForm.time && (
                  <div className="mbm-section">
                    <div className="mbm-section-header">
                      <div className={`mbm-step-number ${adminBookingForm.name && adminBookingForm.email && adminBookingForm.phone ? 'completed' : ''}`}>
                        {adminBookingForm.name && adminBookingForm.email && adminBookingForm.phone ? '✅' : '3'}
                      </div>
                      <h3>Client Information</h3>
                    </div>
                    <div className="mbm-section-content">
                      <div className="mbm-form-grid">
                        <div className="mbm-field full-width">
                          <label>Full Name <span className="required">*</span></label>
                          <input
                            type="text"
                            value={adminBookingForm.name}
                            onChange={(e) => setAdminBookingForm({...adminBookingForm, name: e.target.value})}
                            placeholder="Enter client's full name"
                            required
                          />
                        </div>
                        <div className="mbm-field">
                          <label>Email Address <span className="required">*</span></label>
                          <input
                            type="email"
                            value={adminBookingForm.email}
                            onChange={(e) => setAdminBookingForm({...adminBookingForm, email: e.target.value})}
                            placeholder="client@email.com"
                            required
                          />
                        </div>
                        <div className="mbm-field">
                          <label>Phone Number <span className="required">*</span></label>
                          <input
                            type="tel"
                            value={adminBookingForm.phone}
                            onChange={(e) => setAdminBookingForm({...adminBookingForm, phone: formatPhoneNumber(e.target.value)})}
                            placeholder="717-343-3822"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary & Actions */}
                {adminBookingForm.time && (
                  <div className="mbm-summary">
                    <div className="mbm-summary-info">
                      <div className="mbm-summary-icon">📋</div>
                      <div className="mbm-summary-details">
                        <strong>Appointment Summary</strong>
                        <p>
                          {adminBookingForm.preparer} • {adminBookingForm.date ? new Date(adminBookingForm.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''} • {(() => {
                            if (!adminBookingForm.time) return '';
                            const [h, m] = adminBookingForm.time.split(':');
                            const hour = parseInt(h);
                            return `${hour > 12 ? hour - 12 : hour === 0 ? 12 : hour}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mbm-buttons">
                  <button type="button" onClick={() => setShowAdminManualBooking(false)} className="mbm-btn-cancel">
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="mbm-btn-submit"
                    disabled={!adminBookingForm.preparer || !adminBookingForm.date || !adminBookingForm.time || !adminBookingForm.name || !adminBookingForm.email || !adminBookingForm.phone}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Confirm Booking
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Profile Editor for Preparer */}
        {showProfileEditor && isPreparerView && (
          <div className="modal-overlay" onClick={() => setShowProfileEditor(false)}>
            <div className="modal-content modal-scrollable" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>✏️ Edit Your Profile</h2>
                <button onClick={() => setShowProfileEditor(false)} className="close-btn">&times;</button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleUpdateProfile} className="preparer-form">
                  {/* Photo Upload */}
                  <div className="form-group photo-form-group">
                    <label>Profile Photo:</label>
                    {showCropper && cropImage && cropTarget === 'profile' ? (
                      <div className="inline-cropper-container">
                        <div className="inline-cropper-header">
                          <span>📷 Adjust Your Photo</span>
                        </div>
                        <div className="inline-cropper-area">
                          <Cropper
                            image={cropImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropShape="round"
                            showGrid={false}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                          />
                        </div>
                        <div className="inline-cropper-controls">
                          <div className="zoom-control-inline">
                            <label>🔍 Zoom:</label>
                            <input
                              type="range"
                              min={1}
                              max={3}
                              step={0.1}
                              value={zoom}
                              onChange={(e) => setZoom(Number(e.target.value))}
                              className="zoom-slider"
                            />
                            <span className="zoom-value">{zoom.toFixed(1)}x</span>
                          </div>
                          <p className="crop-hint">Drag to position • Scroll to zoom</p>
                        </div>
                        <div className="inline-cropper-actions">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={handleCropCancel}>
                            ✕ Cancel
                          </button>
                          <button type="button" className="btn btn-primary btn-sm" onClick={handleCropConfirm}>
                            ✅ Apply
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="photo-upload-card">
                        <div className="photo-upload-left">
                          <label htmlFor="preparerProfilePhoto" className="photo-preview-circle">
                            {profileEditData.photo ? (
                              <img src={profileEditData.photo} alt="Preview" className="preview-image" />
                            ) : (
                              <div className="photo-placeholder">
                                <span className="placeholder-icon">[U]</span>
                              </div>
                            )}
                            <div className="photo-overlay">
                              <span>📷</span>
                            </div>
                          </label>
                        </div>
                        <div className="photo-upload-right">
                          <p className="photo-upload-title">Upload Profile Picture</p>
                          <p className="photo-upload-subtitle">JPG, PNG or GIF (Max 2MB)</p>
                          <div className="photo-upload-actions">
                            <label htmlFor="preparerProfilePhoto" className="photo-upload-btn">
                              <span>📷</span> Choose Photo
                            </label>
                            {profileEditData.photo && (
                              <button 
                                type="button" 
                                onClick={() => setProfileEditData({...profileEditData, photo: ''})}
                                className="btn-remove-photo"
                              >
                                ✕ Remove
                              </button>
                            )}
                          </div>
                        </div>
                        <input
                          type="file"
                          id="preparerProfilePhoto"
                          accept="image/*"
                          onChange={(e) => handlePhotoUpload(e, 'profile')}
                          className="photo-input"
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="profileEmail">Email Address:</label>
                    <input
                      type="email"
                      id="profileEmail"
                      value={profileEditData.email}
                      onChange={(e) => setProfileEditData({...profileEditData, email: e.target.value})}
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="profilePhone">Phone Number:</label>
                    <input
                      type="tel"
                      id="profilePhone"
                      value={profileEditData.phone}
                      onChange={(e) => setProfileEditData({...profileEditData, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="profileAddress">Office Address:</label>
                    <textarea
                      id="profileAddress"
                      value={profileEditData.address}
                      onChange={(e) => setProfileEditData({...profileEditData, address: e.target.value})}
                      placeholder="Enter your office address"
                      rows="2"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="profileBio">Professional Bio:</label>
                    <textarea
                      id="profileBio"
                      value={profileEditData.bio}
                      onChange={(e) => setProfileEditData({...profileEditData, bio: e.target.value})}
                      placeholder="Describe your experience, specializations, and what makes you unique..."
                      rows="4"
                    />
                  </div>

                  <div className="modal-footer">
                    <button type="submit" className="btn btn-primary btn-large">💾 Save Changes</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Availability Editor for Preparer */}
        {showAvailabilityEditor && isPreparerView && (
          <div className="modal-overlay" onClick={() => setShowAvailabilityEditor(false)}>
            <div className="modal-content bulk-availability-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>📅 Set Your Availability (Bulk)</h2>
                <button onClick={() => setShowAvailabilityEditor(false)} className="close-btn">&times;</button>
              </div>
              <div className="availability-content">
                <form onSubmit={handleAddAvailability} className="preparer-form">
                  <div className="form-group">
                    <label>Select Dates:</label>
                    <div className="bulk-dates-grid">
                      {getNextNDays(30).map(date => {
                        const dateObj = new Date(date + 'T00:00:00');
                        const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                        const dayNum = dateObj.getDate();
                        const monthName = dateObj.toLocaleDateString('en-US', { month: 'short' });
                        return (
                          <label key={date} className="date-checkbox-label">
                            <input
                              type="checkbox"
                              checked={availabilityForm.dates.includes(date)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAvailabilityForm({...availabilityForm, dates: [...availabilityForm.dates, date]});
                                } else {
                                  setAvailabilityForm({...availabilityForm, dates: availabilityForm.dates.filter(d => d !== date)});
                                }
                              }}
                            />
                            <span className="date-checkbox-content">
                              <span className="date-day">{dayName}</span>
                              <span className="date-num">{dayNum}</span>
                              <span className="date-month">{monthName}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="info-text">Selected {availabilityForm.dates.length} date(s)</p>
                  </div>
                  
                  <div className="form-group">
                    <label>Select Time Slots:</label>
                    <div className="bulk-time-grid">
                      {Array.from({ length: 24 }, (_, i) => {
                        const time24 = `${String(i).padStart(2, '0')}:00`;
                        const time12 = convert24to12Hour(time24);
                        return (
                          <label key={time24} className="time-checkbox-label">
                            <input
                              type="checkbox"
                              checked={availabilityForm.selectedTimeSlots.includes(time24)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAvailabilityForm({...availabilityForm, selectedTimeSlots: [...availabilityForm.selectedTimeSlots, time24].sort()});
                                } else {
                                  setAvailabilityForm({...availabilityForm, selectedTimeSlots: availabilityForm.selectedTimeSlots.filter(t => t !== time24)});
                                }
                              }}
                            />
                            <span className="time-checkbox-content">{time12}</span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="info-text">Selected {availabilityForm.selectedTimeSlots.length} time slot(s)</p>
                  </div>
                  
                  <button type="submit" className="btn btn-primary btn-large">Set Availability</button>
                </form>

                <div className="current-availability">
                  <h3>Your Current Availability</h3>
                  {Object.keys(preparerAvailability)
                    .filter(key => key.startsWith(loggedInPreparer?.name + '_'))
                    .length > 0 ? (
                    <div className="availability-list">
                      {Object.entries(preparerAvailability)
                        .filter(([key]) => key.startsWith(loggedInPreparer?.name + '_'))
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .map(([key, slots]) => {
                          const date = key.split('_')[1];
                          return (
                            <div key={key} className="availability-item">
                              <strong>{formatDate(date)}:</strong> {slots.map(s => convert24to12Hour(s)).join(', ')}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <p className="no-availability">No availability set yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Shareable Link Section - Only show to admin */}
        {!isClientView && !isPreparerView && isPublished && (
          <div className="availability-calendar-section">
            <div className="availability-header">
              <h2>📅 Set Your Availability</h2>
              <p className="availability-subtitle">Select a date and choose your available time slots</p>
            </div>

            <div className="availability-calendar-container">
              {/* Calendar Side */}
              <div className="calendar-panel">
                <div className="calendar-nav">
                  <button className="calendar-nav-btn" onClick={() => navigateCalendarMonth(-1)}>&#8249;</button>
                  <h3 className="calendar-month">{getMonthName(calendarMonth)}</h3>
                  <button className="calendar-nav-btn" onClick={() => navigateCalendarMonth(1)}>&#8250;</button>
                </div>

                <div className="calendar-grid">
                  <div className="calendar-weekdays">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="weekday-label">{day}</div>
                    ))}
                  </div>
                  
                  <div className="calendar-days">
                    {generateCalendarDays().map((date, index) => (
                      <button
                        key={index}
                        className={`calendar-day ${!date ? 'empty' : ''} ${isToday(date) ? 'today' : ''} ${isCalendarDateSelected(date) ? 'selected' : ''} ${isPastDate(date) ? 'past' : ''} ${hasAvailabilityOnDate(date) ? 'has-availability' : ''}`}
                        onClick={() => handleCalendarDateClick(date)}
                        disabled={!date || isPastDate(date)}
                      >
                        {date ? date.getDate() : ''}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot today-dot"></span>
                    <span>Today</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot available-dot"></span>
                    <span>Has Availability</span>
                  </div>
                </div>
              </div>

              {/* Time Slots Side */}
              <div className="time-slots-panel">
                {calendarSelectedDates.length > 0 ? (
                  <>
                    <div className="time-slots-header">
                      <div className="selected-dates-info">
                        <h3>📅 {calendarSelectedDates.length} Date{calendarSelectedDates.length !== 1 ? 's' : ''} Selected</h3>
                        <button className="clear-dates-btn" onClick={clearSelectedDates}>✕ Clear All</button>
                      </div>
                      <div className="selected-dates-list">
                        {calendarSelectedDates.map((date, idx) => (
                          <span key={idx} className="selected-date-chip">
                            {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            <button 
                              className="remove-date-btn" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCalendarDateClick(date);
                              }}
                            >x</button>
                          </span>
                        ))}
                      </div>
                      <p className="selected-count">
                        {calendarSelectedSlots.length} time slot{calendarSelectedSlots.length !== 1 ? 's' : ''} will be applied to all dates
                      </p>
                    </div>

                    <div className="quick-actions">
                      <button className="quick-btn" onClick={selectAllCalendarSlots}>Select All</button>
                      <button className="quick-btn" onClick={selectMorningSlots}>Morning</button>
                      <button className="quick-btn" onClick={selectAfternoonSlots}>Afternoon</button>
                      <button className="quick-btn" onClick={selectEveningSlots}>Evening</button>
                      <button className="quick-btn clear" onClick={clearAllCalendarSlots}>Clear Slots</button>
                    </div>

                    <div className="time-periods">
                      <div className="time-period">
                        <h4 className="period-label">🌅… Morning (6 AM - 12 PM)</h4>
                        <div className="time-slots-grid">
                          {allTimeSlots.filter(slot => {
                            const hour = parseInt(slot.split(':')[0]);
                            return hour >= 6 && hour < 12;
                          }).map(slot => (
                            <button
                              key={slot}
                              className={`time-slot-btn ${calendarSelectedSlots.includes(slot) ? 'selected' : ''}`}
                              onClick={() => toggleCalendarTimeSlot(slot)}
                            >
                              {convert24to12Hour(slot)}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="time-period">
                        <h4 className="period-label">☀️ Afternoon (12 PM - 5 PM)</h4>
                        <div className="time-slots-grid">
                          {allTimeSlots.filter(slot => {
                            const hour = parseInt(slot.split(':')[0]);
                            return hour >= 12 && hour < 17;
                          }).map(slot => (
                            <button
                              key={slot}
                              className={`time-slot-btn ${calendarSelectedSlots.includes(slot) ? 'selected' : ''}`}
                              onClick={() => toggleCalendarTimeSlot(slot)}
                            >
                              {convert24to12Hour(slot)}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="time-period">
                        <h4 className="period-label">🌅™ Evening (5 PM - 11 PM)</h4>
                        <div className="time-slots-grid">
                          {allTimeSlots.filter(slot => {
                            const hour = parseInt(slot.split(':')[0]);
                            return hour >= 17 && hour <= 23;
                          }).map(slot => (
                            <button
                              key={slot}
                              className={`time-slot-btn ${calendarSelectedSlots.includes(slot) ? 'selected' : ''}`}
                              onClick={() => toggleCalendarTimeSlot(slot)}
                            >
                              {convert24to12Hour(slot)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="save-availability-footer">
                      <button className="btn btn-primary save-availability-btn" onClick={handleSaveCalendarAvailability}>
                        💾 Save Availability for {calendarSelectedDates.length} Date{calendarSelectedDates.length !== 1 ? 's' : ''}
                      </button>
                    </div>

                    {/* Preview of selections before saving */}
                    {calendarSelectedSlots.length > 0 && (
                      <div className="selection-preview">
                        <h4 className="preview-title">📋 Preview - What will be saved:</h4>
                        <div className="preview-list">
                          {calendarSelectedDates.map((date, idx) => (
                            <div key={idx} className="preview-item">
                              <div className="preview-date-header">
                                <span className="preview-date">
                                  📅 {date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                                <div className="preview-actions">
                                  <button 
                                    className="preview-btn edit"
                                    onClick={() => {
                                      // Keep only this date selected for editing
                                      setCalendarSelectedDates([date]);
                                    }}
                                    title="Edit this date only"
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button 
                                    className="preview-btn delete"
                                    onClick={() => handleCalendarDateClick(date)}
                                    title="Remove this date"
                                  >
                                    🗑️ Remove
                                  </button>
                                </div>
                              </div>
                              <div className="preview-slots">
                                {calendarSelectedSlots.map((slot, slotIdx) => (
                                  <span key={slotIdx} className="preview-slot-chip">
                                    {convert24to12Hour(slot)}
                                    <button 
                                      className="remove-slot-btn"
                                      onClick={() => toggleCalendarTimeSlot(slot)}
                                      title="Remove this time slot from all dates"
                                    >x</button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-date-selected">
                    <div className="no-date-icon">📅</div>
                    <h3>Select Date(s)</h3>
                    <p>Click on one or more dates in the calendar to set your available time slots. You can select multiple dates to apply the same availability to all of them at once.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* My Availability Tab - Preparer Only */}
        {!isClientView && activeTab === 'my-slots' && isPreparerView && (
          <div className="my-availability-section">
            <div className="my-availability-header">
              <h2>📅 My Availability Schedule</h2>
              <p className="my-availability-subtitle">View and manage all your available time slots</p>
            </div>

            {(() => {
              const now = new Date();
              const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
              
              const myAvailability = Object.entries(preparerAvailability)
                .filter(([key]) => key.startsWith(loggedInPreparer?.name + '_'))
                .sort(([keyA], [keyB]) => {
                  const dateA = keyA.split('_')[1];
                  const dateB = keyB.split('_')[1];
                  return dateA.localeCompare(dateB);
                });

              // Separate into past and upcoming using string comparison (YYYY-MM-DD format)
              const pastSlots = myAvailability.filter(([key]) => {
                const dateStr = key.split('_')[1];
                return dateStr < todayStr;
              });
              
              const upcomingSlots = myAvailability.filter(([key]) => {
                const dateStr = key.split('_')[1];
                return dateStr >= todayStr;
              });

              if (myAvailability.length === 0) {
                return (
                  <div className="empty-slots-state">
                    <div className="empty-slots-icon">[scope]</div>
                    <h3>No Availability Set</h3>
                    <p>You haven't set any availability yet. Go to "Preparer Availability" tab to add your available time slots.</p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setActiveTab('availability')}
                    >
                      📅 Preparer Availability
                    </button>
                  </div>
                );
              }

              const renderSlotCard = ([key, slots]) => {
                const [preparer, dateStr] = key.split('_');
                // Parse date parts directly to avoid timezone issues
                const [year, month, day] = dateStr.split('-').map(Number);
                const date = new Date(year, month - 1, day);
                const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                const formattedDate = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                const isPast = dateStr < todayStr;
                const isToday = dateStr === todayStr;

                return (
                  <div key={key} className={`my-slot-card ${isPast ? 'past' : ''} ${isToday ? 'today' : ''}`}>
                    <div className="slot-card-header">
                      <div className="slot-date-info">
                        <span className="slot-day">{dayName}</span>
                        <span className="slot-full-date">{formattedDate}</span>
                        {isPast && <span className="date-badge past">Past</span>}
                        {isToday && <span className="date-badge today">Today</span>}
                        {!isPast && !isToday && <span className="date-badge upcoming">Upcoming</span>}
                      </div>
                      <div className="slot-card-actions">
                        {!isPast && (
                          <button 
                            className="slot-action-btn edit"
                            onClick={() => {
                              const targetDate = new Date(dateStr + 'T00:00:00');
                              setCalendarSelectedDates([targetDate]);
                              setCalendarSelectedSlots([...slots]);
                              setCalendarMonth(new Date(targetDate.getFullYear(), targetDate.getMonth(), 1));
                              setActiveTab('availability');
                            }}
                            title="Edit this date's availability"
                          >
                            ✏️ Edit
                          </button>
                        )}
                        <button 
                          className="slot-action-btn delete"
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete all ${slots.length} slot(s) for ${formattedDate}?`)) {
                              handleDeleteAvailability(preparer, dateStr);
                            }
                          }}
                          title="Delete all slots for this date"
                        >
                          🗑️ Delete All
                        </button>
                      </div>
                    </div>
                    <div className="slot-card-body">
                      <div className="slot-count-badge">
                        {slots.length} time slot{slots.length !== 1 ? 's' : ''}
                      </div>
                      <div className="slot-times-list">
                        {slots.map((slot, slotIdx) => (
                          <div key={slotIdx} className="slot-time-item">
                            <span className="slot-time">{convert24to12Hour(slot)}</span>
                            <button 
                              className="delete-single-slot"
                              onClick={() => {
                                if (window.confirm(`Delete ${convert24to12Hour(slot)} slot on ${formattedDate}?`)) {
                                  handleDeleteTimeSlot(preparer, dateStr, slot);
                                }
                              }}
                              title="Delete this time slot"
                            >
                              x
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              };

              return (
                <>
                  {/* Summary Stats */}
                  <div className="availability-stats">
                    <div className="avail-stat">
                      <span className="avail-stat-number">{myAvailability.length}</span>
                      <span className="avail-stat-label">Total Dates</span>
                    </div>
                    <div className="avail-stat upcoming">
                      <span className="avail-stat-number">{upcomingSlots.length}</span>
                      <span className="avail-stat-label">Upcoming</span>
                    </div>
                    <div className="avail-stat past">
                      <span className="avail-stat-number">{pastSlots.length}</span>
                      <span className="avail-stat-label">Past</span>
                    </div>
                  </div>

                  {/* Upcoming Slots */}
                  {upcomingSlots.length > 0 && (
                    <div className="slots-section">
                      <h3 className="slots-section-title">
                        <span className="section-icon">📅</span> 
                        Upcoming & Today ({upcomingSlots.length})
                      </h3>
                      <div className="my-slots-list">
                        {upcomingSlots.map(renderSlotCard)}
                      </div>
                    </div>
                  )}

                  {/* Past Slots */}
                  {pastSlots.length > 0 && (
                    <div className="slots-section past-section">
                      <h3 className="slots-section-title">
                        <span className="section-icon">📅</span> 
                        Past Dates ({pastSlots.length})
                        <button 
                          className="delete-all-past-btn"
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete ALL ${pastSlots.length} past date(s)? This cannot be undone.`)) {
                              const keysToDelete = pastSlots.map(([key]) => key);
                              handleBulkDeleteAvailability(keysToDelete);
                            }
                          }}
                        >
                          🗑️ Delete All Past
                        </button>
                      </h3>
                      <div className="my-slots-list">
                        {pastSlots.map(renderSlotCard)}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Documents Tab - Preparer Only (Admin uses CompanyAdminDashboard) */}
        {!isClientView && activeTab === 'documents' && isPreparerView && (
          <div className="documents-section" style={{ padding: '20px' }}>
            
            {/* Admin Toggle - Show/Hide Documents to Preparers */}
            {isAdmin && (
              <div style={{ 
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                borderRadius: '12px',
                padding: '20px 25px',
                marginBottom: '25px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px'
              }}>
                <div style={{ color: 'white' }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    ⭕ Document Visibility for Preparers
                  </h3>
                  <p style={{ margin: '5px 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                    {currentCompany?.showDocumentsToPreparer !== false 
                      ? '✅ Preparers can view submitted documents' 
                      : '🔒 Only admin can view documents'}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ color: 'white', fontSize: '0.9rem', fontWeight: '500' }}>
                    {currentCompany?.showDocumentsToPreparer !== false ? 'Visible' : 'Hidden'}
                  </span>
                  <button
                    onClick={async () => {
                      try {
                        let companyToUpdate = currentCompany;
                        
                        // If currentCompany doesn't have an ID, try to fetch it
                        if (!companyToUpdate?.id) {
                          // Try to get company ID from logged in preparer
                          if (loggedInPreparer?.companyId) {
                            companyToUpdate = await getCompanyById(loggedInPreparer.companyId);
                            if (companyToUpdate) {
                              setCurrentCompany(companyToUpdate);
                            }
                          }
                        }
                        
                        if (!companyToUpdate?.id) {
                          alert('Error: Company ID not found. Please log out and log back in.');
                          return;
                        }
                        
                        const newValue = !(companyToUpdate?.showDocumentsToPreparer !== false);
                        await updateCompany(companyToUpdate.id, { showDocumentsToPreparer: newValue });
                        const updatedCompany = { ...companyToUpdate, showDocumentsToPreparer: newValue };
                        setCurrentCompany(updatedCompany);
                        localStorage.setItem('currentCompany', JSON.stringify(updatedCompany));
                        alert(newValue 
                          ? '✅ Documents are now visible to all preparers!' 
                          : '🔒 Documents are now hidden from preparers - only admin can see them.');
                      } catch (error) {
                        console.error('Error updating document visibility:', error);
                        alert('Failed to update setting: ' + (error.message || 'Unknown error'));
                      }
                    }}
                    style={{
                      width: '60px',
                      height: '32px',
                      borderRadius: '16px',
                      border: 'none',
                      cursor: 'pointer',
                      position: 'relative',
                      background: currentCompany?.showDocumentsToPreparer !== false ? '#22c55e' : '#64748b',
                      transition: 'background 0.3s ease'
                    }}
                  >
                    <span style={{
                      position: 'absolute',
                      top: '4px',
                      left: currentCompany?.showDocumentsToPreparer !== false ? '32px' : '4px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: 'white',
                      transition: 'left 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                  </button>
                </div>
              </div>
            )}

            {/* Show message if documents are hidden from preparers */}
            {isPreparerView && !isAdmin && currentCompany?.showDocumentsToPreparer === false && (
              <div style={{
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '12px',
                padding: '30px',
                textAlign: 'center',
                marginBottom: '25px'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🔒</div>
                <h3 style={{ color: '#92400e', margin: '0 0 10px' }}>Documents Not Available</h3>
                <p style={{ color: '#b45309', margin: 0 }}>
                  Document access is currently restricted. Please contact your admin for access.
                </p>
              </div>
            )}

            {/* Only show documents if admin OR if preparers have access */}
            {(isAdmin || currentCompany?.showDocumentsToPreparer !== false) && (
            <>
            {/* IRS Waiver Form Section - Main Feature */}
            <div style={{ marginBottom: '40px' }}>
              {/* Header with gradient */}
              <div style={{ 
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #3b82f6 100%)',
                borderRadius: '20px 20px 0 0',
                padding: '20px',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Background pattern */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '150px',
                  height: '150px',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                  transform: 'translate(30%, -30%)'
                }}></div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative', zIndex: 1, flexWrap: 'wrap' }}>
                  <div style={{ 
                    width: '50px', 
                    height: '50px', 
                    background: 'rgba(255,255,255,0.15)', 
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    flexShrink: 0
                  }}>
                    📋
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', letterSpacing: '-0.5px', color: '#ffffff' }}>IRS Notice 2014-7 Waiver Form</h2>
                    <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '0.85rem', color: '#ffffff' }}>Medicaid waiver client intake</p>
                  </div>
                </div>
              </div>
              
              {/* Form Card Content */}
              <div style={{ 
                background: 'white',
                border: '1px solid #e5e7eb',
                borderTop: 'none',
                borderRadius: '0 0 20px 20px',
                padding: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}>

                {/* What's Included Box - Compact */}
                <div style={{ background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', marginBottom: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 12px', fontSize: '0.7rem' }}>
                    <span style={{ color: '#64748b', fontWeight: '600' }}>🔄 Includes:</span>
                    <span style={{ color: '#475569' }}>✅ IRS Form</span>
                    <span style={{ color: '#475569' }}>✅ Declaration</span>
                    <span style={{ color: '#475569' }}>✅ Info</span>
                    <span style={{ color: '#475569' }}>✅ Letter</span>
                    <span style={{ color: '#475569' }}>✅ ID</span>
                    <span style={{ color: '#475569' }}>✅ E-Sign</span>
                  </div>
                </div>

                {/* Action Buttons - Responsive Compact Design */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', 
                  gap: '8px'
                }}>
                  {/* Copy Link Card */}
                  <div 
                    onClick={() => {
                      const formUrl = `${window.location.origin}/irs-waiver?company=${loggedInPreparer?.companyId || currentCompany?.id || ''}&preparer=${loggedInPreparer?.id || ''}&taxYear=${consentForm.taxYear}`;
                      navigator.clipboard.writeText(formUrl);
                      alert('✅ Form link copied!\n\nPaste this link anywhere to share with your client.');
                    }}
                    style={{ 
                      background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                      border: '1px solid #10b981',
                      borderRadius: '10px',
                      padding: '12px 8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '44px'
                    }}
                  >
                    <div style={{ 
                      width: '32px', 
                      height: '32px', 
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      margin: '0 auto 6px',
                      boxShadow: '0 2px 6px rgba(16, 185, 129, 0.25)'
                    }}>
                      🔍—
                    </div>
                    <h3 style={{ margin: '0 0 2px 0', color: '#065f46', fontSize: '0.75rem', fontWeight: '600' }}>Copy Link</h3>
                    <p style={{ margin: 0, color: '#047857', fontSize: '0.65rem', lineHeight: '1.2' }}>Share link</p>
                  </div>

                  {/* Preview Form Card */}
                  <div 
                    onClick={() => {
                      const formUrl = `${window.location.origin}/irs-waiver?company=${loggedInPreparer?.companyId || currentCompany?.id || ''}&preparer=${loggedInPreparer?.id || ''}&taxYear=${consentForm.taxYear}`;
                      window.open(formUrl, '_blank');
                    }}
                    style={{ 
                      background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                      border: '1px solid #3b82f6',
                      borderRadius: '10px',
                      padding: '12px 8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '44px'
                    }}
                  >
                    <div style={{ 
                      width: '32px', 
                      height: '32px', 
                      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      margin: '0 auto 6px',
                      boxShadow: '0 2px 6px rgba(59, 130, 246, 0.25)'
                    }}>
                      ⭕
                    </div>
                    <h3 style={{ margin: '0 0 2px 0', color: '#1e40af', fontSize: '0.75rem', fontWeight: '600' }}>Preview</h3>
                    <p style={{ margin: 0, color: '#2563eb', fontSize: '0.65rem', lineHeight: '1.2' }}>View form</p>
                  </div>

                  {/* Send to Client Card */}
                  <div 
                    onClick={() => setShowSendConsentModal(true)}
                    style={{ 
                      background: 'linear-gradient(135deg, #fdf4ff, #fae8ff)',
                      border: '1px solid #a855f7',
                      borderRadius: '10px',
                      padding: '12px 8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '44px'
                    }}
                  >
                    <div style={{ 
                      width: '32px', 
                      height: '32px', 
                      background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      margin: '0 auto 6px',
                      boxShadow: '0 2px 6px rgba(168, 85, 247, 0.25)'
                    }}>
                      📧
                    </div>
                    <h3 style={{ margin: '0 0 2px 0', color: '#6b21a8', fontSize: '0.75rem', fontWeight: '600' }}>Send Email</h3>
                    <p style={{ margin: 0, color: '#7c3aed', fontSize: '0.65rem', lineHeight: '1.2' }}>Email client</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pending Verification Section - IRS Waiver Submissions */}
            <div className="consent-section" style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                color: '#f59e0b', 
                marginBottom: '12px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '1rem'
              }}>
                <span style={{ 
                  background: '#fef3c7', 
                  padding: '6px 12px', 
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  ⏳ Pending Verification
                  <span style={{ 
                    background: '#f59e0b', 
                    color: 'white', 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {irsWaiverSubmissions.filter(s => s.status === 'pending_verification').length}
                  </span>
                </span>
              </h3>
              
              {irsWaiverSubmissions.filter(s => s.status === 'pending_verification').length === 0 ? (
                <div style={{ background: '#fefce8', padding: '25px', borderRadius: '12px', textAlign: 'center', color: '#92400e', border: '1px dashed #fcd34d' }}>
                  <p style={{ margin: 0, fontSize: '0.95rem' }}>[scope] No pending submissions. When clients submit the form, they'll appear here for verification.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {irsWaiverSubmissions.filter(s => s.status === 'pending_verification').map(submission => (
                    <div key={submission.id} style={{ 
                      background: 'white', 
                      border: `2px solid ${submission.isPublished ? '#22c55e' : '#f59e0b'}`, 
                      borderRadius: '12px', 
                      padding: '18px 22px',
                      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                          <h4 style={{ margin: '0 0 6px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', flexWrap: 'wrap' }}>
                            [U] {submission.taxpayerInfo?.name || 'Unknown Client'}
                            <span style={{ background: '#fef3c7', color: '#b45309', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
                              ⏳ Pending Verification
                            </span>
                            {submission.isPublished && (
                              <span style={{ background: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
                                📢 Published
                              </span>
                            )}
                          </h4>
                          <p style={{ margin: '0 0 4px 0', color: '#64748b', fontSize: '0.9rem' }}>
                            📧 {submission.taxpayerInfo?.email} {submission.taxpayerInfo?.phone && `• 📞 ${submission.taxpayerInfo.phone}`}
                          </p>
                          <p style={{ margin: '0 0 4px 0', color: '#6366f1', fontSize: '0.85rem', fontWeight: '500' }}>
                            💼 Preparer: {submission.preparerName || 'Not Assigned'}
                          </p>
                          <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.8rem' }}>
                            Tax Year: {submission.taxYear} • Submitted: {(() => {
                              const dateValue = submission.submittedAt;
                              if (!dateValue) return 'N/A';
                              const date = dateValue?.toDate ? dateValue.toDate() : new Date(typeof dateValue === 'string' ? dateValue.replace(/ ET$/, '') : dateValue);
                              if (isNaN(date.getTime())) return 'N/A';
                              const day = String(date.getDate()).padStart(2, '0');
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const year = date.getFullYear();
                              return `${day}/${month}/${year}`;
                            })()}
                          </p>
                          {(submission.photoIDs?.consumer?.startsWith('data:') || submission.photoIDs?.caregiver?.startsWith('data:')) && (
                            <p style={{ margin: '4px 0 0', color: '#059669', fontSize: '0.8rem' }}>📷 Photo IDs Uploaded ✅</p>
                          )}
                          {submission.signature?.hasSignature && (
                            <p style={{ margin: '4px 0 0', color: '#6366f1', fontSize: '0.8rem' }}>✏️ Signature Captured ✅</p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => setViewingWaiverSubmission(submission)}
                            style={{ padding: '10px 14px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem' }}
                            title="View submission details"
                          >
                            ⭕ View
                          </button>
                          <button
                            onClick={async () => await downloadIRSWaiverPDF(submission, currentCompany, loggedInPreparer)}
                            style={{ padding: '10px 14px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem' }}
                            title="Download PDF"
                          >
                            📥 Download
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Verify and approve this submission?')) {
                                try {
                                  await verifyIRSWaiverSubmission(submission.id, loggedInPreparer?.id, loggedInPreparer?.name);
                                  alert('✅ Submission verified successfully!');
                                } catch (error) {
                                  alert('Failed to verify submission');
                                }
                              }
                            }}
                            style={{ padding: '10px 14px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}
                            title="Verify and approve"
                          >
                            ✅ Verify
                          </button>
                          {/* Delete button - admin only */}
                          {isAdmin && (
                            <button
                              onClick={async () => {
                                if (window.confirm('Delete this submission? This cannot be undone.')) {
                                  try {
                                    await deleteIRSWaiverSubmission(submission.id);
                                  } catch (error) {
                                    alert('Failed to delete');
                                  }
                                }
                              }}
                              style={{ padding: '10px 14px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem' }}
                              title="Delete submission"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Election Info */}
                      <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {submission.consents?.medicaidElection === 'exclude' && (
                            <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '500' }}>📝 Exclude Income</span>
                          )}
                          {submission.consents?.medicaidElection === 'include' && (
                            <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '500' }}>📝 Include Income</span>
                          )}
                          {submission.careRecipient?.name && (
                            <span style={{ background: '#fae8ff', color: '#86198f', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '500' }}>[U] Care Recipient: {submission.careRecipient.name}</span>
                          )}
                          {submission.consents?.engagementAccepted && (
                            <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '500' }}>✅ Terms Accepted</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Verified/Completed IRS Waiver Submissions Section - Organized by Tax Year */}
            <div className="consent-section">
              <h3 style={{ 
                color: '#10b981', 
                marginBottom: '15px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '1.1rem'
              }}>
                <span style={{ 
                  background: '#dcfce7', 
                  padding: '6px 12px', 
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  ✅ Completed Forms
                  <span style={{ 
                    background: '#10b981', 
                    color: 'white', 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {irsWaiverSubmissions.filter(s => s.status === 'verified' || s.status === 'completed').length}
                  </span>
                </span>
              </h3>
              
              {irsWaiverSubmissions.filter(s => s.status === 'verified' || s.status === 'completed').length === 0 ? (
                <div style={{ background: '#f0fdf4', padding: '25px', borderRadius: '12px', textAlign: 'center', color: '#166534', border: '1px dashed #86efac' }}>
                  <p style={{ margin: 0, fontSize: '0.95rem' }}>🔄 No completed forms yet. Verify pending submissions to see them here.</p>
                </div>
              ) : (
                <>
                  {/* Search and Filter Section for Completed Forms */}
                  <div style={{ 
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    borderRadius: '16px',
                    padding: '20px',
                    marginBottom: '20px',
                    border: '1px solid #86efac',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.1)'
                  }}>
                    <h4 style={{ margin: '0 0 15px 0', fontSize: '1rem', fontWeight: '700', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '1.2rem' }}>🔍</span>
                      Search & Filter Completed Forms
                    </h4>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: '#166534', marginBottom: '6px', fontWeight: '600' }}>
                          [U] Search by Name
                        </label>
                        <input
                          type="text"
                          placeholder="Client name..."
                          value={completedSearchName}
                          onChange={(e) => { setCompletedSearchName(e.target.value); setCompletedFormsPage(1); }}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: '2px solid #86efac',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            background: 'white'
                          }}
                        />
                      </div>
                      <div style={{ minWidth: '150px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: '#166534', marginBottom: '6px', fontWeight: '600' }}>
                          📅 From Date
                        </label>
                        <input
                          type="date"
                          value={completedSearchDateFrom}
                          onChange={(e) => { setCompletedSearchDateFrom(e.target.value); setCompletedFormsPage(1); }}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: '2px solid #86efac',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            background: 'white'
                          }}
                        />
                      </div>
                      <div style={{ minWidth: '150px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: '#166534', marginBottom: '6px', fontWeight: '600' }}>
                          📅 To Date
                        </label>
                        <input
                          type="date"
                          value={completedSearchDateTo}
                          onChange={(e) => { setCompletedSearchDateTo(e.target.value); setCompletedFormsPage(1); }}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: '2px solid #86efac',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            background: 'white'
                          }}
                        />
                      </div>
                      {(completedSearchName || completedSearchDateFrom || completedSearchDateTo) && (
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <button
                            onClick={() => { setCompletedSearchName(''); setCompletedSearchDateFrom(''); setCompletedSearchDateTo(''); setCompletedFormsPage(1); }}
                            style={{
                              padding: '10px 16px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '0.9rem'
                            }}
                          >
                            ✕ Clear
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tax Year Filter & Download Section - Responsive */}
                  <div style={{ 
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: '16px',
                    padding: '20px',
                    marginBottom: '20px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                  }}>
                    {/* Section Header */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: '15px',
                      marginBottom: '15px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '10px'
                      }}>
                        <h4 style={{ 
                          margin: 0, 
                          fontSize: '1rem', 
                          fontWeight: '700', 
                          color: '#1e293b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{ fontSize: '1.2rem' }}>🔒</span>
                          Filter by Tax Year
                        </h4>
                        
                        {/* Download All Button - Desktop */}
                        <button
                          onClick={() => handleDownloadAllPdfs(selectedTaxYearTab)}
                          disabled={downloadingAllPdfs}
                          style={{
                            padding: '12px 20px',
                            background: downloadingAllPdfs 
                              ? '#9ca3af' 
                              : 'linear-gradient(135deg, #f59e0b, #d97706)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            cursor: downloadingAllPdfs ? 'not-allowed' : 'pointer',
                            fontWeight: '700',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: downloadingAllPdfs ? 'none' : '0 4px 12px rgba(245, 158, 11, 0.3)',
                            minWidth: 'fit-content'
                          }}
                          title={selectedTaxYearTab ? `Download all ${selectedTaxYearTab} forms as ZIP` : 'Download all forms as ZIP'}
                        >
                          {downloadingAllPdfs ? (
                            <>
                              <span style={{ 
                                display: 'inline-block', 
                                animation: 'spin 1s linear infinite',
                                fontSize: '1.1rem'
                              }}>⏳</span>
                              <span>Downloading... {downloadProgress.current}/{downloadProgress.total}</span>
                            </>
                          ) : (
                            <>
                              <span style={{ fontSize: '1.1rem' }}>📥</span>
                              <span>Download {selectedTaxYearTab ? `${selectedTaxYearTab}` : 'All'} as ZIP</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Tax Year Tabs - Scrollable on Mobile */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '10px', 
                      flexWrap: 'wrap',
                      padding: '5px 0'
                    }}>
                      {/* All Years Tab */}
                      <button
                        onClick={() => { setSelectedTaxYearTab(null); setCompletedFormsPage(1); }}
                        style={{
                          padding: '12px 18px',
                          background: selectedTaxYearTab === null 
                            ? 'linear-gradient(135deg, #10b981, #059669)' 
                            : 'white',
                          color: selectedTaxYearTab === null ? 'white' : '#475569',
                          border: selectedTaxYearTab === null ? 'none' : '2px solid #e2e8f0',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontWeight: '700',
                          fontSize: '0.9rem',
                          transition: 'all 0.2s ease',
                          boxShadow: selectedTaxYearTab === null 
                            ? '0 4px 12px rgba(16, 185, 129, 0.3)' 
                            : '0 2px 4px rgba(0,0,0,0.05)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          whiteSpace: 'nowrap',
                          minHeight: '44px'
                        }}
                      >
                        <span style={{ fontSize: '1rem' }}>📋</span>
                        <span>All Years</span>
                        <span style={{ 
                          background: selectedTaxYearTab === null ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                          padding: '3px 10px',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight: '800'
                        }}>
                          {irsWaiverSubmissions.filter(s => s.status === 'verified' || s.status === 'completed').length}
                        </span>
                      </button>
                      
                      {/* Individual Year Tabs */}
                      {[...new Set(irsWaiverSubmissions
                        .filter(s => s.status === 'verified' || s.status === 'completed')
                        .map(s => s.taxYear))]
                        .sort((a, b) => b - a)
                        .map(year => (
                          <button
                            key={year}
                            onClick={() => { setSelectedTaxYearTab(year); setCompletedFormsPage(1); }}
                            style={{
                              padding: '12px 18px',
                              background: selectedTaxYearTab === year 
                                ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
                                : 'white',
                              color: selectedTaxYearTab === year ? 'white' : '#475569',
                              border: selectedTaxYearTab === year ? 'none' : '2px solid #e2e8f0',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              fontWeight: '700',
                              fontSize: '0.9rem',
                              transition: 'all 0.2s ease',
                              boxShadow: selectedTaxYearTab === year 
                                ? '0 4px 12px rgba(59, 130, 246, 0.3)' 
                                : '0 2px 4px rgba(0,0,0,0.05)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              whiteSpace: 'nowrap',
                              minHeight: '44px'
                            }}
                          >
                            <span style={{ fontSize: '1rem' }}>📅</span>
                            <span>TY {year}</span>
                            <span style={{ 
                              background: selectedTaxYearTab === year ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                              padding: '3px 10px',
                              borderRadius: '20px',
                              fontSize: '0.85rem',
                              fontWeight: '800'
                            }}>
                              {irsWaiverSubmissions.filter(s => (s.status === 'verified' || s.status === 'completed') && s.taxYear === year).length}
                            </span>
                          </button>
                        ))
                      }
                    </div>
                  </div>

                  {/* Download Progress Bar */}
                  {downloadingAllPdfs && (
                    <div style={{ 
                      marginBottom: '20px', 
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
                      padding: '20px', 
                      borderRadius: '16px',
                      border: '2px solid #fcd34d',
                      boxShadow: '0 4px 15px rgba(245, 158, 11, 0.2)'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        marginBottom: '12px',
                        flexWrap: 'wrap',
                        gap: '10px'
                      }}>
                        <span style={{ 
                          fontWeight: '700', 
                          color: '#92400e',
                          fontSize: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{ fontSize: '1.3rem' }}>📦</span>
                          Creating ZIP file...
                        </span>
                        <span style={{ 
                          color: '#92400e', 
                          fontSize: '0.95rem',
                          fontWeight: '600',
                          background: 'white',
                          padding: '6px 14px',
                          borderRadius: '20px'
                        }}>
                          {downloadProgress.current} of {downloadProgress.total} PDFs
                        </span>
                      </div>
                      <div style={{ 
                        background: 'rgba(255,255,255,0.7)', 
                        borderRadius: '12px', 
                        height: '14px',
                        overflow: 'hidden',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ 
                          background: 'linear-gradient(90deg, #f59e0b, #d97706, #f59e0b)',
                          backgroundSize: '200% 100%',
                          animation: 'shimmer 1.5s infinite linear',
                          height: '100%',
                          width: `${(downloadProgress.current / downloadProgress.total) * 100}%`,
                          borderRadius: '12px',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                      <p style={{ 
                        margin: '10px 0 0 0', 
                        fontSize: '0.85rem', 
                        color: '#92400e',
                        textAlign: 'center'
                      }}>
                        Please wait while we generate your PDFs...
                      </p>
                    </div>
                  )}

                  {/* Filtered submissions by selected tax year with pagination */}
                  {(() => {
                    // Apply all filters: status, tax year, name search, date range
                    const filteredSubmissions = irsWaiverSubmissions
                      .filter(s => (s.status === 'verified' || s.status === 'completed') && (selectedTaxYearTab === null || s.taxYear === selectedTaxYearTab))
                      .filter(s => {
                        // Name search filter
                        if (completedSearchName) {
                          const name = s.taxpayerInfo?.name?.toLowerCase() || '';
                          if (!name.includes(completedSearchName.toLowerCase())) return false;
                        }
                        // Date range filter (based on verifiedAt or createdAt)
                        const rawDate = s.verifiedAt || s.createdAt;
                        const submissionDate = rawDate?.toDate ? rawDate.toDate() : new Date(typeof rawDate === 'string' ? rawDate.replace(/ ET$/, '') : rawDate);
                        if (completedSearchDateFrom) {
                          const fromDate = new Date(completedSearchDateFrom);
                          if (submissionDate < fromDate) return false;
                        }
                        if (completedSearchDateTo) {
                          const toDate = new Date(completedSearchDateTo);
                          toDate.setHours(23, 59, 59, 999); // Include entire day
                          if (submissionDate > toDate) return false;
                        }
                        return true;
                      });
                    const totalPages = Math.ceil(filteredSubmissions.length / COMPLETED_FORMS_PER_PAGE);
                    const startIndex = (completedFormsPage - 1) * COMPLETED_FORMS_PER_PAGE;
                    const paginatedSubmissions = filteredSubmissions.slice(startIndex, startIndex + COMPLETED_FORMS_PER_PAGE);
                    
                    // Check if all current page items are selected
                    const allPageSelected = paginatedSubmissions.length > 0 && paginatedSubmissions.every(s => selectedCompletedForms.includes(s.id));
                    
                    return (
                      <>
                        {/* Selection Actions Bar */}
                        {selectedCompletedForms.length > 0 && (
                          <div style={{ 
                            marginBottom: '15px', 
                            padding: '14px 18px',
                            background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                            border: '2px solid #3b82f6',
                            borderRadius: '12px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            <span style={{ color: '#1d4ed8', fontSize: '0.95rem', fontWeight: '600' }}>
                              ☀️ {selectedCompletedForms.length} form{selectedCompletedForms.length > 1 ? 's' : ''} selected
                            </span>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              <button
                                onClick={async () => {
                                  const selectedSubs = irsWaiverSubmissions.filter(s => selectedCompletedForms.includes(s.id));
                                  if (selectedSubs.length === 0) return;
                                  
                                  // Single file - download directly as PDF
                                  if (selectedSubs.length === 1) {
                                    const submission = selectedSubs[0];
                                    try {
                                      await downloadIRSWaiverPDF(submission, currentCompany, loggedInPreparer);
                                      setSelectedCompletedForms([]);
                                    } catch (err) {
                                      console.error('Error downloading PDF:', err);
                                      alert('Failed to download PDF');
                                    }
                                    return;
                                  }
                                  
                                  // Multiple files - create ZIP
                                  if (!window.confirm(`Download ${selectedSubs.length} selected forms as ZIP?`)) return;
                                  
                                  setDownloadingAllPdfs(true);
                                  setDownloadProgress({ current: 0, total: selectedSubs.length });
                                  
                                  try {
                                    const JSZip = (await import('jszip')).default;
                                    const { saveAs } = await import('file-saver');
                                    const zip = new JSZip();
                                    
                                    for (let i = 0; i < selectedSubs.length; i++) {
                                      const submission = selectedSubs[i];
                                      setDownloadProgress({ current: i + 1, total: selectedSubs.length });
                                      
                                      try {
                                        const pdfBytes = await generateIRSWaiverPDF(submission, currentCompany, loggedInPreparer);
                                        const clientName = (submission.taxpayerInfo?.name || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
                                        const fileName = `IRS_Waiver_${clientName}_TY${submission.taxYear || 'Unknown'}.pdf`;
                                        zip.file(fileName, pdfBytes);
                                      } catch (err) {
                                        console.error('Error generating PDF:', err);
                                      }
                                    }
                                    
                                    const zipBlob = await zip.generateAsync({ type: 'blob' });
                                    saveAs(zipBlob, `Selected_Forms_${selectedSubs.length}_files.zip`);
                                    setSelectedCompletedForms([]);
                                    alert(`✅ Successfully downloaded ${selectedSubs.length} forms!`);
                                  } catch (error) {
                                    console.error('Error creating ZIP:', error);
                                    alert('Failed to create ZIP file');
                                  } finally {
                                    setDownloadingAllPdfs(false);
                                  }
                                }}
                                disabled={downloadingAllPdfs}
                                style={{
                                  padding: '10px 18px',
                                  background: 'linear-gradient(135deg, #10b981, #059669)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  fontSize: '0.9rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                📥 {selectedCompletedForms.length === 1 ? 'Download PDF' : `Download ${selectedCompletedForms.length} as ZIP`}
                              </button>
                              <button
                                onClick={() => setSelectedCompletedForms([])}
                                style={{
                                  padding: '10px 18px',
                                  background: '#f1f5f9',
                                  color: '#475569',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  fontSize: '0.9rem'
                                }}
                              >
                                ✕ Clear Selection
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* Results count - Enhanced */}
                        <div style={{ 
                          marginBottom: '20px', 
                          padding: '12px 16px',
                          background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
                          border: '1px solid #bae6fd',
                          borderRadius: '10px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {/* Select All Checkbox */}
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={allPageSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    // Add all current page items to selection
                                    const newSelected = [...new Set([...selectedCompletedForms, ...paginatedSubmissions.map(s => s.id)])];
                                    setSelectedCompletedForms(newSelected);
                                  } else {
                                    // Remove all current page items from selection
                                    setSelectedCompletedForms(prev => prev.filter(id => !paginatedSubmissions.map(s => s.id).includes(id)));
                                  }
                                }}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                              <span style={{ color: '#0369a1', fontSize: '0.9rem', fontWeight: '600' }}>Select All</span>
                            </label>
                            <span style={{ color: '#0369a1', fontSize: '0.95rem', fontWeight: '500' }}>
                              📋 Showing <strong>{filteredSubmissions.length > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + COMPLETED_FORMS_PER_PAGE, filteredSubmissions.length)}</strong> of <strong>{filteredSubmissions.length}</strong> forms
                            </span>
                          </div>
                          {selectedTaxYearTab && (
                            <span style={{ 
                              background: '#0ea5e9', 
                              color: 'white', 
                              padding: '4px 12px', 
                              borderRadius: '20px', 
                              fontSize: '0.8rem',
                              fontWeight: '600'
                            }}>
                              Tax Year: {selectedTaxYearTab}
                            </span>
                          )}
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {paginatedSubmissions.map(submission => (
                            <div key={submission.id} style={{ 
                              background: selectedCompletedForms.includes(submission.id) ? '#dcfce7' : 'white', 
                              border: selectedCompletedForms.includes(submission.id) ? '2px solid #22c55e' : '1px solid #d1d5db', 
                              borderRadius: '8px', 
                              padding: '12px 15px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '10px',
                              flexWrap: 'wrap'
                            }}>
                              {/* Left: Checkbox + Name */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1', minWidth: '150px' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedCompletedForms.includes(submission.id)}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    const isChecked = e.target.checked;
                                    if (isChecked) {
                                      setSelectedCompletedForms(prev => [...prev, submission.id]);
                                    } else {
                                      setSelectedCompletedForms(prev => prev.filter(id => id !== submission.id));
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ 
                                    width: '22px', 
                                    height: '22px', 
                                    cursor: 'pointer', 
                                    accentColor: '#22c55e', 
                                    flexShrink: 0,
                                    WebkitAppearance: 'checkbox',
                                    appearance: 'checkbox'
                                  }}
                                />
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {submission.taxpayerInfo?.name || 'Unknown'}
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                    TY {submission.taxYear} • {(() => {
                                      const dateValue = submission.verifiedAt || submission.createdAt;
                                      if (!dateValue) return 'N/A';
                                      // Handle Firebase Timestamp (has toDate method) or regular date string
                                      const date = dateValue?.toDate ? dateValue.toDate() : new Date(typeof dateValue === 'string' ? dateValue.replace(/ ET$/, '') : dateValue);
                                      if (isNaN(date.getTime())) return 'N/A';
                                      const day = String(date.getDate()).padStart(2, '0');
                                      const month = String(date.getMonth() + 1).padStart(2, '0');
                                      const year = date.getFullYear();
                                      return `${day}/${month}/${year}`;
                                    })()}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Right: Action Buttons */}
                              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                <button
                                  onClick={async () => await downloadIRSWaiverPDF(submission, currentCompany, loggedInPreparer)}
                                  style={{ padding: '8px 12px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' }}
                                >
                                  📥 PDF
                                </button>
                                <button
                                  onClick={() => setViewingWaiverSubmission(submission)}
                                  style={{ padding: '8px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' }}
                                >
                                  ⭕ View
                                </button>
                                {/* Complete button - only if not already fully completed */}
                                {submission.status !== 'completed' && (
                                  <button
                                    onClick={() => {
                                      setEditingPreparerSection(submission);
                                      setPreparerSectionData(submission.preparerSection || {
                                        preparerSignature: loggedInPreparer?.name || '',
                                        preparerSignatureDate: toEasternInputDate(),
                                        preparerSignatureImage: savedPreparerSignature || '',
                                        ptin: '',
                                        firmName: currentCompany?.name || '',
                                          firmAddress: currentCompany?.address || '',
                                          firmEIN: '',
                                          notes: ''
                                        });
                                      }}
                                      style={{ padding: '8px 12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' }}
                                    >
                                      ✏️ Done
                                    </button>
                                  )}
                                </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Pagination Controls - Mobile Responsive */}
                        {totalPages > 1 && (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            gap: '15px', 
                            marginTop: '25px',
                            paddingTop: '25px',
                            borderTop: '2px solid #e5e7eb'
                          }}>
                            {/* Page info */}
                            <div style={{
                              background: '#f8fafc',
                              padding: '8px 16px',
                              borderRadius: '20px',
                              color: '#64748b',
                              fontSize: '0.9rem',
                              fontWeight: '500'
                            }}>
                              Page {completedFormsPage} of {totalPages}
                            </div>
                            
                            {/* Navigation buttons */}
                            <div style={{ 
                              display: 'flex', 
                              flexWrap: 'wrap',
                              justifyContent: 'center',
                              alignItems: 'center', 
                              gap: '10px'
                            }}>
                              <button
                                onClick={() => setCompletedFormsPage(prev => Math.max(1, prev - 1))}
                                disabled={completedFormsPage === 1}
                                style={{
                                  padding: '12px 20px',
                                  background: completedFormsPage === 1 ? '#e5e7eb' : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                  color: completedFormsPage === 1 ? '#9ca3af' : 'white',
                                  border: 'none',
                                  borderRadius: '10px',
                                  cursor: completedFormsPage === 1 ? 'not-allowed' : 'pointer',
                                  fontWeight: '600',
                                  fontSize: '0.95rem',
                                  minHeight: '44px',
                                  boxShadow: completedFormsPage === 1 ? 'none' : '0 2px 6px rgba(59, 130, 246, 0.3)',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                ← Previous
                              </button>
                            
                              <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                gap: '6px' 
                              }}>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                  <button
                                    key={page}
                                    onClick={() => setCompletedFormsPage(page)}
                                    style={{
                                      padding: '10px 16px',
                                      background: page === completedFormsPage ? 'linear-gradient(135deg, #10b981, #059669)' : 'white',
                                      color: page === completedFormsPage ? 'white' : '#64748b',
                                      border: page === completedFormsPage ? 'none' : '2px solid #e5e7eb',
                                      borderRadius: '10px',
                                      cursor: 'pointer',
                                      fontWeight: '600',
                                      fontSize: '0.95rem',
                                      minWidth: '44px',
                                      minHeight: '44px',
                                      boxShadow: page === completedFormsPage ? '0 2px 6px rgba(16, 185, 129, 0.3)' : '0 1px 2px rgba(0,0,0,0.05)',
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    {page}
                                  </button>
                                ))}
                              </div>
                            
                              <button
                                onClick={() => setCompletedFormsPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={completedFormsPage === totalPages}
                                style={{
                                  padding: '12px 20px',
                                  background: completedFormsPage === totalPages ? '#e5e7eb' : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                  color: completedFormsPage === totalPages ? '#9ca3af' : 'white',
                                  border: 'none',
                                  borderRadius: '10px',
                                  cursor: completedFormsPage === totalPages ? 'not-allowed' : 'pointer',
                                  fontWeight: '600',
                                  fontSize: '0.95rem',
                                  minHeight: '44px',
                                  boxShadow: completedFormsPage === totalPages ? 'none' : '0 2px 6px rgba(59, 130, 246, 0.3)',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                Next →
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
        </>
        )}
        </div>
        )}

        {/* Tax Returns Tab Content - Preparer Only (Admin uses CompanyAdminDashboard) */}
        {!isClientView && activeTab === 'taxreturns' && isPreparerView && (
          <div className="tax-returns-section" style={{ padding: '20px' }}>
            {/* Header with Summary */}
            <div className="tax-returns-header" style={{ 
              background: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)',
              borderRadius: '16px 16px 0 0',
              padding: '12px 16px',
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '20px' }}>💰</span>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '700' }}>Tax Returns Tracker</h2>
                  </div>
                </div>
                {/* Summary Stats - Inline */}
                <div className="tax-returns-stats" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '0.8rem' }}>
                  <span>[\$] <b>${taxReturnsGrandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</b></span>
                  <span>✅ <b>${taxReturnsPaidTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</b></span>
                  <span>⏳ <b>${taxReturnsUnpaidTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</b></span>
                  <span>📊 <b>{taxReturns.length}</b></span>
                </div>
              </div>
            </div>

            {/* Single Row Input Form */}
            <div className="tax-returns-form-container" style={{ 
              background: '#f8fafc',
              padding: '10px 16px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <form onSubmit={handleSubmitTaxReturn}>
                <div className="tax-returns-form-row" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  flexWrap: 'wrap'
                }}>
                  {/* Client Name */}
                  <input
                    type="text"
                    name="clientName"
                    value={taxReturnForm.clientName}
                    onChange={handleTaxReturnFormChange}
                    placeholder="Client Name *"
                    required
                    className="tax-return-input"
                    style={{ 
                      flex: '1 1 120px',
                      minWidth: '100px',
                      padding: '8px 10px', 
                      borderRadius: '6px', 
                      border: '1px solid #d1d5db', 
                      fontSize: '13px',
                      boxSizing: 'border-box'
                    }}
                  />

                  {/* Phone */}
                  <input
                    type="tel"
                    name="phone"
                    value={taxReturnForm.phone}
                    onChange={handleTaxReturnFormChange}
                    placeholder="Phone"
                    className="tax-return-input"
                    style={{ 
                      flex: '1 1 100px',
                      minWidth: '90px',
                      padding: '8px 10px', 
                      borderRadius: '6px', 
                      border: '1px solid #d1d5db', 
                      fontSize: '13px',
                      boxSizing: 'border-box'
                    }}
                  />

                  {/* Amount */}
                  <input
                    type="number"
                    name="amount"
                    value={taxReturnForm.amount}
                    onChange={handleTaxReturnFormChange}
                    placeholder="Amount *"
                    required
                    min="0"
                    step="0.01"
                    className="tax-return-input"
                    style={{ 
                      flex: '0 1 80px',
                      minWidth: '70px',
                      padding: '8px 10px', 
                      borderRadius: '6px', 
                      border: '1px solid #d1d5db', 
                      fontSize: '13px',
                      boxSizing: 'border-box'
                    }}
                  />

                  {/* Tax Year */}
                  <select
                    name="taxYear"
                    value={taxReturnForm.taxYear}
                    onChange={handleTaxReturnFormChange}
                    className="tax-return-select"
                    style={{ 
                      flex: '0 1 70px',
                      minWidth: '65px',
                      padding: '8px 6px', 
                      borderRadius: '6px', 
                      border: '1px solid #d1d5db', 
                      fontSize: '13px',
                      boxSizing: 'border-box',
                      background: 'white'
                    }}
                  >
                    {[...Array(10)].map((_, i) => {
                      const year = new Date().getFullYear() - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>

                  {/* Payment Status */}
                  <select
                    name="paymentStatus"
                    value={taxReturnForm.paymentStatus}
                    onChange={handleTaxReturnFormChange}
                    className="tax-return-select"
                    style={{ 
                      flex: '0 1 80px',
                      minWidth: '75px',
                      padding: '8px 6px', 
                      borderRadius: '6px', 
                      border: '1px solid #d1d5db', 
                      fontSize: '13px',
                      boxSizing: 'border-box',
                      background: 'white'
                    }}
                  >
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                  </select>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={savingTaxReturn}
                    className="tax-return-submit-btn"
                    style={{ 
                      padding: '8px 14px', 
                      background: 'linear-gradient(135deg, #10b981, #059669)', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '6px', 
                      fontSize: '13px', 
                      fontWeight: '600', 
                      cursor: savingTaxReturn ? 'not-allowed' : 'pointer',
                      opacity: savingTaxReturn ? 0.7 : 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {savingTaxReturn ? '...' : (editingTaxReturn ? 'Update' : '+ Add')}
                  </button>

                  {/* Cancel Button (when editing) */}
                  {editingTaxReturn && (
                    <button
                      type="button"
                      onClick={handleCancelEditTaxReturn}
                      className="tax-return-cancel-btn"
                      style={{ 
                        padding: '8px 12px', 
                        background: '#ef4444', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        fontSize: '13px', 
                        fontWeight: '500', 
                        cursor: 'pointer'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Action Bar - Download PDF */}
            <div className="tax-returns-action-bar" style={{ 
              background: 'white',
              padding: '12px 20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  {selectedTaxReturns.length > 0 
                    ? `${selectedTaxReturns.length} selected` 
                    : `${taxReturns.length} records`}
                </span>
              </div>
              <button
                onClick={handleDownloadTaxReturnsPDF}
                disabled={taxReturns.length === 0}
                className="tax-return-download-btn"
                style={{ 
                  padding: '8px 16px', 
                  background: taxReturns.length === 0 ? '#e5e7eb' : 'linear-gradient(135deg, #3b82f6, #1d4ed8)', 
                  color: taxReturns.length === 0 ? '#9ca3af' : 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontSize: '0.85rem', 
                  fontWeight: '600', 
                  cursor: taxReturns.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                📥 Download PDF {selectedTaxReturns.length > 0 ? `(${selectedTaxReturns.length})` : ''}
              </button>
            </div>

            {/* Tax Returns Table */}
            <div className="tax-returns-table-container" style={{ 
              background: 'white',
              borderRadius: '0 0 16px 16px',
              overflow: 'hidden'
            }}>
              {taxReturns.length === 0 ? (
                <div className="tax-returns-empty" style={{ 
                  padding: '50px 20px', 
                  textAlign: 'center',
                  color: '#64748b'
                }}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
                  <h3 style={{ margin: '0 0 8px 0', color: '#374151', fontSize: '1.1rem' }}>No Tax Returns Yet</h3>
                  <p style={{ margin: 0, fontSize: '0.9rem' }}>Add your first entry using the form above.</p>
                </div>
              ) : (
                <>
                  {/* Mobile Card View */}
                  <div className="tax-returns-mobile-cards">
                    {taxReturns.map((tr, index) => (
                      <div 
                        key={tr.id} 
                        className="tax-return-card"
                        style={{
                          background: selectedTaxReturns.includes(tr.id) ? '#eff6ff' : 'white',
                          borderRadius: '12px',
                          padding: '14px',
                          marginBottom: '10px',
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                        }}
                      >
                        {/* Card Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                              type="checkbox"
                              checked={selectedTaxReturns.includes(tr.id)}
                              onChange={() => handleSelectTaxReturn(tr.id)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <div>
                              <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.95rem' }}>{tr.clientName}</div>
                              <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '2px' }}>{tr.phone || 'No phone'}</div>
                            </div>
                          </div>
                          <span style={{ 
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            background: tr.paymentStatus === 'paid' ? '#dcfce7' : '#fee2e2',
                            color: tr.paymentStatus === 'paid' ? '#166534' : '#dc2626'
                          }}>
                            {tr.paymentStatus === 'paid' ? '✅ Paid' : '⏳ Unpaid'}
                          </span>
                        </div>
                        
                        {/* Card Body */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: '1px solid #f3f4f6', borderBottom: '1px solid #f3f4f6' }}>
                          <div style={{ textAlign: 'center', flex: 1 }}>
                            <div style={{ color: '#6b7280', fontSize: '0.7rem', marginBottom: '2px' }}>Year</div>
                            <div style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>{tr.taxYear}</div>
                          </div>
                          <div style={{ textAlign: 'center', flex: 1, borderLeft: '1px solid #e5e7eb' }}>
                            <div style={{ color: '#6b7280', fontSize: '0.7rem', marginBottom: '2px' }}>Amount</div>
                            <div style={{ fontWeight: '700', color: '#059669', fontSize: '1rem' }}>${tr.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                          </div>
                        </div>
                        
                        {/* Card Actions */}
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                          <button
                            onClick={() => handleEditTaxReturn(tr)}
                            style={{ 
                              flex: 1,
                              padding: '10px', 
                              background: '#3b82f6', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: '8px', 
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTaxReturn(tr.id)}
                            style={{ 
                              flex: 1,
                              padding: '10px', 
                              background: '#ef4444', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: '8px', 
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop Table View */}
                  <div className="tax-returns-desktop-table" style={{ overflowX: 'auto' }}>
                    <table className="tax-returns-table" style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      minWidth: '700px'
                    }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                          <th className="tax-col-checkbox" style={{ padding: '12px 10px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '0.8rem', width: '50px' }}>
                            <input
                              type="checkbox"
                              checked={selectedTaxReturns.length === taxReturns.length && taxReturns.length > 0}
                              onChange={handleSelectAllTaxReturns}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          </th>
                          <th className="tax-col-name" style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.8rem' }}>Client Name</th>
                          <th className="tax-col-phone" style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.8rem' }}>Phone</th>
                          <th className="tax-col-year" style={{ padding: '12px 14px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '0.8rem' }}>Year</th>
                          <th className="tax-col-amount" style={{ padding: '12px 14px', textAlign: 'right', fontWeight: '600', color: '#374151', fontSize: '0.8rem' }}>Amount</th>
                          <th className="tax-col-status" style={{ padding: '12px 14px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '0.8rem' }}>Status</th>
                          <th className="tax-col-actions" style={{ padding: '12px 14px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '0.8rem' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taxReturns.map((tr, index) => (
                          <tr 
                            key={tr.id} 
                            className="tax-return-row"
                            style={{ 
                              borderBottom: '1px solid #e5e7eb',
                              background: selectedTaxReturns.includes(tr.id) ? '#eff6ff' : (index % 2 === 0 ? 'white' : '#fafafa')
                            }}
                          >
                            <td className="tax-col-checkbox" style={{ padding: '12px 10px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={selectedTaxReturns.includes(tr.id)}
                                onChange={() => handleSelectTaxReturn(tr.id)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                            </td>
                            <td className="tax-col-name" style={{ padding: '12px 14px', fontWeight: '500', color: '#1e293b', fontSize: '0.9rem' }}>
                              {tr.clientName}
                            </td>
                            <td className="tax-col-phone" style={{ padding: '12px 14px', color: '#64748b', fontSize: '0.9rem' }}>
                              {tr.phone || '-'}
                            </td>
                            <td className="tax-col-year" style={{ padding: '12px 14px', textAlign: 'center', color: '#64748b', fontSize: '0.9rem' }}>
                              {tr.taxYear}
                            </td>
                            <td className="tax-col-amount" style={{ padding: '12px 14px', textAlign: 'right', fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>
                              ${tr.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="tax-col-status" style={{ padding: '12px 14px', textAlign: 'center' }}>
                              <span className="tax-status-badge" style={{ 
                                display: 'inline-block',
                                padding: '4px 12px',
                                borderRadius: '15px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                background: tr.paymentStatus === 'paid' ? '#dcfce7' : '#fee2e2',
                                color: tr.paymentStatus === 'paid' ? '#166534' : '#dc2626'
                              }}>
                                {tr.paymentStatus === 'paid' ? '✅ Paid' : '⏳ Unpaid'}
                              </span>
                            </td>
                            <td className="tax-col-actions" style={{ padding: '12px 14px', textAlign: 'center' }}>
                              <div className="tax-action-buttons" style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                <button
                                  onClick={() => handleEditTaxReturn(tr)}
                                  className="tax-edit-btn"
                                  style={{ 
                                    padding: '5px 10px', 
                                    background: '#3b82f6', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '5px', 
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    fontWeight: '500'
                                  }}
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => handleDeleteTaxReturn(tr.id)}
                                  className="tax-delete-btn"
                                  style={{ 
                                    padding: '5px 10px', 
                                    background: '#ef4444', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '5px', 
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    fontWeight: '500'
                                  }}
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Preparer Section Edit Modal */}
        {editingPreparerSection && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '30px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '1.4rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                ✏️ Complete Preparer Section
              </h2>
              <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '0.9rem' }}>
                For: {editingPreparerSection.taxpayerInfo?.name || `${editingPreparerSection.taxpayer?.firstName || ''} ${editingPreparerSection.taxpayer?.lastName || ''}`.trim()}
                {editingPreparerSection.spouseInfo?.name && ` & ${editingPreparerSection.spouseInfo.name}`}
                {editingPreparerSection.spouse?.firstName && ` & ${editingPreparerSection.spouse.firstName} ${editingPreparerSection.spouse.lastName}`}
              </p>
              
              {/* Client Address Verification */}
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px', color: '#1d4ed8', fontSize: '0.95rem' }}>📍 Client Address to Verify</h4>
                <p style={{ margin: 0, color: '#1e293b', fontWeight: '500' }}>
                  {editingPreparerSection.taxpayerInfo?.address || editingPreparerSection.taxpayer?.address || 'N/A'}
                  {(editingPreparerSection.taxpayerInfo?.city || editingPreparerSection.taxpayer?.city) && 
                    `, ${editingPreparerSection.taxpayerInfo?.city || editingPreparerSection.taxpayer?.city}, ${editingPreparerSection.taxpayerInfo?.state || editingPreparerSection.taxpayer?.state} ${editingPreparerSection.taxpayerInfo?.zipCode || editingPreparerSection.taxpayer?.zipCode}`
                  }
                </p>
              </div>
              
              <div style={{ display: 'grid', gap: '16px' }}>
                {/* Preparer Name (typed signature) */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Preparer Name *
                  </label>
                  <input
                    type="text"
                    value={preparerSectionData.preparerSignature}
                    onChange={(e) => setPreparerSectionData({...preparerSectionData, preparerSignature: e.target.value})}
                    placeholder="Type your full name"
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                  />
                </div>
                
                {/* Preparer Signature Drawing */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Preparer Signature (Draw or Use Saved) *
                  </label>
                  
                  {/* Use saved signature option */}
                  {savedPreparerSignature && (
                    <div style={{ marginBottom: '10px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ color: '#166534', fontWeight: '500', fontSize: '0.9rem' }}>✅ Saved Signature Available</span>
                          <img src={savedPreparerSignature} alt="Saved Signature" style={{ height: '40px', background: 'white', borderRadius: '4px', padding: '4px' }} />
                        </div>
                        <button
                          type="button"
                          onClick={() => setPreparerSectionData({...preparerSectionData, preparerSignatureImage: savedPreparerSignature})}
                          style={{ padding: '8px 16px', background: preparerSectionData.preparerSignatureImage === savedPreparerSignature ? '#22c55e' : '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem' }}
                        >
                          {preparerSectionData.preparerSignatureImage === savedPreparerSignature ? '✅ Using Saved' : 'Use This Signature'}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Signature Canvas */}
                  <div style={{ border: '2px dashed #d1d5db', borderRadius: '8px', padding: '8px', background: '#fafafa' }}>
                    <canvas
                      ref={preparerSignatureCanvasRef}
                      width={500}
                      height={120}
                      style={{ width: '100%', height: '120px', cursor: 'crosshair', background: 'white', borderRadius: '4px', touchAction: 'none' }}
                      onMouseDown={(e) => {
                        setIsDrawingPreparerSignature(true);
                        const canvas = preparerSignatureCanvasRef.current;
                        const ctx = canvas.getContext('2d');
                        const rect = canvas.getBoundingClientRect();
                        ctx.beginPath();
                        ctx.moveTo((e.clientX - rect.left) * (canvas.width / rect.width), (e.clientY - rect.top) * (canvas.height / rect.height));
                      }}
                      onMouseMove={(e) => {
                        if (!isDrawingPreparerSignature) return;
                        const canvas = preparerSignatureCanvasRef.current;
                        const ctx = canvas.getContext('2d');
                        const rect = canvas.getBoundingClientRect();
                        ctx.lineWidth = 2;
                        ctx.lineCap = 'round';
                        ctx.strokeStyle = '#000';
                        ctx.lineTo((e.clientX - rect.left) * (canvas.width / rect.width), (e.clientY - rect.top) * (canvas.height / rect.height));
                        ctx.stroke();
                      }}
                      onMouseUp={() => {
                        setIsDrawingPreparerSignature(false);
                        const canvas = preparerSignatureCanvasRef.current;
                        const signatureData = canvas.toDataURL('image/png');
                        setPreparerSectionData({...preparerSectionData, preparerSignatureImage: signatureData});
                      }}
                      onMouseLeave={() => setIsDrawingPreparerSignature(false)}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        setIsDrawingPreparerSignature(true);
                        const canvas = preparerSignatureCanvasRef.current;
                        const ctx = canvas.getContext('2d');
                        const rect = canvas.getBoundingClientRect();
                        const touch = e.touches[0];
                        ctx.beginPath();
                        ctx.moveTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                      }}
                      onTouchMove={(e) => {
                        e.preventDefault();
                        if (!isDrawingPreparerSignature) return;
                        const canvas = preparerSignatureCanvasRef.current;
                        const ctx = canvas.getContext('2d');
                        const rect = canvas.getBoundingClientRect();
                        const touch = e.touches[0];
                        ctx.lineWidth = 2;
                        ctx.lineCap = 'round';
                        ctx.strokeStyle = '#000';
                        ctx.lineTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                        ctx.stroke();
                      }}
                      onTouchEnd={() => {
                        setIsDrawingPreparerSignature(false);
                        const canvas = preparerSignatureCanvasRef.current;
                        const signatureData = canvas.toDataURL('image/png');
                        setPreparerSectionData({...preparerSectionData, preparerSignatureImage: signatureData});
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                      <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Draw your signature above</span>
                      <button
                        type="button"
                        onClick={() => {
                          const canvas = preparerSignatureCanvasRef.current;
                          const ctx = canvas.getContext('2d');
                          ctx.clearRect(0, 0, canvas.width, canvas.height);
                          setPreparerSectionData({...preparerSectionData, preparerSignatureImage: ''});
                        }}
                        style={{ padding: '4px 12px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  
                  {/* Preview drawn signature */}
                  {preparerSectionData.preparerSignatureImage && preparerSectionData.preparerSignatureImage !== savedPreparerSignature && (
                    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: '#64748b', fontSize: '0.85rem' }}>Preview:</span>
                      <img src={preparerSectionData.preparerSignatureImage} alt="Signature Preview" style={{ height: '40px', background: '#f8fafc', borderRadius: '4px', padding: '4px', border: '1px solid #e5e7eb' }} />
                    </div>
                  )}
                </div>
                
                {/* Signature Date */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Signature Date *
                  </label>
                  <input
                    type="date"
                    value={preparerSectionData.preparerSignatureDate}
                    onChange={(e) => setPreparerSectionData({...preparerSectionData, preparerSignatureDate: e.target.value})}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                  />
                </div>
                
                {/* PTIN */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    PTIN (Preparer Tax Identification Number) *
                  </label>
                  <input
                    type="text"
                    value={preparerSectionData.ptin}
                    onChange={(e) => setPreparerSectionData({...preparerSectionData, ptin: e.target.value})}
                    placeholder="P00000000"
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                  />
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {/* Firm Name */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      Firm Name
                    </label>
                    <input
                      type="text"
                      value={preparerSectionData.firmName}
                      onChange={(e) => setPreparerSectionData({...preparerSectionData, firmName: e.target.value})}
                      placeholder="Your firm name"
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                    />
                  </div>
                  
                  {/* Firm EIN */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      Firm EIN
                    </label>
                    <input
                      type="text"
                      value={preparerSectionData.firmEIN}
                      onChange={(e) => setPreparerSectionData({...preparerSectionData, firmEIN: e.target.value})}
                      placeholder="XX-XXXXXXX"
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
                
                {/* Firm Address */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Firm Address
                  </label>
                  <input
                    type="text"
                    value={preparerSectionData.firmAddress}
                    onChange={(e) => setPreparerSectionData({...preparerSectionData, firmAddress: e.target.value})}
                    placeholder="Firm street address, city, state, ZIP"
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                  />
                </div>
                
                {/* Notes */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Notes (Optional)
                  </label>
                  <textarea
                    value={preparerSectionData.notes}
                    onChange={(e) => setPreparerSectionData({...preparerSectionData, notes: e.target.value})}
                    placeholder="Any additional notes..."
                    rows={2}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
                
                {/* Save signature for future use checkbox */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    id="saveSignature"
                    checked={preparerSectionData.saveSignatureForFuture || false}
                    onChange={(e) => setPreparerSectionData({...preparerSectionData, saveSignatureForFuture: e.target.checked})}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label htmlFor="saveSignature" style={{ color: '#374151', fontSize: '0.9rem', cursor: 'pointer' }}>
                    💾 Save my signature for future documents
                  </label>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setEditingPreparerSection(null);
                    setPreparerSectionData({
                      preparerSignature: '',
                      preparerSignatureDate: '',
                      preparerSignatureImage: '',
                      ptin: '',
                      firmName: '',
                      firmAddress: '',
                      firmEIN: '',
                      notes: ''
                    });
                  }}
                  style={{ padding: '12px 24px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.95rem' }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!preparerSectionData.preparerSignature || !preparerSectionData.preparerSignatureDate || !preparerSectionData.ptin) {
                      alert('Please fill in required fields: Name, Date, and PTIN');
                      return;
                    }
                    if (!preparerSectionData.preparerSignatureImage) {
                      alert('Please draw your signature or use your saved signature');
                      return;
                    }
                    try {
                      // Save signature for future use if checked
                      if (preparerSectionData.saveSignatureForFuture && preparerSectionData.preparerSignatureImage) {
                        localStorage.setItem('savedPreparerSignature', JSON.stringify(preparerSectionData.preparerSignatureImage));
                        setSavedPreparerSignature(preparerSectionData.preparerSignatureImage);
                      }
                      
                      await completeIRSWaiverSubmission(editingPreparerSection.id, {
                        ...preparerSectionData,
                        completedBy: loggedInPreparer?.name || 'Admin',
                        completedById: loggedInPreparer?.id,
                        completedAt: toEasternISOString()
                      });
                      alert('✅ Form verified and completed successfully!');
                      setEditingPreparerSection(null);
                      setPreparerSectionData({
                        preparerSignature: '',
                        preparerSignatureDate: '',
                        preparerSignatureImage: '',
                        ptin: '',
                        firmName: '',
                        firmAddress: '',
                        firmEIN: '',
                        notes: ''
                      });
                    } catch (error) {
                      console.error('Error completing form:', error);
                      alert('Failed to complete form. Please try again.');
                    }
                  }}
                  style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  ✅ Verify & Complete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preparer Availability View - Show when preparer clicks Set Availability tab */}
        {!isClientView && activeTab === 'availability' && isPreparerView && (
          <div className="preparer-availability-section" style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>
                  📅 Your Availability
                </h2>
                <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem' }}>
                  Manage your available time slots for client bookings
                </p>
              </div>
              <button
                onClick={() => setShowAvailabilityEditor(true)}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                + Set New Availability
              </button>
            </div>

            {/* Current Availability Display */}
            {Object.keys(preparerAvailability)
              .filter(key => key.startsWith(loggedInPreparer?.name + '_'))
              .length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                {Object.entries(preparerAvailability)
                  .filter(([key]) => key.startsWith(loggedInPreparer?.name + '_'))
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([key, slots]) => {
                    const date = key.split('_')[1];
                    const dateObj = new Date(date + 'T00:00:00');
                    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
                    const monthDay = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    const isPast = new Date(date) < new Date(new Date().toDateString());
                    
                    return (
                      <div key={key} style={{
                        background: isPast ? '#f8fafc' : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                        borderRadius: '12px',
                        padding: '16px',
                        border: isPast ? '1px solid #e2e8f0' : '1px solid #bae6fd',
                        opacity: isPast ? 0.6 : 1
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '12px'
                        }}>
                          <div>
                            <div style={{ fontWeight: '700', color: '#0369a1', fontSize: '1rem' }}>{dayName}</div>
                            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>{monthDay}</div>
                          </div>
                          {!isPast && (
                            <button
                              onClick={() => handleDeleteAvailability(key)}
                              style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                color: '#dc2626',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '500'
                              }}
                            >
                              🗑️ Remove
                            </button>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {slots.map(slot => (
                            <span key={slot} style={{
                              background: isPast ? '#e2e8f0' : '#3b82f6',
                              color: isPast ? '#64748b' : 'white',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              fontWeight: '500'
                            }}>
                              {convert24to12Hour(slot)}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '2px dashed #cbd5e1'
              }}>
                <span style={{ fontSize: '3rem', display: 'block', marginBottom: '16px' }}>📅</span>
                <h3 style={{ margin: '0 0 8px 0', color: '#1e293b', fontWeight: '600' }}>No Availability Set</h3>
                <p style={{ margin: '0 0 20px 0', color: '#64748b' }}>
                  You haven't set any available time slots yet. Click the button above to set your availability.
                </p>
                <button
                  onClick={() => setShowAvailabilityEditor(true)}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.95rem'
                  }}
                >
                  + Set Your First Availability
                </button>
              </div>
            )}
          </div>
        )}

        {/* Admin Availability View - Only for owner (admin uses CompanyAdminDashboard) */}
        {!isClientView && activeTab === 'availability' && !isPreparerView && !(isAdmin && currentCompany) && (
        <div className="availability-actions-section">
          <div className="admin-availability-info">
            <p>💡 Preparers can set their availability from their own dashboard. As admin, you can view and manage all availability schedules below.</p>
          </div>
        </div>
        )}

        {/* Empty State for Availability - Owner Only (admin uses CompanyAdminDashboard) */}
        {!isClientView && activeTab === 'availability' && !isPreparerView && !(isAdmin && currentCompany) && Object.keys(preparerAvailability).length === 0 && (
          <div className="empty-state availability-empty">
            <span className="empty-icon">📅</span>
            <h3>No Availability Set</h3>
            <p>No preparers have set their availability yet. Preparers can set their availability from their dashboard.</p>
          </div>
        )}
        
        {!isClientView && activeTab === 'availability' && !isPreparerView && !(isAdmin && currentCompany) && Object.keys(preparerAvailability).length > 0 && (
        <div className="availability-schedule-section" style={{padding: '0'}}>
          {/* Beautiful Card-Based Availability View */}
          {(() => {
            const today = new Date();
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            
            // Group availability by preparer
            const availabilityByPreparer = {};
            Object.entries(preparerAvailability).forEach(([key, slots]) => {
              const [preparerName, date] = key.split('_');
              if (!availabilityByPreparer[preparerName]) {
                availabilityByPreparer[preparerName] = [];
              }
              availabilityByPreparer[preparerName].push({ date, slots, key });
            });

            // Get preparer info
            const getPreparerInfo = (name) => preparers.find(p => p.name === name) || { name, photo: null, active: true };

            return (
              <div style={{padding: '20px 0'}}>
                {/* Header */}
                <div style={{
                  marginBottom: '25px',
                  background: 'linear-gradient(135deg, #0a2540 0%, #134e5e 50%, #1a6b3d 100%)',
                  borderRadius: '16px',
                  padding: '25px',
                  color: 'white'
                }}>
                  <h2 style={{margin: '0 0 10px 0', fontSize: '1.5rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px'}}>
                    📅 Preparer Availability Schedule
                  </h2>
                  <p style={{margin: 0, opacity: 0.9, fontSize: '0.95rem'}}>View and manage all preparer availability from one place</p>
                  
                  {/* Stats Row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                    gap: '15px',
                    marginTop: '20px'
                  }}>
                    <div style={{textAlign: 'center', padding: '15px', background: 'rgba(255,255,255,0.15)', borderRadius: '12px'}}>
                      <div style={{fontSize: '1.8rem', fontWeight: '700'}}>{Object.keys(availabilityByPreparer).length}</div>
                      <div style={{fontSize: '0.8rem', opacity: 0.9}}>Preparers</div>
                    </div>
                    <div style={{textAlign: 'center', padding: '15px', background: 'rgba(255,255,255,0.15)', borderRadius: '12px'}}>
                      <div style={{fontSize: '1.8rem', fontWeight: '700'}}>
                        {Object.keys(preparerAvailability).filter(key => key.split('_')[1] >= todayStr).length}
                      </div>
                      <div style={{fontSize: '0.8rem', opacity: 0.9}}>Upcoming Days</div>
                    </div>
                    <div style={{textAlign: 'center', padding: '15px', background: 'rgba(255,255,255,0.15)', borderRadius: '12px'}}>
                      <div style={{fontSize: '1.8rem', fontWeight: '700'}}>
                        {Object.values(preparerAvailability).reduce((total, slots) => total + (slots?.length || 0), 0)}
                      </div>
                      <div style={{fontSize: '0.8rem', opacity: 0.9}}>Total Slots</div>
                    </div>
                  </div>
                </div>

                {/* Preparer Cards Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
                  gap: '20px'
                }}>
                  {Object.entries(availabilityByPreparer).map(([preparerName, dates]) => {
                    const prepInfo = getPreparerInfo(preparerName);
                    const sortedDates = dates.sort((a, b) => a.date.localeCompare(b.date));
                    const upcomingDates = sortedDates.filter(d => d.date >= todayStr);
                    const pastDates = sortedDates.filter(d => d.date < todayStr);
                    const totalSlots = dates.reduce((sum, d) => sum + d.slots.length, 0);

                    return (
                      <div key={preparerName} style={{
                        background: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden'
                      }}>
                        {/* Preparer Header */}
                        <div style={{
                          padding: '20px',
                          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                          borderBottom: '1px solid #e2e8f0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '15px'
                        }}>
                          <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            background: prepInfo.photo ? 'none' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0,
                            border: '3px solid white',
                            boxShadow: '0 3px 10px rgba(0,0,0,0.1)'
                          }}>
                            {prepInfo.photo ? (
                              <img src={prepInfo.photo} alt={preparerName} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                            ) : (
                              <span style={{color: 'white', fontSize: '24px'}}>[U]</span>
                            )}
                          </div>
                          <div style={{flex: 1}}>
                            <h3 style={{margin: 0, fontSize: '1.15rem', fontWeight: '600', color: '#1e293b'}}>{preparerName}</h3>
                            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px', flexWrap: 'wrap'}}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                fontSize: '0.8rem',
                                color: prepInfo.active !== false ? '#10b981' : '#ef4444'
                              }}>
                                <span style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  background: prepInfo.active !== false ? '#10b981' : '#ef4444'
                                }}></span>
                                {prepInfo.active !== false ? 'Active' : 'Inactive'}
                              </span>
                              <span style={{
                                background: '#dbeafe',
                                color: '#1d4ed8',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                {totalSlots} slots
                              </span>
                              <span style={{
                                background: upcomingDates.length > 0 ? '#dcfce7' : '#fef3c7',
                                color: upcomingDates.length > 0 ? '#166534' : '#92400e',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                {upcomingDates.length} upcoming
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Dates List */}
                        <div style={{maxHeight: '350px', overflowY: 'auto'}}>
                          {upcomingDates.length === 0 && pastDates.length === 0 ? (
                            <div style={{padding: '40px 20px', textAlign: 'center', color: '#94a3b8'}}>
                              <div style={{fontSize: '40px', marginBottom: '10px'}}>[scope]</div>
                              <p style={{margin: 0}}>No availability set</p>
                            </div>
                          ) : (
                            <>
                              {/* Upcoming Dates */}
                              {upcomingDates.length > 0 && (
                                <div style={{padding: '15px 20px'}}>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    fontWeight: '700',
                                    color: '#10b981',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    marginBottom: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}>
                                    <span>📅</span> Upcoming ({upcomingDates.length})
                                  </div>
                                  {upcomingDates.slice(0, 5).map(({date, slots, key}) => {
                                    const dateObj = new Date(date + 'T00:00:00');
                                    const isToday = date === todayStr;
                                    const dayName = isToday ? 'Today' : dateObj.toLocaleDateString('en-US', {weekday: 'short'});
                                    const formattedDate = dateObj.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
                                    
                                    return (
                                      <div key={key} style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                        padding: '12px',
                                        background: isToday ? '#f0fdf4' : '#f8fafc',
                                        borderRadius: '12px',
                                        marginBottom: '10px',
                                        border: isToday ? '2px solid #86efac' : '1px solid #e2e8f0'
                                      }}>
                                        <div style={{
                                          minWidth: '65px',
                                          textAlign: 'center',
                                          background: isToday ? '#22c55e' : '#6366f1',
                                          color: 'white',
                                          borderRadius: '10px',
                                          padding: '8px 10px'
                                        }}>
                                          <div style={{fontSize: '0.65rem', fontWeight: '500', opacity: 0.9, textTransform: 'uppercase'}}>{dayName}</div>
                                          <div style={{fontSize: '0.85rem', fontWeight: '700'}}>{formattedDate}</div>
                                        </div>
                                        
                                        <div style={{flex: 1}}>
                                          <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
                                            {slots.sort().slice(0, 6).map((time, idx) => (
                                              <span key={idx} style={{
                                                background: 'white',
                                                border: '1px solid #e2e8f0',
                                                padding: '4px 8px',
                                                borderRadius: '6px',
                                                fontSize: '0.75rem',
                                                color: '#475569',
                                                fontWeight: '500'
                                              }}>
                                                {convert24to12Hour(time)}
                                              </span>
                                            ))}
                                            {slots.length > 6 && (
                                              <span style={{
                                                background: '#f1f5f9',
                                                padding: '4px 8px',
                                                borderRadius: '6px',
                                                fontSize: '0.75rem',
                                                color: '#64748b',
                                                fontWeight: '500'
                                              }}>
                                                +{slots.length - 6} more
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        
                                        <button
                                          onClick={() => handleDeleteAvailability(preparerName, date)}
                                          style={{
                                            background: '#fee2e2',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '6px 10px',
                                            cursor: 'pointer',
                                            fontSize: '0.75rem',
                                            color: '#dc2626',
                                            fontWeight: '500',
                                            transition: 'all 0.2s'
                                          }}
                                          title="Delete this date"
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    );
                                  })}
                                  {upcomingDates.length > 5 && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      color: '#6366f1',
                                      fontSize: '0.85rem',
                                      fontWeight: '500'
                                    }}>
                                      + {upcomingDates.length - 5} more upcoming dates
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Past Dates (Collapsed) */}
                              {pastDates.length > 0 && (
                                <div style={{
                                  padding: '15px 20px',
                                  borderTop: '1px solid #e2e8f0',
                                  background: '#fafafa'
                                }}>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    fontWeight: '700',
                                    color: '#94a3b8',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                  }}>
                                    <span>📅 Past ({pastDates.length})</span>
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Delete ALL ${pastDates.length} past date(s) for ${preparerName}?`)) {
                                          pastDates.forEach(({key}) => {
                                            const [prep, dt] = key.split('_');
                                            handleDeleteAvailability(prep, dt);
                                          });
                                        }
                                      }}
                                      style={{
                                        background: '#fee2e2',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '4px 10px',
                                        cursor: 'pointer',
                                        fontSize: '0.7rem',
                                        color: '#dc2626',
                                        fontWeight: '600'
                                      }}
                                    >
                                      Delete All Past
                                    </button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
        )}

        {/* Appointments Table - Show for preparer only (Admin uses CompanyAdminDashboard) */}
        {!isClientView && activeTab === 'appointments' && isPreparerView && (
        <div className="table-section">
          {/* Appointment Filter Tabs - Consolidated view */}
          <div className="appointment-filter-tabs" style={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: '8px', 
            marginBottom: '20px', 
            background: '#f1f5f9', 
            padding: '8px', 
            borderRadius: '12px',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            <button 
              onClick={() => setAppointmentFilterTab('today')}
              className="filter-tab-btn"
              style={{
                flex: '1 1 auto',
                minWidth: '100px',
                padding: '12px 16px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.85rem',
                transition: 'all 0.2s ease',
                background: appointmentFilterTab === 'today' ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' : 'transparent',
                color: appointmentFilterTab === 'today' ? 'white' : '#64748b',
                boxShadow: appointmentFilterTab === 'today' ? '0 2px 8px rgba(139, 92, 246, 0.3)' : 'none',
                whiteSpace: 'nowrap'
              }}
            >
              📅 Today ({isPreparerView ? appointments.filter(apt => apt.preparer === loggedInPreparer?.name && apt.date === getTodayString()).length : appointments.filter(apt => apt.date === getTodayString()).length})
            </button>
            <button 
              onClick={() => setAppointmentFilterTab('all')}
              className="filter-tab-btn"
              style={{
                flex: '1 1 auto',
                minWidth: '100px',
                padding: '12px 16px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.85rem',
                transition: 'all 0.2s ease',
                background: appointmentFilterTab === 'all' ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' : 'transparent',
                color: appointmentFilterTab === 'all' ? 'white' : '#64748b',
                boxShadow: appointmentFilterTab === 'all' ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none',
                whiteSpace: 'nowrap'
              }}
            >
              📊 All ({isPreparerView ? appointments.filter(apt => apt.preparer === loggedInPreparer?.name).length : appointments.length})
            </button>
            <button 
              onClick={() => setAppointmentFilterTab('confirmed')}
              className="filter-tab-btn"
              style={{
                flex: '1 1 auto',
                minWidth: '120px',
                padding: '12px 16px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.85rem',
                transition: 'all 0.2s ease',
                background: appointmentFilterTab === 'confirmed' ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'transparent',
                color: appointmentFilterTab === 'confirmed' ? 'white' : '#64748b',
                boxShadow: appointmentFilterTab === 'confirmed' ? '0 2px 8px rgba(34, 197, 94, 0.3)' : 'none',
                whiteSpace: 'nowrap'
              }}
            >
              ✅ Upcoming ({isPreparerView ? appointments.filter(apt => apt.preparer === loggedInPreparer?.name && apt.confirmed).length : appointments.filter(apt => apt.confirmed).length})
            </button>
            <button 
              onClick={() => setAppointmentFilterTab('notConfirmed')}
              className="filter-tab-btn"
              style={{
                flex: '1 1 auto',
                minWidth: '120px',
                padding: '12px 16px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.85rem',
                transition: 'all 0.2s ease',
                background: appointmentFilterTab === 'notConfirmed' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'transparent',
                color: appointmentFilterTab === 'notConfirmed' ? 'white' : '#64748b',
                boxShadow: appointmentFilterTab === 'notConfirmed' ? '0 2px 8px rgba(245, 158, 11, 0.3)' : 'none',
                whiteSpace: 'nowrap'
              }}
            >
              ⏳ Pending ({isPreparerView ? appointments.filter(apt => apt.preparer === loggedInPreparer?.name && !apt.confirmed).length : appointments.filter(apt => !apt.confirmed).length})
            </button>
          </div>

          {/* 7-Day Upcoming Schedule - Today first, then next 6 days */}
          {(() => {
            const now = new Date();
            const weekDays = [];
            for (let i = 0; i < 7; i++) {
              const d = new Date(now);
              d.setDate(now.getDate() + i);
              const dateStr = toEasternInputDate(d);
              const baseAppts = isPreparerView ? appointments.filter(apt => apt.preparer === loggedInPreparer?.name) : appointments;
              const dayAppts = baseAppts.filter(apt => apt.date === dateStr && apt.status !== 'cancelled');
              const fileCount = dayAppts.length;
              const clientCount = dayAppts.reduce((sum, apt) => sum + (parseInt(apt.numberOfClients) || 1), 0);
              const easternDay = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short' });
              weekDays.push({ dayName: i === 0 ? 'Today' : easternDay, date: d, dateStr, fileCount, clientCount, isToday: i === 0 });
            }
            return (
              <div style={{
                marginBottom: '20px',
                background: 'linear-gradient(135deg, #f8fafc, #eef2ff)',
                borderRadius: '16px',
                padding: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', color: '#334155', fontWeight: '700' }}>
                    📅 Upcoming 7 Days
                  </h3>
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: '500' }}>
                    {weekDays[0].date.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })} - {weekDays[6].date.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '8px'
                }}>
                  {weekDays.map((day, idx) => {
                    const isSelected = appointmentSearchDate === day.dateStr;
                    const isActive = day.isToday || isSelected;
                    return (
                      <div
                        key={idx}
                        onClick={() => {
                          if (appointmentSearchDate === day.dateStr) {
                            setAppointmentSearchDate('');
                          } else {
                            setAppointmentSearchDate(day.dateStr);
                          }
                          setAppointmentFilterTab('all');
                          setAppointmentCurrentPage(1);
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          padding: '10px 6px',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          background: day.isToday && !isSelected
                            ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
                            : isSelected
                              ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)'
                              : 'white',
                          color: isActive ? 'white' : '#334155',
                          border: day.isToday && !isSelected ? '2px solid #2563eb' : isSelected ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                          boxShadow: day.isToday ? '0 4px 12px rgba(59, 130, 246, 0.3)' : isSelected ? '0 4px 12px rgba(124, 58, 237, 0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
                          position: 'relative'
                        }}
                      >
                        <span style={{
                          fontSize: day.isToday ? '0.65rem' : '0.7rem',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '4px',
                          opacity: isActive ? 1 : 0.7
                        }}>
                          {day.dayName}
                        </span>
                        <span style={{
                          fontSize: '1.3rem',
                          fontWeight: '800',
                          lineHeight: 1,
                          marginBottom: '2px'
                        }}>
                          {parseInt(day.dateStr.split('-')[2])}
                        </span>
                        <span style={{
                          fontSize: '0.65rem',
                          color: isActive ? 'rgba(255,255,255,0.8)' : '#94a3b8',
                          marginBottom: '4px'
                        }}>
                          {day.date.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short' })}
                        </span>
                        <span style={{
                          fontSize: '0.65rem',
                          fontWeight: '600',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          background: isActive
                            ? 'rgba(255,255,255,0.25)'
                            : day.clientCount > 0 ? 'linear-gradient(135deg, #dbeafe, #ede9fe)' : '#f1f5f9',
                          color: isActive ? 'white' : day.clientCount > 0 ? '#3b82f6' : '#94a3b8',
                          marginBottom: '2px'
                        }}>
                          {day.clientCount} appt{day.clientCount !== 1 ? 's' : ''}
                        </span>
                        {day.isToday && !isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background: '#22c55e',
                            border: '2px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Search and Filter Bar for Admin/Preparer Appointments */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            marginBottom: '20px', 
            flexWrap: 'wrap',
            padding: '16px',
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', marginBottom: '6px', fontWeight: '600' }}>
                🔍 Search by Name/Email/Phone
              </label>
              <input
                type="text"
                placeholder="Search client name, email or phone..."
                value={appointmentSearchName}
                onChange={(e) => { setAppointmentSearchName(e.target.value); setAppointmentCurrentPage(1); }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  transition: 'border-color 0.2s'
                }}
              />
            </div>
            <div style={{ minWidth: '180px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', marginBottom: '6px', fontWeight: '600' }}>
                📅 Filter by Date
              </label>
              <input
                type="date"
                value={appointmentSearchDate}
                onChange={(e) => { setAppointmentSearchDate(e.target.value); setAppointmentCurrentPage(1); }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '0.95rem'
                }}
              />
            </div>
            {(appointmentSearchName || appointmentSearchDate) && (
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  onClick={() => { setAppointmentSearchName(''); setAppointmentSearchDate(''); setAppointmentCurrentPage(1); }}
                  style={{
                    padding: '10px 16px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}
                >
                  ✕ Clear Filters
                </button>
              </div>
            )}
          </div>

          {/* All Appointments - Card Based Layout */}
          <div className="appointments-section-modern">
            <div className="appointments-header-modern">
              <div className="header-left">
                <h2>📋 {appointmentFilterTab === 'today' ? "Today's Appointments" : appointmentFilterTab === 'all' ? 'All Appointments' : appointmentFilterTab === 'confirmed' ? 'Upcoming Appointments' : 'Pending Appointments'}</h2>
                <span className="appointment-count">
                  {(() => {
                    const baseAppts = isPreparerView ? appointments.filter(apt => apt.preparer === loggedInPreparer?.name) : appointments;
                    const today = getTodayString();
                    if (appointmentFilterTab === 'today') return baseAppts.filter(apt => apt.date === today).length;
                    if (appointmentFilterTab === 'confirmed') return baseAppts.filter(apt => apt.confirmed).length;
                    if (appointmentFilterTab === 'notConfirmed') return baseAppts.filter(apt => !apt.confirmed).length;
                    return baseAppts.length;
                  })()} total
                </span>
              </div>
              
              {/* View Toggle & Actions */}
              <div className="header-right-actions">
                {/* View Mode Toggle */}
                <div className="view-toggle-btns">
                  <button 
                    className={`view-toggle-btn ${appointmentViewMode === 'card' ? 'active' : ''}`}
                    onClick={() => setAppointmentViewMode('card')}
                    title="Card View"
                  >
                    💳 Cards
                  </button>
                  <button 
                    className={`view-toggle-btn ${appointmentViewMode === 'table' ? 'active' : ''}`}
                    onClick={() => setAppointmentViewMode('table')}
                    title="Table View"
                  >
                    📊 Table
                  </button>
                </div>

                {/* Email Reminder Actions */}
                {emailReminderSelectedIds.length > 0 && (
                  <button 
                    className="btn btn-email-reminder"
                    onClick={() => setShowEmailReminderModal(true)}
                  >
                    📧 Send Reminder ({emailReminderSelectedIds.length})
                  </button>
                )}
              </div>
            </div>
            
            {/* Bulk Actions for Admin */}
            {!isPreparerView && selectedIds.length > 0 && (
              <div className="bulk-actions-bar">
                <span className="bulk-selected-count">{selectedIds.length} selected</span>
                <button onClick={handleBulkDelete} className="btn btn-danger btn-sm">
                  🗑️ Delete Selected
                </button>
                <button onClick={handleConfirm} className="btn btn-success btn-sm">
                  ✅ Confirm Selected
                </button>
              </div>
            )}

            {(() => {
              // Filter appointments based on preparer, selected tab, and search filters
              const getFilteredAppointments = () => {
                let filtered = isPreparerView ? appointments.filter(apt => apt.preparer === loggedInPreparer?.name) : appointments;
                const today = getTodayString();
                
                if (appointmentFilterTab === 'today') {
                  filtered = filtered.filter(apt => apt.date === today);
                } else if (appointmentFilterTab === 'confirmed') {
                  filtered = filtered.filter(apt => apt.confirmed);
                } else if (appointmentFilterTab === 'notConfirmed') {
                  filtered = filtered.filter(apt => !apt.confirmed);
                }
                // Apply search filters
                if (appointmentSearchName) {
                  const searchLower = appointmentSearchName.toLowerCase();
                  filtered = filtered.filter(apt => 
                    apt.fullName?.toLowerCase().includes(searchLower) ||
                    apt.clientName?.toLowerCase().includes(searchLower) ||
                    apt.email?.toLowerCase().includes(searchLower) ||
                    apt.phoneNumber?.toLowerCase().includes(searchLower)
                  );
                }
                if (appointmentSearchDate) {
                  filtered = filtered.filter(apt => apt.date === appointmentSearchDate);
                }
                // Sort by date/time ascending (earliest appointment first - for tracking who's next)
                return filtered.sort((a, b) => {
                  const dateCompare = a.date.localeCompare(b.date);
                  if (dateCompare !== 0) return dateCompare;
                  const timeA = a.timeSlots?.[0] || a.time || '00:00';
                  const timeB = b.timeSlots?.[0] || b.time || '00:00';
                  return timeA.localeCompare(timeB);
                });
              };
              
              const filteredAppointments = getFilteredAppointments();
              
              // Pagination logic
              const totalPages = Math.ceil(filteredAppointments.length / appointmentsPerPage);
              const startIndex = (appointmentCurrentPage - 1) * appointmentsPerPage;
              const endIndex = startIndex + appointmentsPerPage;
              const paginatedAppointments = filteredAppointments.slice(startIndex, endIndex);
              
              return filteredAppointments.length > 0 ? (
              <>
                {/* Sorting Info Banner */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px',
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                  borderRadius: '12px',
                  border: '1px solid #bbf7d0'
                }}>
                  <span style={{ fontWeight: '600', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    📋 {filteredAppointments.length} appointment{filteredAppointments.length !== 1 ? 's' : ''}
                    {filteredAppointments.length > 0 && appointmentCurrentPage === 1 && (
                      <span style={{ 
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)', 
                        color: 'white', 
                        padding: '2px 8px', 
                        borderRadius: '8px', 
                        fontSize: '0.75em',
                        fontWeight: 'bold'
                      }}>
                        ⭐ #{1} is Next
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: '0.85em', color: '#15803d' }}>
                    Sorted by date & time (earliest first)
                  </span>
                </div>
                
                {/* Pagination Info */}
                <div className="pagination-info" style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <span style={{ color: '#64748b', fontSize: '0.9rem' }}>
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredAppointments.length)} of {filteredAppointments.length} appointments
                  </span>
                  {totalPages > 1 && (
                    <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => setAppointmentCurrentPage(1)}
                        disabled={appointmentCurrentPage === 1}
                        className="pagination-btn"
                        style={{
                          padding: '8px 12px',
                          border: 'none',
                          borderRadius: '8px',
                          background: appointmentCurrentPage === 1 ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                          color: appointmentCurrentPage === 1 ? '#94a3b8' : 'white',
                          cursor: appointmentCurrentPage === 1 ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          fontSize: '0.85rem'
                        }}
                      >
                        🌙 First
                      </button>
                      <button
                        onClick={() => setAppointmentCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={appointmentCurrentPage === 1}
                        className="pagination-btn"
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          borderRadius: '8px',
                          background: appointmentCurrentPage === 1 ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                          color: appointmentCurrentPage === 1 ? '#94a3b8' : 'white',
                          cursor: appointmentCurrentPage === 1 ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          fontSize: '0.85rem'
                        }}
                      >
                        ← Prev
                      </button>
                      <span style={{ padding: '8px 16px', background: '#f1f5f9', borderRadius: '8px', fontWeight: '600', color: '#1e293b' }}>
                        Page {appointmentCurrentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setAppointmentCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={appointmentCurrentPage === totalPages}
                        className="pagination-btn"
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          borderRadius: '8px',
                          background: appointmentCurrentPage === totalPages ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                          color: appointmentCurrentPage === totalPages ? '#94a3b8' : 'white',
                          cursor: appointmentCurrentPage === totalPages ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          fontSize: '0.85rem'
                        }}
                      >
                        Next →
                      </button>
                      <button
                        onClick={() => setAppointmentCurrentPage(totalPages)}
                        disabled={appointmentCurrentPage === totalPages}
                        className="pagination-btn"
                        style={{
                          padding: '8px 12px',
                          border: 'none',
                          borderRadius: '8px',
                          background: appointmentCurrentPage === totalPages ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                          color: appointmentCurrentPage === totalPages ? '#94a3b8' : 'white',
                          cursor: appointmentCurrentPage === totalPages ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          fontSize: '0.85rem'
                        }}
                      >
                        Last 🌟
                      </button>
                    </div>
                  )}
                </div>
              
                {/* Card View - Compact */}
                {appointmentViewMode === 'card' && (
                  <div className="appointments-cards-grid compact">
                    {paginatedAppointments
                      .map((appointment, index) => (
                      <div key={appointment.id} className={`appointment-card-compact ${appointment.confirmed ? 'confirmed' : 'pending'} ${emailReminderSelectedIds.includes(appointment.id) ? 'email-selected' : ''}`} style={{ position: 'relative' }}>
                        
                        {/* Queue Position Indicator */}
                        <div style={{
                          position: 'absolute',
                          top: '-10px',
                          left: '-10px',
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: index === 0 && appointmentCurrentPage === 1 ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          zIndex: 10
                        }}>
                          #{startIndex + index + 1}
                        </div>
                        {index === 0 && appointmentCurrentPage === 1 && (
                          <div style={{
                            position: 'absolute',
                            top: '-10px',
                            right: '-10px',
                            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '0.7em',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
                            zIndex: 10
                          }}>
                            ⭐ NEXT UP
                          </div>
                        )}
                        
                        {/* Top Row: Checkbox + Status */}
                        <div className="compact-card-top">
                          <label className="compact-checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
                            <input 
                              type="checkbox"
                              checked={emailReminderSelectedIds.includes(appointment.id)}
                              onChange={() => handleEmailReminderSelect(appointment.id)}
                            />
                            <span className="compact-checkbox-custom"></span>
                          </label>
                          <div className={`compact-status ${appointment.confirmed ? 'confirmed' : 'pending'}`}>
                            {appointment.confirmed ? '✅ Confirmed' : '⏳ Pending'}
                          </div>
                        </div>
                        
                        {/* Main Info */}
                        <div className="compact-card-main">
                          <div className="compact-datetime">
                            <span className="compact-date">{formatDate(appointment.date)}</span>
                            <span className="compact-time">{convert24to12Hour(appointment.time)}</span>
                          </div>
                          <div className="compact-client">
                            <span className="compact-name">[U] {appointment.fullName}</span>
                            <span className="compact-preparer">📋 {appointment.preparer}</span>
                          </div>
                        </div>
                        
                        {/* Contact Info */}
                        <div className="compact-card-contact">
                          {appointment.phoneNumber && (
                            <span className="compact-phone">📞 {appointment.phoneNumber}</span>
                          )}
                          {appointment.email && (
                            <span className="compact-email">📧 {appointment.email}</span>
                          )}
                          {appointment.fillingType && (
                            <span className="compact-filing">🔄 {appointment.fillingType}</span>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="compact-card-actions">
                          <button 
                            className="compact-btn view"
                            onClick={() => setExpandedAppointmentId(appointment.id)}
                            title="View Details"
                          >
                            ⭕ View
                          </button>
                          {!appointment.confirmed && (
                            <button 
                              className="compact-btn confirm"
                              onClick={() => handleConfirmAppointment(appointment)}
                              title="Confirm"
                            >
                              ✅ Confirm
                            </button>
                          )}
                          <button 
                            className="compact-btn delete"
                            onClick={() => handleDelete(appointment.id)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Table View */}
                {appointmentViewMode === 'table' && (
                  <div className="appointments-table-wrapper">
                    <table className="appointments-table-modern">
                      <thead>
                        <tr>
                          <th className="th-select">
                            <input 
                              type="checkbox" 
                              checked={emailReminderSelectedIds.length === paginatedAppointments.length && paginatedAppointments.length > 0}
                              onChange={() => handleSelectAllForEmail(paginatedAppointments)}
                              title="Select all on this page"
                            />
                          </th>
                          <th style={{ width: '50px' }}>#</th>
                          <th>Date</th>
                          <th>Time</th>
                          <th>Client Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Filing Type</th>
                          <th>Preparer</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedAppointments.map((appointment, index) => (
                          <tr key={appointment.id} className={`${appointment.confirmed ? 'row-confirmed' : 'row-pending'} ${emailReminderSelectedIds.includes(appointment.id) ? 'row-email-selected' : ''}`}>
                            <td className="td-select">
                              <input 
                                type="checkbox" 
                                checked={emailReminderSelectedIds.includes(appointment.id)}
                                onChange={() => handleEmailReminderSelect(appointment.id)}
                              />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                background: index === 0 && appointmentCurrentPage === 1 ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.8em'
                              }}>
                                {startIndex + index + 1}
                              </span>
                              {index === 0 && appointmentCurrentPage === 1 && (
                                <span style={{ marginLeft: '4px', fontSize: '0.7em' }}>⭐</span>
                              )}
                            </td>
                            <td className="td-date">{formatDate(appointment.date)}</td>
                            <td className="td-time">{convert24to12Hour(appointment.time)}</td>
                            <td className="td-name">
                              <span className="table-client-name">[U] {appointment.fullName}</span>
                            </td>
                            <td className="td-email">{appointment.email || '-'}</td>
                            <td className="td-phone">{appointment.phoneNumber || '-'}</td>
                            <td className="td-type">{appointment.fillingType || '-'}</td>
                            <td className="td-preparer">
                              <span className="table-preparer-badge">{appointment.preparer}</span>
                            </td>
                            <td className="td-status">
                              <span className={`table-status-badge ${appointment.confirmed ? 'confirmed' : 'pending'}`}>
                                {appointment.confirmed ? '✅ Confirmed' : '⏳ Pending'}
                              </span>
                            </td>
                            <td className="td-actions">
                              <div className="table-actions-btns">
                                {!appointment.confirmed && (
                                  <button 
                                    onClick={() => handleConfirmAppointment(appointment)}
                                    className="table-btn confirm"
                                    title="Confirm"
                                  >
                                    ✅
                                  </button>
                                )}
                                <button 
                                  onClick={() => handleDelete(appointment.id)}
                                  className="table-btn delete"
                                  title="Delete"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Bottom Pagination */}
                {totalPages > 1 && (
                  <div className="pagination-bottom" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setAppointmentCurrentPage(1)}
                      disabled={appointmentCurrentPage === 1}
                      style={{
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: '8px',
                        background: appointmentCurrentPage === 1 ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        color: appointmentCurrentPage === 1 ? '#94a3b8' : 'white',
                        cursor: appointmentCurrentPage === 1 ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}
                    >
                      🌙 First
                    </button>
                    <button
                      onClick={() => setAppointmentCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={appointmentCurrentPage === 1}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '8px',
                        background: appointmentCurrentPage === 1 ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        color: appointmentCurrentPage === 1 ? '#94a3b8' : 'white',
                        cursor: appointmentCurrentPage === 1 ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}
                    >
                      ← Prev
                    </button>
                    <span style={{ padding: '8px 16px', background: '#f1f5f9', borderRadius: '8px', fontWeight: '600', color: '#1e293b' }}>
                      Page {appointmentCurrentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setAppointmentCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={appointmentCurrentPage === totalPages}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '8px',
                        background: appointmentCurrentPage === totalPages ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        color: appointmentCurrentPage === totalPages ? '#94a3b8' : 'white',
                        cursor: appointmentCurrentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}
                    >
                      Next →
                    </button>
                    <button
                      onClick={() => setAppointmentCurrentPage(totalPages)}
                      disabled={appointmentCurrentPage === totalPages}
                      style={{
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: '8px',
                        background: appointmentCurrentPage === totalPages ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        color: appointmentCurrentPage === totalPages ? '#94a3b8' : 'white',
                        cursor: appointmentCurrentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}
                    >
                      Last 🌟
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-appointments-modern">
                <div className="no-data-icon">[scope]</div>
                <h3>{appointmentFilterTab === 'all' ? 'No Appointments Yet' : appointmentFilterTab === 'confirmed' ? 'No Confirmed Appointments' : 'No Pending Appointments'}</h3>
                <p>{appointmentFilterTab === 'all' ? 'When clients book appointments, they will appear here.' : appointmentFilterTab === 'confirmed' ? 'Confirmed appointments will appear here.' : 'Appointments waiting for confirmation will appear here.'}</p>
              </div>
            );
            })()}
          </div>
        </div>
        )}

        {/* Email Reminder Modal */}
        {showEmailReminderModal && (
          <div className="modal-overlay" onClick={() => setShowEmailReminderModal(false)}>
            <div className="email-reminder-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>📧 Send Tax Filing Reminder</h2>
                <button className="modal-close-btn" onClick={() => setShowEmailReminderModal(false)}>x</button>
              </div>
              
              <div className="modal-body">
                <div className="selected-clients-info">
                  <span className="selected-count-badge">
                    {emailReminderSelectedIds.length} client(s) selected
                  </span>
                  <p className="selected-emails">
                    Emails will be sent to: {appointments
                      .filter(apt => emailReminderSelectedIds.includes(apt.id) && apt.email)
                      .map(apt => apt.email)
                      .join(', ') || 'No valid emails'}
                  </p>
                </div>

                <div className="form-group">
                  <label>Email Subject</label>
                  <input 
                    type="text"
                    value={emailReminderSubject}
                    onChange={(e) => setEmailReminderSubject(e.target.value)}
                    placeholder="Tax Filing Reminder"
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Custom Message (Optional)</label>
                  <textarea 
                    value={emailReminderMessage}
                    onChange={(e) => setEmailReminderMessage(e.target.value)}
                    placeholder="Leave blank to use default reminder message with document checklist..."
                    className="form-control"
                    rows={6}
                  />
                </div>

                <div className="default-message-preview">
                  <h4>📝 Default Message Preview:</h4>
                  <div className="message-preview-box">
                    <p>Dear Client,</p>
                    <p>This is a friendly reminder about your upcoming tax filing.</p>
                    <p><strong>Documents to bring:</strong></p>
                    <ul>
                      <li>W-2 forms from all employers</li>
                      <li>1099 forms (if applicable)</li>
                      <li>Social Security cards for all family members</li>
                      <li>Previous year's tax return</li>
                      <li>Any other relevant tax documents</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEmailReminderModal(false);
                    setEmailReminderMessage('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSendEmailReminder}
                  disabled={appointments.filter(apt => emailReminderSelectedIds.includes(apt.id) && apt.email).length === 0}
                >
                  📧 Send Reminder ({appointments.filter(apt => emailReminderSelectedIds.includes(apt.id) && apt.email).length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Appointment Details Modal */}
        {expandedAppointmentId && (() => {
          const apt = appointments.find(a => a.id === expandedAppointmentId);
          if (!apt) return null;
          return (
            <div className="modal-overlay" onClick={() => setExpandedAppointmentId(null)}>
              <div className="view-details-modal" onClick={(e) => e.stopPropagation()}>
                <div className="view-details-header">
                  <h3>📋 Appointment Details</h3>
                  <button className="modal-close-btn" onClick={() => setExpandedAppointmentId(null)}>x</button>
                </div>
                <div className="view-details-body">
                  <div className="view-details-status">
                    <span className={`status-badge-large ${apt.confirmed ? 'confirmed' : 'pending'}`}>
                      {apt.confirmed ? '✅ Confirmed' : '⏳ Pending Confirmation'}
                    </span>
                  </div>
                  <div className="view-details-grid">
                    <div className="detail-item">
                      <label>📅 Date</label>
                      <span>{formatDate(apt.date)}</span>
                    </div>
                    <div className="detail-item">
                      <label>⏰ Time</label>
                      <span>{convert24to12Hour(apt.time)}</span>
                    </div>
                    <div className="detail-item">
                      <label>[U] Client Name</label>
                      <span>{apt.fullName}</span>
                    </div>
                    <div className="detail-item">
                      <label>📧 Email</label>
                      <span>{apt.email || 'Not provided'}</span>
                    </div>
                    <div className="detail-item">
                      <label>📱 Phone</label>
                      <span>{apt.phone || 'Not provided'}</span>
                    </div>
                    <div className="detail-item">
                      <label>🔄 Filing Type</label>
                      <span>{apt.filingType || 'Not specified'}</span>
                    </div>
                    <div className="detail-item">
                      <label>👨‍💼 Preparer</label>
                      <span>{apt.preparer}</span>
                    </div>
                    {apt.notes && (
                      <div className="detail-item full-width">
                        <label>📝 Notes</label>
                        <span>{apt.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="view-details-actions">
                  {!apt.confirmed && (
                    <button 
                      className="btn btn-success"
                      onClick={() => {
                        handleConfirmAppointment(apt);
                        setExpandedAppointmentId(null);
                      }}
                    >
                      ✅ Confirm Appointment
                    </button>
                  )}
                  <button 
                    className="btn btn-danger"
                    onClick={() => {
                      handleDelete(apt.id);
                      setExpandedAppointmentId(null);
                    }}
                  >
                    🗑️ Delete
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setExpandedAppointmentId(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* PDF Document Builder Modal */}
        {showDocumentBuilder && (
          <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1001, padding: '10px' }}>
            <div style={{ width: '100%', maxWidth: '1400px', height: '95vh', background: 'white', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <PDFDocumentBuilder
                onSave={async (templateData) => {
                  try {
                    await saveDocumentTemplate({
                      ...templateData,
                      preparerId: loggedInPreparer?.id,
                      companyId: currentCompany?.id
                    });
                    setShowDocumentBuilder(false);
                    setEditingTemplate(null);
                    alert('✅ Template saved successfully!');
                  } catch (error) {
                    console.error('Error saving template:', error);
                    alert('❌ Failed to save template. Please try again.');
                  }
                }}
                preparerId={loggedInPreparer?.id}
                companyId={currentCompany?.id}
                existingTemplate={editingTemplate}
                onClose={() => {
                  setShowDocumentBuilder(false);
                  setEditingTemplate(null);
                }}
              />
            </div>
          </div>
        )}

        {/* Send Document Modal */}
        {showSendDocumentModal && selectedTemplate && (
          <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '15px', overflowY: 'auto' }}>
            <div style={{ maxWidth: '600px', width: '100%', background: 'white', borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>📧 Send Document for Signature</h2>
                <button 
                  onClick={() => {
                    setShowSendDocumentModal(false);
                    setSelectedTemplate(null);
                    setDocumentSendForm({ clientName: '', clientEmail: '', clientPhone: '' });
                    setPreparerFieldValues({});
                  }} 
                  style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '30px', height: '30px', borderRadius: '50%', fontSize: '1.1rem', cursor: 'pointer' }}
                >x</button>
              </div>
              
              <div style={{ padding: '20px', overflowY: 'auto' }}>
                {/* Template info */}
                <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '10px', padding: '12px 15px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '40px', height: '50px', background: '#dc2626', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}>
                      PDF
                    </div>
                    <div>
                      <p style={{ margin: '0 0 3px 0', fontWeight: '600', color: '#6b21a8' }}>{selectedTemplate.name}</p>
                      <div style={{ display: 'flex', gap: '10px', fontSize: '0.75rem' }}>
                        <span style={{ padding: '2px 8px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '4px' }}>
                          [U] Client: {selectedTemplate.fields?.filter(f => f.filledBy !== 'preparer').length || 0}
                        </span>
                        <span style={{ padding: '2px 8px', background: '#f5f3ff', color: '#6d28d9', borderRadius: '4px' }}>
                          📋 Preparer: {selectedTemplate.fields?.filter(f => f.filledBy === 'preparer').length || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preparer Fields Section */}
                {selectedTemplate.fields?.filter(f => f.filledBy === 'preparer').length > 0 && (
                  <div style={{ marginBottom: '20px', padding: '15px', background: '#f5f3ff', border: '1px solid #e9d5ff', borderRadius: '10px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#6b21a8', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      📋 Preparer Fields (Fill before sending)
                    </h4>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {selectedTemplate.fields?.filter(f => f.filledBy === 'preparer').map(field => (
                        <div key={field.id}>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#4c1d95', fontSize: '0.85rem' }}>
                            {field.label} {field.required && <span style={{ color: '#dc2626' }}>*</span>}
                          </label>
                          {field.type === 'checkbox' ? (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={preparerFieldValues[field.id] || false}
                                onChange={(e) => setPreparerFieldValues(prev => ({ ...prev, [field.id]: e.target.checked }))}
                              />
                              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Check if applicable</span>
                            </label>
                          ) : field.type === 'date' ? (
                            <input
                              type="date"
                              value={preparerFieldValues[field.id] || ''}
                              onChange={(e) => setPreparerFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                              required={field.required}
                              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #c4b5fd', fontSize: '0.9rem', boxSizing: 'border-box' }}
                            />
                          ) : field.type === 'signature' || field.type === 'initials' ? (
                            <input
                              type="text"
                              value={preparerFieldValues[field.id] || ''}
                              onChange={(e) => setPreparerFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                              placeholder={field.type === 'signature' ? 'Type your signature' : 'Type your initials'}
                              required={field.required}
                              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #c4b5fd', fontSize: '0.9rem', boxSizing: 'border-box', fontFamily: 'cursive' }}
                            />
                          ) : (
                            <input
                              type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                              value={preparerFieldValues[field.id] || ''}
                              onChange={(e) => setPreparerFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                              required={field.required}
                              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #c4b5fd', fontSize: '0.9rem', boxSizing: 'border-box' }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Client Info Section */}
                <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#1d4ed8', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    [U] Send To (Client Information)
                  </h4>
                </div>

                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!documentSendForm.clientName || !documentSendForm.clientEmail) {
                    alert('Please enter client name and email');
                    return;
                  }
                  
                  // Check if all required preparer fields are filled
                  const preparerFields = selectedTemplate.fields?.filter(f => f.filledBy === 'preparer') || [];
                  const missingPreparerFields = preparerFields.filter(f => f.required && !preparerFieldValues[f.id]);
                  if (missingPreparerFields.length > 0) {
                    alert(`Please fill all required preparer fields: ${missingPreparerFields.map(f => f.label).join(', ')}`);
                    return;
                  }
                  
                  setSendingDocument(true);
                  try {
                    const request = await createDocumentRequest({
                      templateId: selectedTemplate.id,
                      templateName: selectedTemplate.name,
                      templateData: selectedTemplate,
                      clientName: documentSendForm.clientName,
                      clientEmail: documentSendForm.clientEmail,
                      clientPhone: documentSendForm.clientPhone,
                      preparerId: loggedInPreparer?.id,
                      preparerName: loggedInPreparer?.name,
                      companyId: currentCompany?.id,
                      companyName: currentCompany?.name,
                      preparerFieldValues: preparerFieldValues // Include preparer's filled values
                    });
                    
                    // Send email with the document link
                    const documentLink = `${window.location.origin}/document?id=${request.id}`;
                    await sendConsentEmail(
                      documentSendForm.clientEmail,
                      documentSendForm.clientName,
                      `Action Required: Please Sign ${selectedTemplate.name} - ${currentCompany?.name || 'Tax Services'}`,
                      { 
                        preparerName: loggedInPreparer?.name, 
                        consentLink: documentLink,
                        taxYear: getEasternYear()
                      },
                      { name: currentCompany?.name, email: currentCompany?.email, phone: currentCompany?.phone }
                    );
                    
                    alert('✅ Document request sent successfully!');
                    setShowSendDocumentModal(false);
                    setSelectedTemplate(null);
                    setDocumentSendForm({ clientName: '', clientEmail: '', clientPhone: '' });
                    setPreparerFieldValues({});
                  } catch (error) {
                    console.error('Error sending document:', error);
                    alert('❌ Failed to send document. Please try again.');
                  } finally {
                    setSendingDocument(false);
                  }
                }}>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>Client Full Name *</label>
                    <input 
                      type="text" 
                      value={documentSendForm.clientName} 
                      onChange={(e) => setDocumentSendForm(prev => ({ ...prev, clientName: e.target.value }))} 
                      placeholder="John Smith" 
                      required 
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', boxSizing: 'border-box' }} 
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>Client Email *</label>
                    <input 
                      type="email" 
                      value={documentSendForm.clientEmail} 
                      onChange={(e) => setDocumentSendForm(prev => ({ ...prev, clientEmail: e.target.value }))} 
                      placeholder="john@email.com" 
                      required 
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', boxSizing: 'border-box' }} 
                    />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>Phone (Optional)</label>
                    <input 
                      type="tel" 
                      value={documentSendForm.clientPhone} 
                      onChange={(e) => setDocumentSendForm(prev => ({ ...prev, clientPhone: e.target.value }))} 
                      placeholder="(555) 123-4567" 
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', boxSizing: 'border-box' }} 
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button 
                      type="button" 
                      onClick={() => {
                        setShowSendDocumentModal(false);
                        setSelectedTemplate(null);
                        setDocumentSendForm({ clientName: '', clientEmail: '', clientPhone: '' });
                        setPreparerFieldValues({});
                      }}
                      style={{ padding: '10px 20px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer' }}
                    >Cancel</button>
                    <button 
                      type="submit" 
                      disabled={sendingDocument} 
                      style={{ 
                        padding: '10px 24px', 
                        background: sendingDocument ? '#94a3b8' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '8px', 
                        fontSize: '0.9rem', 
                        fontWeight: '600', 
                        cursor: sendingDocument ? 'not-allowed' : 'pointer' 
                      }}
                    >
                      {sendingDocument ? '⏳ Sending...' : '📧 Send Document'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Send Consent Request Modal - Enhanced UI */}
        {showSendConsentModal && (
          <div className="modal-overlay" style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(15, 23, 42, 0.8)', 
            backdropFilter: 'blur(8px)',
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            zIndex: 1000, 
            padding: '20px' 
          }}>
            <div style={{ 
              maxWidth: '520px', 
              width: '100%', 
              background: 'white', 
              borderRadius: '24px', 
              boxShadow: '0 25px 80px rgba(0,0,0,0.3)', 
              overflow: 'hidden'
            }}>
              {/* Header */}
              <div style={{ 
                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)', 
                padding: '28px 30px', 
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Background decoration */}
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  right: '-20%',
                  width: '200px',
                  height: '200px',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                  borderRadius: '50%'
                }}></div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ 
                      width: '56px', 
                      height: '56px', 
                      background: 'rgba(255,255,255,0.2)', 
                      backdropFilter: 'blur(10px)',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '28px'
                    }}>
                      📧
                    </div>
                    <div>
                      <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem', fontWeight: '700' }}>Send Form to Client</h2>
                      <p style={{ margin: '6px 0 0 0', color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>Email a secure signing link</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowSendConsentModal(false)} 
                    style={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      border: 'none', 
                      color: 'white', 
                      width: '36px', 
                      height: '36px', 
                      borderRadius: '50%', 
                      fontSize: '1.2rem', 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div style={{ padding: '28px 30px' }}>
                {/* Sender Info Card */}
                <div style={{ 
                  background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', 
                  border: '1px solid #c4b5fd', 
                  borderRadius: '16px', 
                  padding: '18px 20px', 
                  marginBottom: '24px' 
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ 
                      width: '48px', 
                      height: '48px', 
                      background: 'linear-gradient(135deg, #7c3aed, #a855f7)', 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      color: 'white', 
                      fontWeight: 'bold',
                      fontSize: '1.2rem',
                      boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
                    }}>
                      {loggedInPreparer?.name?.charAt(0) || 'P'}
                    </div>
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', color: '#5b21b6', fontSize: '1rem' }}>
                        {loggedInPreparer?.name || 'Tax Preparer'}
                      </p>
                      <p style={{ margin: '2px 0 0 0', fontSize: '0.85rem', color: '#7c3aed' }}>
                        {currentCompany?.name || 'Tax Services'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Info Banner */}
                <div style={{ 
                  background: 'linear-gradient(135deg, #f0fdfa, #ccfbf1)', 
                  border: '1px solid #5eead4',
                  borderRadius: '12px', 
                  padding: '14px 18px', 
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '1.4rem' }}>🔒</span>
                  <p style={{ margin: 0, color: '#0f766e', fontSize: '0.9rem', lineHeight: '1.5' }}>
                    Your client will receive a secure link to complete the IRS Waiver form with e-signature
                  </p>
                </div>
                
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!consentForm.taxpayerName || !consentForm.taxpayerEmail) {
                    alert('Please enter taxpayer name and email');
                    return;
                  }
                  setSendingConsent(true);
                  try {
                    const request = await createConsentRequest({
                      taxpayerName: consentForm.taxpayerName,
                      taxpayerEmail: consentForm.taxpayerEmail,
                      taxpayerPhone: consentForm.taxpayerPhone,
                      taxYear: consentForm.taxYear,
                      preparerId: loggedInPreparer?.id,
                      preparerName: loggedInPreparer?.name,
                      companyId: currentCompany?.id,
                      companyName: currentCompany?.name
                    });
                    // Use the new IRS Waiver Intake Form (same as preview) - include taxYear in URL
                    const consentLink = `${window.location.origin}/irs-waiver?company=${currentCompany?.id}&preparer=${loggedInPreparer?.id}&requestId=${request.id}&taxYear=${consentForm.taxYear}`;
                    await sendConsentEmail(
                      consentForm.taxpayerEmail,
                      consentForm.taxpayerName,
                      `Action Required: Tax Consent Form - ${currentCompany?.name || 'Tax Services'}`,
                      { preparerName: loggedInPreparer?.name, consentLink, taxYear: consentForm.taxYear },
                      { name: currentCompany?.name, email: currentCompany?.email, phone: currentCompany?.phone }
                    );
                    alert('✅ Form sent successfully!\n\nYour client will receive an email with the secure link.');
                    setShowSendConsentModal(false);
                    setConsentForm({ taxpayerName: '', taxpayerEmail: '', taxpayerPhone: '', taxYear: getEasternYear() - 1 });
                  } catch (error) {
                    console.error('Error sending consent:', error);
                    alert('❌ Failed to send. Please try again.');
                  } finally {
                    setSendingConsent(false);
                  }
                }}>
                  {/* Name Field */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'flex', 
                      marginBottom: '8px', 
                      fontWeight: '600', 
                      color: '#1e293b', 
                      fontSize: '0.95rem',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>[U]</span> Client's Full Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input 
                      type="text" 
                      value={consentForm.taxpayerName} 
                      onChange={(e) => setConsentForm(prev => ({ ...prev, taxpayerName: e.target.value }))} 
                      placeholder="John Smith" 
                      required 
                      style={{ 
                        width: '100%', 
                        padding: '14px 16px', 
                        borderRadius: '12px', 
                        border: '2px solid #e2e8f0', 
                        fontSize: '1rem', 
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s',
                        outline: 'none'
                      }} 
                    />
                  </div>

                  {/* Email Field */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'flex', 
                      marginBottom: '8px', 
                      fontWeight: '600', 
                      color: '#1e293b', 
                      fontSize: '0.95rem',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>📧</span> Client's Email <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input 
                      type="email" 
                      value={consentForm.taxpayerEmail} 
                      onChange={(e) => setConsentForm(prev => ({ ...prev, taxpayerEmail: e.target.value }))} 
                      placeholder="john@email.com" 
                      required 
                      style={{ 
                        width: '100%', 
                        padding: '14px 16px', 
                        borderRadius: '12px', 
                        border: '2px solid #e2e8f0', 
                        fontSize: '1rem', 
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s',
                        outline: 'none'
                      }} 
                    />
                  </div>

                  {/* Phone and Tax Year Row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '28px' }}>
                    <div>
                      <label style={{ 
                        display: 'flex', 
                        marginBottom: '8px', 
                        fontWeight: '600', 
                        color: '#1e293b', 
                        fontSize: '0.95rem',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span>📱</span> Phone <span style={{ color: '#94a3b8', fontWeight: '400' }}>(Optional)</span>
                      </label>
                      <input 
                        type="tel" 
                        value={consentForm.taxpayerPhone} 
                        onChange={(e) => setConsentForm(prev => ({ ...prev, taxpayerPhone: e.target.value }))} 
                        placeholder="(555) 123-4567" 
                        style={{ 
                          width: '100%', 
                          padding: '14px 16px', 
                          borderRadius: '12px', 
                          border: '2px solid #e2e8f0', 
                          fontSize: '1rem', 
                          boxSizing: 'border-box',
                          outline: 'none'
                        }} 
                      />
                    </div>
                    <div>
                      <label style={{ 
                        display: 'flex', 
                        marginBottom: '8px', 
                        fontWeight: '600', 
                        color: '#1e293b', 
                        fontSize: '0.95rem',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span>📅</span> Tax Year
                      </label>
                      <select 
                        value={consentForm.taxYear} 
                        onChange={(e) => setConsentForm(prev => ({ ...prev, taxYear: parseInt(e.target.value) }))} 
                        style={{ 
                          width: '100%', 
                          padding: '14px 16px', 
                          borderRadius: '12px', 
                          border: '2px solid #e2e8f0', 
                          fontSize: '1rem', 
                          boxSizing: 'border-box',
                          background: 'white',
                          cursor: 'pointer',
                          outline: 'none'
                        }}
                      >
                        {[...Array(5)].map((_, i) => { const year = new Date().getFullYear() - i; return <option key={year} value={year}>{year}</option>; })}
                      </select>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button 
                      type="button" 
                      onClick={() => setShowSendConsentModal(false)} 
                      style={{ 
                        padding: '14px 28px', 
                        background: '#f1f5f9', 
                        color: '#64748b', 
                        border: 'none', 
                        borderRadius: '12px', 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      disabled={sendingConsent} 
                      style={{ 
                        padding: '14px 32px', 
                        background: sendingConsent ? '#94a3b8' : 'linear-gradient(135deg, #7c3aed, #a855f7)', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '12px', 
                        fontSize: '1rem', 
                        fontWeight: '700', 
                        cursor: sendingConsent ? 'not-allowed' : 'pointer',
                        boxShadow: sendingConsent ? 'none' : '0 4px 15px rgba(124, 58, 237, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                    >
                      {sendingConsent ? (
                        <>⏳ Sending...</>
                      ) : (
                        <>📧 Send to Client</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Signed Consent Modal */}
        {viewingConsent && (
          <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '15px', overflowY: 'auto' }}>
            <div style={{ maxWidth: '700px', width: '100%', background: 'white', borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 }}>
                <h2 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>📋 Signed Consent Document</h2>
                <button onClick={() => setViewingConsent(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '30px', height: '30px', borderRadius: '50%', fontSize: '1.1rem', cursor: 'pointer' }}>x</button>
              </div>
              
              <div style={{ padding: '20px' }}>
                {/* Document Sender Info - DocuSign style */}
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '12px 15px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold' }}>
                      {viewingConsent.preparerName?.charAt(0) || '[abc]'}
                    </div>
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', color: '#0c4a6e' }}>Sent by: {viewingConsent.preparerName || 'Tax Preparer'}</p>
                      <p style={{ margin: 0, fontSize: '0.8rem', color: '#0369a1' }}>{viewingConsent.companyName || 'Tax Services'}</p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600' }}>
                      ✅ Completed
                    </span>
                  </div>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '1rem' }}>[U] Taxpayer Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                    <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Name</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxpayerInfo?.name}</span></div>
                    <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Email</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxpayerInfo?.email}</span></div>
                    {viewingConsent.taxpayerInfo?.phone && <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Phone</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxpayerInfo.phone}</span></div>}
                    {viewingConsent.taxpayerInfo?.ssn && <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>SSN (Last 4)</span><span style={{ color: '#1e293b', fontWeight: '500' }}>***-**-{viewingConsent.taxpayerInfo.ssn}</span></div>}
                  </div>
                </div>
                
                <div style={{ background: '#f0fdf4', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#166534', fontSize: '1rem' }}>🔄 Document Details</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                    <div><span style={{ color: '#166534', fontSize: '0.8rem', display: 'block' }}>Tax Year</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxYear}</span></div>
                    <div><span style={{ color: '#166534', fontSize: '0.8rem', display: 'block' }}>Signed Date</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.signedAt ? new Date(viewingConsent.signedAt).toLocaleString() : 'N/A'}</span></div>
                  </div>
                </div>
                
                <div style={{ background: '#eff6ff', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#1d4ed8', fontSize: '1rem' }}>✅ Consents Given</h3>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {viewingConsent.consents?.disclosure && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}><span style={{ color: '#10b981' }}>✅</span><span>IRC \xc2\xa77216 Disclosure Consent</span></div>}
                    {viewingConsent.consents?.useConsent && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}><span style={{ color: '#10b981' }}>✅</span><span>Consent to Use Tax Information</span></div>}
                    {viewingConsent.consents?.efileAuth && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}><span style={{ color: '#10b981' }}>✅</span><span>IRS E-File Authorization</span></div>}
                    {viewingConsent.consents?.incomeWaiver && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}><span style={{ color: '#f59e0b' }}>📝</span><span>Income Documentation Waiver</span></div>}
                    {viewingConsent.consents?.privacyPolicy && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}><span style={{ color: '#10b981' }}>✅</span><span>Privacy Policy Acknowledgment</span></div>}
                    {viewingConsent.consents?.feeAgreement && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}><span style={{ color: '#10b981' }}>💰</span><span>Fee Agreement Accepted</span></div>}
                  </div>
                </div>

                {/* Custom Fields Filled */}
                {viewingConsent.customFields && Object.keys(viewingConsent.customFields).length > 0 && (
                  <div style={{ background: '#faf5ff', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #c4b5fd' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#6d28d9', fontSize: '1rem' }}>📝 Additional Information Provided</h3>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {Object.entries(viewingConsent.customFields).map(([label, fieldData]) => (
                        <div key={label} style={{ background: 'white', padding: '12px', borderRadius: '8px' }}>
                          <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>{label}</span>
                          <span style={{ color: '#1e293b', fontWeight: '500' }}>
                            {fieldData.fieldType === 'checkbox' 
                              ? (fieldData.value ? '✅ Yes' : '✅ No')
                              : fieldData.fieldType === 'currency'
                                ? `$${fieldData.value || '0.00'}`
                                : fieldData.value || 'N/A'
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {viewingConsent.signature && (
                  <div style={{ background: '#fefce8', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '1rem' }}>✏️ Electronic Signature</h3>
                    {viewingConsent.signature.image && <div style={{ background: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #fcd34d', marginBottom: '10px' }}><img src={viewingConsent.signature.image} alt="Signature" style={{ maxWidth: '100%', maxHeight: '100px' }} /></div>}
                    <div><span style={{ color: '#92400e' }}>Typed Name: </span><span style={{ fontWeight: '500' }}>{viewingConsent.signature.typed}</span></div>
                  </div>
                )}

                {/* PDF Template - View/Print/Download */}
                {pdfTemplate && (
                  <div style={{ background: '#f0f9ff', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #0ea5e9' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#0369a1', fontSize: '1rem' }}>🔄 Consent Form PDF</h3>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '40px', height: '50px', background: '#dc2626', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}>PDF</div>
                        <span style={{ color: '#0c4a6e', fontWeight: '500' }}>{pdfTemplate.name}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => window.open(pdfTemplate.data, '_blank')}
                          style={{ padding: '8px 16px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '0.9rem' }}
                        >
                          ⭕ View PDF
                        </button>
                        <button
                          onClick={() => {
                            const printWindow = window.open(pdfTemplate.data, '_blank');
                            printWindow.onload = () => printWindow.print();
                          }}
                          style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '0.9rem' }}
                        >
                          🖨️ Print PDF
                        </button>
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = pdfTemplate.data;
                            link.download = `consent_${viewingConsent.taxpayerInfo?.name?.replace(/\s+/g, '_')}_${viewingConsent.taxYear}.pdf`;
                            link.click();
                          }}
                          style={{ padding: '8px 16px', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '0.9rem' }}
                        >
                          📥 Download
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
                  <button onClick={() => {
                    // Generate filled PDF document for print
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(`
                      <html>
                      <head>
                        <title>Tax Consent - ${viewingConsent.taxpayerInfo?.name}</title>
                        <style>
                          @media print { @page { margin: 0.5in; } }
                          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                          h1 { color: #1d4ed8; border-bottom: 3px solid #3b82f6; padding-bottom: 15px; margin-bottom: 30px; }
                          .header-info { background: #f0f9ff; padding: 20px; border-radius: 10px; margin-bottom: 25px; }
                          .section { margin: 25px 0; padding: 20px; background: #f8fafc; border-radius: 10px; border-left: 4px solid #3b82f6; }
                          .section h2 { color: #1e293b; margin: 0 0 15px 0; font-size: 1.1rem; }
                          .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
                          .info-item label { display: block; font-size: 0.8rem; color: #64748b; margin-bottom: 3px; }
                          .info-item span { font-weight: 600; color: #1e293b; }
                          .consent-item { padding: 10px 15px; background: white; border-radius: 8px; margin: 8px 0; display: flex; align-items: center; gap: 10px; }
                          .consent-item.checked { border-left: 3px solid #10b981; }
                          .signature-box { background: white; padding: 20px; border: 2px solid #fcd34d; border-radius: 10px; text-align: center; }
                          .signature-box img { max-width: 300px; max-height: 100px; }
                          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 0.8rem; color: #64748b; }
                        </style>
                      </head>
                      <body>
                        <h1>📋 Tax Consent Document</h1>
                        
                        <div class="header-info">
                          <strong>Prepared by:</strong> ${viewingConsent.preparerName || 'Tax Preparer'}<br/>
                          <strong>Company:</strong> ${viewingConsent.companyName || 'Tax Services'}<br/>
                          <strong>Tax Year:</strong> ${viewingConsent.taxYear}
                        </div>
                        
                        <div class="section">
                          <h2>[U] Taxpayer Information</h2>
                          <div class="info-grid">
                            <div class="info-item"><label>Full Name</label><span>${viewingConsent.taxpayerInfo?.name || 'N/A'}</span></div>
                            <div class="info-item"><label>Email Address</label><span>${viewingConsent.taxpayerInfo?.email || 'N/A'}</span></div>
                            <div class="info-item"><label>Phone Number</label><span>${viewingConsent.taxpayerInfo?.phone || 'N/A'}</span></div>
                            <div class="info-item"><label>SSN (Last 4)</label><span>***-**-${viewingConsent.taxpayerInfo?.ssn || 'XXXX'}</span></div>
                          </div>
                        </div>
                        
                        <div class="section">
                          <h2>✅ Consents & Authorizations</h2>
                          ${viewingConsent.consents?.disclosure ? '<div class="consent-item checked">✅ IRC \xc2\xa77216 Disclosure Consent</div>' : ''}
                          ${viewingConsent.consents?.useConsent ? '<div class="consent-item checked">✅ Consent to Use Tax Information</div>' : ''}
                          ${viewingConsent.consents?.efileAuth ? '<div class="consent-item checked">✅ IRS E-File Authorization</div>' : ''}
                          ${viewingConsent.consents?.incomeWaiver ? '<div class="consent-item checked">📝 Income Documentation Waiver</div>' : ''}
                          ${viewingConsent.consents?.privacyPolicy ? '<div class="consent-item checked">✅ Privacy Policy Acknowledgment</div>' : ''}
                          ${viewingConsent.consents?.feeAgreement ? '<div class="consent-item checked">💰 Fee Agreement Accepted</div>' : ''}
                        </div>
                        
                        <div class="section">
                          <h2>✏️ Electronic Signature</h2>
                          <div class="signature-box">
                            ${viewingConsent.signature?.image ? `<img src="${viewingConsent.signature.image}" alt="Signature"/>` : ''}
                            <p style="margin: 10px 0 0 0;"><strong>Typed Name:</strong> ${viewingConsent.signature?.typed || 'N/A'}</p>
                            <p style="margin: 5px 0 0 0;"><strong>Signed Date:</strong> ${viewingConsent.signedAt ? new Date(viewingConsent.signedAt).toLocaleString('en-US', { timeZone: 'America/New_York' }) + ' ET' : 'N/A'}</p>
                          </div>
                        </div>
                        
                        <div class="footer">
                          <strong>Document ID:</strong> ${viewingConsent.id}<br/>
                          <strong>Generated:</strong> ${toEasternDateTimeString()}<br/>
                          <em>This document was electronically signed and is legally binding.</em>
                        </div>
                      </body>
                      </html>
                    `);
                    printWindow.document.close();
                    printWindow.print();
                  }} style={{ padding: '10px 20px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>🖨️ Print Summary</button>
                  <button onClick={() => setViewingConsent(null)} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View IRS Waiver Submission Modal */}
        {viewingWaiverSubmission && (
          <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '15px', overflowY: 'auto' }}>
            <div className="modal-content" style={{ maxWidth: '900px', width: '100%', background: 'white', borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ background: viewingWaiverSubmission.status === 'completed' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #f59e0b, #d97706)', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 }}>
                <h2 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>
                  {viewingWaiverSubmission.status === 'completed' ? '✅ Completed IRS Waiver' : '⏳ IRS Waiver - Pending Verification'}
                </h2>
                <button onClick={() => setViewingWaiverSubmission(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '30px', height: '30px', borderRadius: '50%', fontSize: '1.1rem', cursor: 'pointer' }}>x</button>
              </div>
              
              <div style={{ padding: '20px' }}>
                {/* Status Badge */}
                <div style={{ background: viewingWaiverSubmission.status === 'completed' ? '#dcfce7' : '#fef3c7', border: `1px solid ${viewingWaiverSubmission.status === 'completed' ? '#86efac' : '#fcd34d'}`, borderRadius: '10px', padding: '12px 15px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '2rem' }}>{viewingWaiverSubmission.status === 'completed' ? '✅' : '⏳'}</span>
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', color: viewingWaiverSubmission.status === 'completed' ? '#166534' : '#92400e' }}>
                        {viewingWaiverSubmission.status === 'completed' ? 'Verified & Completed' : 'Pending Preparer Verification'}
                      </p>
                      <p style={{ margin: 0, fontSize: '0.8rem', color: viewingWaiverSubmission.status === 'completed' ? '#15803d' : '#b45309' }}>
                        {viewingWaiverSubmission.status === 'completed' 
                          ? `Completed on ${(() => { const v = viewingWaiverSubmission.completedAt || viewingWaiverSubmission.verifiedAt; if (!v) return 'N/A'; const d = v?.toDate ? v.toDate() : new Date(typeof v === 'string' ? v.replace(/ ET$/, '') : v); return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString(); })()}`
                          : 'Fill preparer section below and verify'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* ==================== PREPARER SECTION - Page 1 & 5 Fields ==================== */}
                {viewingWaiverSubmission.status === 'pending_verification' && (
                  <div style={{ background: 'linear-gradient(135deg, #8B5A2B 0%, #A0522D 100%)', padding: '20px', borderRadius: '12px', marginBottom: '20px', color: 'white' }}>
                    <h3 style={{ margin: '0 0 15px 0', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      ✏️ Preparer Section (Page 1 & 5 Fields)
                    </h3>
                    <p style={{ margin: '0 0 15px', fontSize: '0.9rem', opacity: 0.9 }}>
                      Fill in your preparer information below. This will appear on Page 1 (Preparer Name, Signature, Date) and Page 5 (Accepted by).
                    </p>
                    
                    <div style={{ background: 'rgba(255,255,255,0.95)', borderRadius: '10px', padding: '20px' }}>
                      {/* Preparer Name */}
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>
                          [U] Preparer Name (Page 1 & Page 5)
                        </label>
                        <input
                          type="text"
                          value={preparerSectionData.preparerName || loggedInPreparer?.name || ''}
                          onChange={(e) => setPreparerSectionData({...preparerSectionData, preparerName: e.target.value})}
                          placeholder="Enter preparer name"
                          style={{ width: '100%', padding: '12px 15px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '1rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      
                      {/* Date */}
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>
                          📅 Date (Page 1)
                        </label>
                        <input
                          type="date"
                          value={preparerSectionData.preparerSignatureDate || toEasternInputDate()}
                          onChange={(e) => setPreparerSectionData({...preparerSectionData, preparerSignatureDate: e.target.value})}
                          style={{ width: '100%', padding: '12px 15px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '1rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      
                      {/* Preparer Signature */}
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>
                          ✏️ Preparer Signature (Page 1)
                        </label>
                        
                        {/* Saved Signature Option */}
                        {savedPreparerSignature && (
                          <div style={{ marginBottom: '10px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '1.2rem' }}>💾</span>
                                <span style={{ fontWeight: '500', color: '#166534' }}>Saved Signature Available</span>
                              </div>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => setPreparerSectionData({...preparerSectionData, preparerSignatureImage: savedPreparerSignature})}
                                  style={{ padding: '8px 14px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem' }}
                                >
                                  ✅ Use Saved
                                </button>
                                <button
                                  onClick={() => {
                                    if(window.confirm('Clear saved signature?')) {
                                      setSavedPreparerSignature(null);
                                      localStorage.removeItem('savedPreparerSignature');
                                    }
                                  }}
                                  style={{ padding: '8px 14px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem' }}
                                >
                                  🗑️ Clear
                                </button>
                              </div>
                            </div>
                            {savedPreparerSignature && (
                              <div style={{ marginTop: '10px', padding: '10px', background: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                <img src={savedPreparerSignature} alt="Saved Signature" style={{ maxWidth: '200px', maxHeight: '60px' }} />
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Signature Canvas */}
                        <div style={{ border: '2px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: 'white' }}>
                          <canvas
                            ref={preparerSignatureCanvasRef}
                            width={400}
                            height={150}
                            style={{ width: '100%', height: '150px', cursor: 'crosshair', display: 'block' }}
                            onMouseDown={(e) => {
                              setIsDrawingPreparerSignature(true);
                              const canvas = preparerSignatureCanvasRef.current;
                              const ctx = canvas.getContext('2d');
                              const rect = canvas.getBoundingClientRect();
                              ctx.beginPath();
                              ctx.moveTo((e.clientX - rect.left) * (canvas.width / rect.width), (e.clientY - rect.top) * (canvas.height / rect.height));
                            }}
                            onMouseMove={(e) => {
                              if (!isDrawingPreparerSignature) return;
                              const canvas = preparerSignatureCanvasRef.current;
                              const ctx = canvas.getContext('2d');
                              const rect = canvas.getBoundingClientRect();
                              ctx.lineWidth = 2;
                              ctx.lineCap = 'round';
                              ctx.strokeStyle = '#1e293b';
                              ctx.lineTo((e.clientX - rect.left) * (canvas.width / rect.width), (e.clientY - rect.top) * (canvas.height / rect.height));
                              ctx.stroke();
                            }}
                            onMouseUp={() => {
                              setIsDrawingPreparerSignature(false);
                              const canvas = preparerSignatureCanvasRef.current;
                              const signatureData = canvas.toDataURL('image/png');
                              setPreparerSectionData({...preparerSectionData, preparerSignatureImage: signatureData});
                            }}
                            onMouseLeave={() => setIsDrawingPreparerSignature(false)}
                            onTouchStart={(e) => {
                              e.preventDefault();
                              setIsDrawingPreparerSignature(true);
                              const canvas = preparerSignatureCanvasRef.current;
                              const ctx = canvas.getContext('2d');
                              const rect = canvas.getBoundingClientRect();
                              const touch = e.touches[0];
                              ctx.beginPath();
                              ctx.moveTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                            }}
                            onTouchMove={(e) => {
                              e.preventDefault();
                              if (!isDrawingPreparerSignature) return;
                              const canvas = preparerSignatureCanvasRef.current;
                              const ctx = canvas.getContext('2d');
                              const rect = canvas.getBoundingClientRect();
                              const touch = e.touches[0];
                              ctx.lineWidth = 2;
                              ctx.lineCap = 'round';
                              ctx.strokeStyle = '#1e293b';
                              ctx.lineTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                              ctx.stroke();
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              setIsDrawingPreparerSignature(false);
                              const canvas = preparerSignatureCanvasRef.current;
                              const signatureData = canvas.toDataURL('image/png');
                              setPreparerSectionData({...preparerSectionData, preparerSignatureImage: signatureData});
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => {
                              const canvas = preparerSignatureCanvasRef.current;
                              const ctx = canvas.getContext('2d');
                              ctx.clearRect(0, 0, canvas.width, canvas.height);
                              setPreparerSectionData({...preparerSectionData, preparerSignatureImage: ''});
                            }}
                            style={{ padding: '8px 14px', background: '#f1f5f9', color: '#475569', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                          >
                            🗑️ Clear Signature
                          </button>
                          {preparerSectionData.preparerSignatureImage && !savedPreparerSignature && (
                            <button
                              onClick={() => {
                                setSavedPreparerSignature(preparerSectionData.preparerSignatureImage);
                                localStorage.setItem('savedPreparerSignature', JSON.stringify(preparerSectionData.preparerSignatureImage));
                                alert('✅ Signature saved for future use!');
                              }}
                              style={{ padding: '8px 14px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                            >
                              💾 Save for Reuse
                            </button>
                          )}
                        </div>
                        
                        {/* Current Signature Preview */}
                        {preparerSectionData.preparerSignatureImage && (
                          <div style={{ marginTop: '10px', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <p style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#64748b' }}>Current Signature:</p>
                            <img src={preparerSectionData.preparerSignatureImage} alt="Current Signature" style={{ maxWidth: '200px', maxHeight: '60px' }} />
                          </div>
                        )}
                      </div>
                      
                      {/* Additional Preparer Fields (Optional) */}
                      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '15px', marginTop: '15px' }}>
                        <p style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic' }}>Optional Fields:</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#475569' }}>PTIN</label>
                            <input
                              type="text"
                              value={preparerSectionData.ptin || ''}
                              onChange={(e) => setPreparerSectionData({...preparerSectionData, ptin: e.target.value})}
                              placeholder="P00000000"
                              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#475569' }}>Firm Name</label>
                            <input
                              type="text"
                              value={preparerSectionData.firmName || ''}
                              onChange={(e) => setPreparerSectionData({...preparerSectionData, firmName: e.target.value})}
                              placeholder="Tax Services LLC"
                              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Verify Button */}
                    <div style={{ marginTop: '15px', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button
                        onClick={async () => {
                          if (!preparerSectionData.preparerName && !loggedInPreparer?.name) {
                            alert('Please enter preparer name');
                            return;
                          }
                          if (!preparerSectionData.preparerSignatureImage) {
                            alert('Please draw or select your signature');
                            return;
                          }
                          
                          if (window.confirm('Complete verification? Your preparer information will be added to the PDF.')) {
                            try {
                              // Save preparer section data and complete the submission
                              await completeIRSWaiverSubmission(viewingWaiverSubmission.id, {
                                preparerName: preparerSectionData.preparerName || loggedInPreparer?.name,
                                preparerSignatureDate: preparerSectionData.preparerSignatureDate || toEasternInputDate(),
                                preparerSignatureImage: preparerSectionData.preparerSignatureImage,
                                ptin: preparerSectionData.ptin || '',
                                firmName: preparerSectionData.firmName || '',
                                completedById: loggedInPreparer?.id,
                                completedByName: loggedInPreparer?.name,
                                completedAt: toEasternISOString()
                              });
                              alert('✅ Form completed! Preparer section has been filled and the form is now verified.');
                              setViewingWaiverSubmission(null);
                              // Reset preparer section data
                              setPreparerSectionData({
                                preparerName: '',
                                preparerSignature: '',
                                preparerSignatureDate: '',
                                preparerSignatureImage: '',
                                ptin: '',
                                firmName: '',
                                firmAddress: '',
                                firmEIN: '',
                                notes: ''
                              });
                            } catch (error) {
                              console.error('Error completing submission:', error);
                              alert('Failed to complete submission');
                            }
                          }
                        }}
                        style={{ padding: '14px 28px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)' }}
                      >
                        ✅ Complete & Verify Form
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Show Preparer Section Data if Already Completed */}
                {viewingWaiverSubmission.status === 'completed' && viewingWaiverSubmission.preparerSection && (
                  <div style={{ background: '#f0fdf4', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '2px solid #86efac' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#166534', fontSize: '1rem' }}>✏️ Preparer Section (Completed)</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                      <div><span style={{ color: '#22c55e', fontSize: '0.8rem', display: 'block' }}>Preparer Name</span><span style={{ color: '#1e293b', fontWeight: '600' }}>{viewingWaiverSubmission.preparerSection.preparerName}</span></div>
                      <div><span style={{ color: '#22c55e', fontSize: '0.8rem', display: 'block' }}>Date</span><span style={{ color: '#1e293b', fontWeight: '600' }}>{viewingWaiverSubmission.preparerSection.preparerSignatureDate}</span></div>
                      {viewingWaiverSubmission.preparerSection.ptin && <div><span style={{ color: '#22c55e', fontSize: '0.8rem', display: 'block' }}>PTIN</span><span style={{ color: '#1e293b', fontWeight: '600' }}>{viewingWaiverSubmission.preparerSection.ptin}</span></div>}
                      {viewingWaiverSubmission.preparerSection.firmName && <div><span style={{ color: '#22c55e', fontSize: '0.8rem', display: 'block' }}>Firm</span><span style={{ color: '#1e293b', fontWeight: '600' }}>{viewingWaiverSubmission.preparerSection.firmName}</span></div>}
                    </div>
                    {viewingWaiverSubmission.preparerSection.preparerSignatureImage && (
                      <div style={{ marginTop: '12px', padding: '10px', background: 'white', borderRadius: '8px', border: '1px solid #86efac' }}>
                        <p style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#166534', fontWeight: '500' }}>Preparer Signature:</p>
                        <img src={viewingWaiverSubmission.preparerSection.preparerSignatureImage} alt="Preparer Signature" style={{ maxWidth: '200px', maxHeight: '80px' }} />
                      </div>
                    )}
                  </div>
                )}

                {/* Preparer & Client Info Summary */}
                <div style={{ background: '#eff6ff', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #bfdbfe' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                    <div>
                      <p style={{ margin: '0 0 4px 0', color: '#64748b', fontSize: '0.8rem' }}>💼 Assigned Preparer</p>
                      <p style={{ margin: 0, color: '#1d4ed8', fontWeight: '600', fontSize: '1rem' }}>{viewingWaiverSubmission.preparerName || 'Not Assigned'}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 4px 0', color: '#64748b', fontSize: '0.8rem' }}>[U] Client Name</p>
                      <p style={{ margin: 0, color: '#1e293b', fontWeight: '600', fontSize: '1rem' }}>{viewingWaiverSubmission.taxpayerInfo?.name || 'Unknown'}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 4px 0', color: '#64748b', fontSize: '0.8rem' }}>📅 Submitted</p>
                      <p style={{ margin: 0, color: '#1e293b', fontWeight: '500', fontSize: '0.95rem' }}>{(() => {
                        const dateValue = viewingWaiverSubmission.submittedAt;
                        if (!dateValue) return 'N/A';
                        const date = dateValue?.toDate ? dateValue.toDate() : new Date(typeof dateValue === 'string' ? dateValue.replace(/ ET$/, '') : dateValue);
                        if (isNaN(date.getTime())) return 'N/A';
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        return `${day}/${month}/${year}`;
                      })()}</p>
                    </div>
                    {viewingWaiverSubmission.isPublished && (
                      <div>
                        <span style={{ background: '#dcfce7', color: '#166534', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '600' }}>
                          📢 Published to All Preparers
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Taxpayer Information */}
                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '1rem' }}>[U] Taxpayer Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                    <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Name</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.taxpayerInfo?.name}</span></div>
                    <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Email</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.taxpayerInfo?.email}</span></div>
                    {viewingWaiverSubmission.taxpayerInfo?.phone && <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Phone</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.taxpayerInfo.phone}</span></div>}
                    {viewingWaiverSubmission.taxpayerInfo?.ssn && <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>SSN (Last 4)</span><span style={{ color: '#1e293b', fontWeight: '500' }}>***-**-{viewingWaiverSubmission.taxpayerInfo.ssn}</span></div>}
                    {viewingWaiverSubmission.taxpayerInfo?.address && <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Address</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.taxpayerInfo.address}</span></div>}
                    {viewingWaiverSubmission.taxpayerInfo?.dob && <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Date of Birth</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.taxpayerInfo.dob}</span></div>}
                  </div>
                </div>

                {/* Spouse Information (if filing jointly) */}
                {viewingWaiverSubmission.spouseInfo?.name && (
                  <div style={{ background: '#fdf4ff', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #e879f9' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#86198f', fontSize: '1rem' }}>👥 Spouse Information</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                      <div><span style={{ color: '#a855f7', fontSize: '0.8rem', display: 'block' }}>Name</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.spouseInfo.name}</span></div>
                      {viewingWaiverSubmission.spouseInfo?.ssn && <div><span style={{ color: '#a855f7', fontSize: '0.8rem', display: 'block' }}>SSN (Last 4)</span><span style={{ color: '#1e293b', fontWeight: '500' }}>***-**-{viewingWaiverSubmission.spouseInfo.ssn}</span></div>}
                      {viewingWaiverSubmission.spouseInfo?.dob && <div><span style={{ color: '#a855f7', fontSize: '0.8rem', display: 'block' }}>Date of Birth</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.spouseInfo.dob}</span></div>}
                    </div>
                  </div>
                )}

                {/* Care Recipient Information (if applicable) */}
                {viewingWaiverSubmission.careRecipient?.name && (
                  <div style={{ background: '#fff7ed', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #fdba74' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#c2410c', fontSize: '1rem' }}>[+] Care Recipient Information</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                      <div><span style={{ color: '#ea580c', fontSize: '0.8rem', display: 'block' }}>Name</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.careRecipient.name}</span></div>
                      {viewingWaiverSubmission.careRecipient?.relationship && <div><span style={{ color: '#ea580c', fontSize: '0.8rem', display: 'block' }}>Relationship</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.careRecipient.relationship}</span></div>}
                      {viewingWaiverSubmission.careRecipient?.ssn && <div><span style={{ color: '#ea580c', fontSize: '0.8rem', display: 'block' }}>SSN (Last 4)</span><span style={{ color: '#1e293b', fontWeight: '500' }}>***-**-{viewingWaiverSubmission.careRecipient.ssn}</span></div>}
                    </div>
                  </div>
                )}

                {/* Election Information */}
                <div style={{ background: '#eff6ff', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#1d4ed8', fontSize: '1rem' }}>📋 Election & Consents</h3>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: 'white', borderRadius: '8px' }}>
                      <span style={{ color: viewingWaiverSubmission.consents?.medicaidElection === 'exclude' ? '#f59e0b' : '#10b981' }}>
                        {viewingWaiverSubmission.consents?.medicaidElection === 'exclude' ? '📝' : '✅'}
                      </span>
                      <span style={{ fontWeight: '500' }}>
                        Medicaid Waiver Election: {viewingWaiverSubmission.consents?.medicaidElection === 'exclude' ? 'EXCLUDE Income from Filing' : 'INCLUDE Income in Filing'}
                      </span>
                    </div>
                    {viewingWaiverSubmission.consents?.engagementAccepted && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: 'white', borderRadius: '8px' }}>
                        <span style={{ color: '#10b981' }}>✅</span>
                        <span>Engagement Letter & Terms Accepted</span>
                      </div>
                    )}
                    {viewingWaiverSubmission.consents?.acknowledgement && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: 'white', borderRadius: '8px' }}>
                        <span style={{ color: '#10b981' }}>✅</span>
                        <span>IRS Notice Acknowledgement</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Photo ID Images */}
                <div style={{ background: '#f0fdf4', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #86efac' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#166534', fontSize: '1rem' }}>📷 Photo ID Verification</h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    {/* Consumer Photo */}
                    <div>
                      <p style={{ margin: '0 0 8px', fontWeight: '500', color: '#166534', fontSize: '0.9rem' }}>[U] Consumer/Taxpayer Photo ID</p>
                      {viewingWaiverSubmission.photoIDs?.consumer && viewingWaiverSubmission.photoIDs.consumer.startsWith('data:') ? (
                        <img 
                          src={viewingWaiverSubmission.photoIDs.consumer} 
                          alt="Consumer ID" 
                          style={{ width: '100%', maxHeight: '250px', objectFit: 'contain', borderRadius: '8px', border: '2px solid #86efac', background: 'white', padding: '5px' }}
                        />
                      ) : viewingWaiverSubmission.photoIDs?.consumerUploaded ? (
                        <div style={{ padding: '20px', background: '#fef3c7', borderRadius: '8px', textAlign: 'center' }}>
                          <span style={{ fontSize: '2rem' }}>📷</span>
                          <p style={{ margin: '10px 0 0', color: '#92400e', fontWeight: '500' }}>Photo was uploaded</p>
                          <p style={{ margin: '5px 0 0', fontSize: '0.8rem', color: '#b45309' }}>(Old submission - image not stored)</p>
                        </div>
                      ) : (
                        <div style={{ padding: '20px', background: '#fee2e2', borderRadius: '8px', textAlign: 'center' }}>
                          <span style={{ fontSize: '2rem' }}>❌</span>
                          <p style={{ margin: '10px 0 0', color: '#dc2626', fontWeight: '500' }}>No Photo Uploaded</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Caregiver Photo */}
                    <div>
                      <p style={{ margin: '0 0 8px', fontWeight: '500', color: '#166534', fontSize: '0.9rem' }}>👥 Caregiver Photo ID</p>
                      {viewingWaiverSubmission.photoIDs?.caregiver && viewingWaiverSubmission.photoIDs.caregiver.startsWith('data:') ? (
                        <img 
                          src={viewingWaiverSubmission.photoIDs.caregiver} 
                          alt="Caregiver ID" 
                          style={{ width: '100%', maxHeight: '250px', objectFit: 'contain', borderRadius: '8px', border: '2px solid #86efac', background: 'white', padding: '5px' }}
                        />
                      ) : viewingWaiverSubmission.photoIDs?.caregiverUploaded ? (
                        <div style={{ padding: '20px', background: '#fef3c7', borderRadius: '8px', textAlign: 'center' }}>
                          <span style={{ fontSize: '2rem' }}>📷</span>
                          <p style={{ margin: '10px 0 0', color: '#92400e', fontWeight: '500' }}>Photo was uploaded</p>
                          <p style={{ margin: '5px 0 0', fontSize: '0.8rem', color: '#b45309' }}>(Old submission - image not stored)</p>
                        </div>
                      ) : (
                        <div style={{ padding: '20px', background: '#fee2e2', borderRadius: '8px', textAlign: 'center' }}>
                          <span style={{ fontSize: '2rem' }}>❌</span>
                          <p style={{ margin: '10px 0 0', color: '#dc2626', fontWeight: '500' }}>No Photo Uploaded</p>
                        </div>
                      )}
                    </div>
                  </div>
                    
                    {/* Photo Verification Checklist */}
                    <div style={{ marginTop: '15px', padding: '12px', background: 'white', borderRadius: '8px', border: '1px solid #86efac' }}>
                      <p style={{ margin: '0 0 8px', fontWeight: '600', color: '#166534', fontSize: '0.9rem' }}>✅ Verification Checklist:</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input type="checkbox" style={{ width: '18px', height: '18px' }} />
                          <span style={{ fontSize: '0.85rem' }}>Photo ID is clear and readable</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input type="checkbox" style={{ width: '18px', height: '18px' }} />
                          <span style={{ fontSize: '0.85rem' }}>Name matches form</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input type="checkbox" style={{ width: '18px', height: '18px' }} />
                          <span style={{ fontSize: '0.85rem' }}>ID is not expired</span>
                        </label>
                      </div>
                    </div>
                  </div>

                {/* Signatures */}
                <div style={{ background: '#fefce8', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #fcd34d' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '1rem' }}>✏️ Electronic Signatures</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    {/* Taxpayer Signature */}
                    <div style={{ background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #fde68a' }}>
                      <p style={{ margin: '0 0 8px', fontWeight: '600', color: '#92400e', fontSize: '0.9rem' }}>[U] Taxpayer Signature</p>
                      {viewingWaiverSubmission.signature?.image && viewingWaiverSubmission.signature.image.startsWith('data:') ? (
                        <img src={viewingWaiverSubmission.signature.image} alt="Taxpayer Signature" style={{ maxWidth: '100%', maxHeight: '80px', marginBottom: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }} />
                      ) : viewingWaiverSubmission.signature?.image && !viewingWaiverSubmission.signature.image.startsWith('data:') ? (
                        <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e5e7eb', marginBottom: '8px' }}>
                          <p style={{ margin: 0, fontFamily: "'Dancing Script', 'Brush Script MT', cursive", fontSize: '1.5rem', color: '#1e293b' }}>{viewingWaiverSubmission.signature.image}</p>
                          <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#64748b' }}>Typed signature</p>
                        </div>
                      ) : viewingWaiverSubmission.signature?.hasSignature ? (
                        <p style={{ margin: '0 0 8px', color: '#22c55e', fontWeight: '500' }}>✅ Signature Provided</p>
                      ) : (
                        <p style={{ margin: '0 0 8px', color: '#dc2626', fontWeight: '500' }}>❌ No Signature</p>
                      )}
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#374151' }}>
                        <strong>Date:</strong> {viewingWaiverSubmission.signature?.date || 'N/A'}
                      </p>
                    </div>
                    
                    {/* Spouse Signature (if applicable) */}
                    {viewingWaiverSubmission.spouseInfo?.name && (
                      <div style={{ background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #fde68a' }}>
                        <p style={{ margin: '0 0 8px', fontWeight: '600', color: '#92400e', fontSize: '0.9rem' }}>👥 Spouse Signature</p>
                        {viewingWaiverSubmission.spouseSignature?.image && viewingWaiverSubmission.spouseSignature.image.startsWith('data:') ? (
                          <img src={viewingWaiverSubmission.spouseSignature.image} alt="Spouse Signature" style={{ maxWidth: '100%', maxHeight: '80px', marginBottom: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }} />
                        ) : viewingWaiverSubmission.spouseSignature?.image && !viewingWaiverSubmission.spouseSignature.image.startsWith('data:') ? (
                          <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e5e7eb', marginBottom: '8px' }}>
                            <p style={{ margin: 0, fontFamily: "'Dancing Script', 'Brush Script MT', cursive", fontSize: '1.5rem', color: '#1e293b' }}>{viewingWaiverSubmission.spouseSignature.image}</p>
                            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#64748b' }}>Typed signature</p>
                          </div>
                        ) : viewingWaiverSubmission.spouseSignature?.hasSignature ? (
                          <p style={{ margin: '0 0 8px', color: '#22c55e', fontWeight: '500' }}>✅ Signature Provided</p>
                        ) : (
                          <p style={{ margin: '0 0 8px', color: '#94a3b8', fontWeight: '500' }}>- No Spouse Signature</p>
                        )}
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#374151' }}>
                          <strong>Date:</strong> {viewingWaiverSubmission.spouseSignature?.date || 'N/A'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Document Details */}
                <div style={{ background: '#f1f5f9', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#475569', fontSize: '1rem' }}>🔄 Document Details</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                    <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Tax Year</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.taxYear}</span></div>
                    <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Submitted</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{(() => {
                      const dateValue = viewingWaiverSubmission.submittedAt;
                      if (!dateValue) return 'N/A';
                      const date = dateValue?.toDate ? dateValue.toDate() : new Date(typeof dateValue === 'string' ? dateValue.replace(/ ET$/, '') : dateValue);
                      if (isNaN(date.getTime())) return 'N/A';
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()}</span></div>
                    {viewingWaiverSubmission.verifiedAt && <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Verified</span><span style={{ color: '#1e293b', fontWeight: '500' }}>{(() => {
                      const dateValue = viewingWaiverSubmission.verifiedAt;
                      const date = dateValue?.toDate ? dateValue.toDate() : new Date(typeof dateValue === 'string' ? dateValue.replace(/ ET$/, '') : dateValue);
                      if (isNaN(date.getTime())) return 'N/A';
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()}</span></div>}
                    <div><span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Document ID</span><span style={{ color: '#1e293b', fontWeight: '500', fontSize: '0.85rem' }}>{viewingWaiverSubmission.id}</span></div>
                  </div>
                </div>
                
                {/* PDF Preview Section */}
                <div style={{ background: '#e0f2fe', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#0369a1', fontSize: '1rem' }}>🔄 PDF Document Preview</h3>
                  
                  {!showPdfPreviewInModal ? (
                    <button
                      onClick={async () => {
                        try {
                          // Generate PDF for preview
                          const pdfData = await generateIRSWaiverPDF(viewingWaiverSubmission, currentCompany, loggedInPreparer);
                          if (pdfData) {
                            const blob = pdfData.output('blob');
                            const url = URL.createObjectURL(blob);
                            setPdfPreviewUrl(url);
                            setShowPdfPreviewInModal(true);
                          }
                        } catch (error) {
                          console.error('Error generating PDF preview:', error);
                          alert('Error generating PDF preview');
                        }
                      }}
                      style={{ 
                        padding: '12px 24px', 
                        background: 'linear-gradient(135deg, #0ea5e9, #0284c7)', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '8px', 
                        cursor: 'pointer', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      ⭕ Preview Full PDF Document
                    </button>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ color: '#0369a1', fontWeight: '600' }}>PDF Preview:</span>
                        <button
                          onClick={() => {
                            setShowPdfPreviewInModal(false);
                            if (pdfPreviewUrl) {
                              URL.revokeObjectURL(pdfPreviewUrl);
                              setPdfPreviewUrl(null);
                            }
                          }}
                          style={{ 
                            padding: '6px 12px', 
                            background: '#94a3b8', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '6px', 
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          ✕ Hide Preview
                        </button>
                      </div>
                      {pdfPreviewUrl && (
                        <iframe
                          src={pdfPreviewUrl}
                          style={{
                            width: '100%',
                            height: '500px',
                            border: '2px solid #0ea5e9',
                            borderRadius: '8px',
                            background: 'white'
                          }}
                          title="IRS Waiver PDF Preview"
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
                  <button 
                    onClick={async () => await downloadIRSWaiverPDF(viewingWaiverSubmission, currentCompany, loggedInPreparer)}
                    style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    📥 Download PDF
                  </button>
                  <button onClick={() => { 
                    setViewingWaiverSubmission(null); 
                    setShowPdfPreviewInModal(false);
                    if (pdfPreviewUrl) {
                      URL.revokeObjectURL(pdfPreviewUrl);
                      setPdfPreviewUrl(null);
                    }
                  }} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}

      </>
      )}
    </div>
  );
}

export default App;
